import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { storybookAPI, aiAPI, bookReviewAPI, generationJobAPI, getImageUrl, imageAPI, userAPI } from '../utils/api';
import { setSignedUrlInCache, getAllEntries, removeEntry, getSignedUrlFromCache } from '../utils/imageCache';
import { runWithConcurrency } from '../utils/runWithConcurrency';
import {
  Storybook,
  Character,
  Page,
  TextElement,
  ImageElement,
  BookIdentityReadiness,
  QuickCreateComposition,
  QuickCreateStage,
  QuickCreateState,
  StructuredPlotData
} from '../types/storybook';
import {
  BookDesignStyle,
  NormalizedRegion,
  PAGE_LAYOUT_VERSION,
  PageLayoutTemplate
} from '../types/layout';
import { getTextBoxStyle, applyStyleToText } from '../utils/textBoxStyles';
import {
  buildDeterministicPageLayout,
  BuildPageLayoutInput,
  DeterministicPageLayout,
  isFullSceneIllustration,
  mapImageRegionsToCanvas,
  MIN_READABLE_NARRATION_FONT_SIZE,
  resolveCanonicalTemplate,
  resolvePageDesignStyle,
  resolvePlannedTextHeight,
  resolveProviderAspectRatio,
  resolveTextLayout
} from '../utils/pageLayout';
import { planNarrationPageFlow } from '../utils/pageFlowPlanner';
import {
  applyPreflightAutoFix,
  preflightBook,
  PreflightIssue,
  summarizePreflight
} from '../utils/pagePreflight';
import {
  createProductionTextBlockMeasurer,
  createStoryTextTypography,
  PRODUCTION_FONT_ASSET_VERSION,
  resolveProductionFontFamily,
  resolveTextDirection,
  STORY_BUBBLE_PADDING,
  STORY_TEXT_LINE_HEIGHT,
  STORY_TEXT_PADDING,
  StoryTextTypography,
  toCssFontFamily
} from '../utils/textTypography';
import Canvas from '../components/Canvas';
import Toolbar from '../components/Toolbar';
import Sidebar from '../components/Sidebar';
import PagesList from '../components/PagesList';
import PreflightPanel, { preflightIssueKey } from '../components/PreflightPanel';
import StoryInfo from '../components/StoryInfo';
import CropModal from '../components/CropModal';
import PlotModal from '../components/PlotModal';
import PageGenerationProgress from '../components/PageGenerationProgress';
import StylePromptsModal from '../components/StylePromptsModal';
import DesignSettingsModal from '../components/DesignSettingsModal';
import PdfProgressModal from '../components/PdfProgressModal';
import ExportProofModal from '../components/ExportProofModal';
import TutorialModal from '../components/TutorialModal';
import AIPlotTutorialModal from '../components/AIPlotTutorialModal';
import ResolutionCheckerModal from '../components/ResolutionCheckerModal';
import ImageHistoryModal from '../components/ImageHistoryModal';
import QuickCreateWorkflow from '../components/QuickCreateWorkflow';
import GenerationJobPanel from '../components/GenerationJobPanel';
import BookReviewDashboard from '../components/BookReviewDashboard';
import {
  BookReviewSnapshot,
  ReviewHistoryTarget,
  ReviewLayer,
  ReviewRevision,
  ReviewSafeIssue
} from '../types/bookReview';
import {
  GenerationJob,
  GenerationJobAction,
  GenerationJobConnectionState
} from '../types/generationJob';
import {
  ExportLayoutOverrideAcknowledgement,
  ExportLayoutOverrideReceipt,
  ExportPreflightIssue,
  ExportPreflightReport,
  ExportProofArtifact,
  ExportProofModalPhase
} from '../types/exportPreflight';
import html2canvas from 'html2canvas';
import { renderPageHtml } from '@tiny-fox/storybook-renderer';
import {
  BUNDLED_FONT_ASSET_VERSION,
  createAuthoritativeMeasurementAdapter,
  createSeriesStyleDirection,
  planProductionPageFlow
} from '@tiny-fox/storybook-layout';
import { createFeatheredCanvas } from '../utils/featherMask';
import {
  approveQuickCreateStage,
  estimateQuickCreate,
  reviseQuickCreateReferenceDirection,
  reviseQuickCreateStage,
  shouldRetryQuickCreateDraftJob,
  withStableOutlinePageIds
} from '../utils/quickCreate';
import {
  buildQuickCreateStoryboard,
  resolveQuickCreateComposition
} from '../utils/quickCreateComposition';
import {
  fetchVerifiedExportProof,
  validateServerExportProofDescriptor
} from '../utils/exportProofArtifact';
import {
  cancelExportRun,
  createExportRunCoordinator,
  exportProofFailurePhase,
  ExportRunContext,
  isActiveExportRun,
  isExportRunCancellation,
  startExportRun
} from '../utils/exportRun';
import {
  deletePageAtIndex,
  insertPageAfterCurrent,
  reorderBookPages,
  closeResponsivePanelAndRestoreFocus,
  handleResponsivePanelEscape,
  useEditorGenerationDomain,
  useEditorHistoryDomain,
  useEditorPageDomain,
  useEditorSaveDomain,
  useEditorSelectionDomain
} from '../features/editor';
import './StorybookEditor.css';

const verifiedLayoutOverrideReceipt = (
  value: unknown,
  report: ExportPreflightReport
): ExportLayoutOverrideReceipt | null => {
  if (!value || typeof value !== 'object' || Array.isArray(value)) return null;
  const candidate = value as Partial<ExportLayoutOverrideReceipt>;
  return candidate.applied === true
    && candidate.schemaVersion === 1
    && candidate.reportId === report.reportId
    && typeof candidate.issueDigest === 'string'
    && /^[a-f0-9]{64}$/.test(candidate.issueDigest)
    && Number.isSafeInteger(candidate.issueCount)
    && Number(candidate.issueCount) > 0
    && candidate.issueCount === report.summary.errorCount
    ? candidate as ExportLayoutOverrideReceipt
    : null;
};

const manualAIIdempotencyKey = (kind: string, storybookId?: string): string => {
  const random = typeof globalThis.crypto?.randomUUID === 'function'
    ? globalThis.crypto.randomUUID()
    : `${Date.now()}-${Math.random().toString(16).slice(2)}`;
  return `manual:${kind}:${storybookId || 'draft'}:${random}`.slice(0, 160);
};

const approvedCharacterAnchor = (character?: Character): string | undefined => {
  if (!character?.approvedReferenceVersionId || !character.approvedBibleVersionId) return undefined;
  const version = character.referenceVersions?.find(candidate => (
    candidate.id === character.approvedReferenceVersionId
    && candidate.status === 'approved'
    && candidate.bibleVersionId === character.approvedBibleVersionId
  ));
  return version?.references.find(reference => reference.purpose === 'three-quarter')?.imageUrl
    || version?.references.find(reference => reference.purpose === 'front')?.imageUrl;
};

const identityReadinessForBook = (book: Storybook | null): BookIdentityReadiness => {
  if (!book) return { ready: false, issues: [], characters: [] };
  const issues: BookIdentityReadiness['issues'] = [];
  const approvedArt = book.artBible?.versions.find(version => (
    version.id === book.artBible?.approvedVersionId && version.status === 'approved'
  ));
  if (!approvedArt) issues.push({ code: 'APPROVED_ART_BIBLE_REQUIRED', message: 'Approve an Art Bible.' });
  const activeProfile = book.generationProfiles?.find(profile => (
    profile.id === book.activeGenerationProfileId && profile.status === 'active'
  ));
  if (!activeProfile) issues.push({ code: 'ACTIVE_GENERATION_PROFILE_REQUIRED', message: 'Lock the book model profile.' });
  const outlineNames = new Set((book.structuredPlotData?.characters || [])
    .map(character => character.name.trim().toLocaleLowerCase()));
  const relevantCharacters = (book.characters || []).filter(character => (
    outlineNames.size === 0 || outlineNames.has(character.name.trim().toLocaleLowerCase())
  ));
  relevantCharacters.forEach(character => {
    const bible = character.bibleVersions?.find(version => (
      version.id === character.approvedBibleVersionId && version.status === 'approved'
    ));
    const references = character.referenceVersions?.find(version => (
      version.id === character.approvedReferenceVersionId && version.status === 'approved'
    ));
    if (!character.characterId || !bible || !references || references.bibleVersionId !== bible.id) {
      issues.push({
        code: 'APPROVED_CHARACTER_IDENTITY_REQUIRED',
        message: `${character.name} needs an approved Character Bible and four-view kit.`,
        ...(character.characterId ? { characterId: character.characterId } : {})
      });
    }
  });
  if (book.modelMigration && book.modelMigration.status !== 'complete') {
    issues.push({ code: 'MODEL_PROFILE_RERENDER_REQUIRED', message: 'Complete the required full model-profile rerender.' });
  }
  return {
    ready: issues.length === 0,
    issues,
    artBibleVersionId: approvedArt?.id,
    generationProfileId: activeProfile?.id,
    characters: relevantCharacters.filter(character => Boolean(character.characterId)).map(character => ({
      characterId: character.characterId!,
      name: character.name,
      approvedBibleVersionId: character.approvedBibleVersionId,
      approvedReferenceVersionId: character.approvedReferenceVersionId
    }))
  };
};

const CANVAS_SIZES = [
  // Paperback Trim Sizes
  { value: '5x8', label: '5" × 8" (Paperback)', width: 500, height: 800 },
  { value: '5.06x7.81', label: '5.06" × 7.81" (Paperback)', width: 506, height: 781 },
  { value: '5.25x8', label: '5.25" × 8" (Paperback)', width: 525, height: 800 },
  { value: '5.5x8.5', label: '5.5" × 8.5" (Paperback)', width: 550, height: 850 },
  { value: '6x9', label: '6" × 9" (Paperback)', width: 600, height: 900 },
  
  // Large Paperback Trim Sizes
  { value: '6.14x9.21', label: '6.14" × 9.21" (Paperback)', width: 614, height: 921 },
  { value: '6.69x9.61', label: '6.69" × 9.61" (Paperback)', width: 669, height: 961 },
  { value: '7x10', label: '7" × 10" (Paperback)', width: 700, height: 1000 },
  { value: '7.44x9.69', label: '7.44" × 9.69" (Paperback)', width: 744, height: 969 },
  { value: '7.5x9.25', label: '7.5" × 9.25" (Paperback)', width: 750, height: 925 },
  { value: '8x10', label: '8" × 10" (Paperback)', width: 800, height: 1000 },
  { value: '8.25x6', label: '8.25" × 6" (Paperback)', width: 825, height: 600 },
  { value: '8.25x8.25', label: '8.25" × 8.25" (Paperback)', width: 825, height: 825 },
  { value: '8.5x8.5', label: '8.5" × 8.5" (Paperback)', width: 850, height: 850 },
  { value: '8.5x11', label: '8.5" × 11" (Paperback)', width: 850, height: 1100 },
  { value: '8.27x11.69', label: '8.27" × 11.69" (Paperback)', width: 827, height: 1169 },
  
  // Hardcover Trim Sizes
  { value: '5.5x8.5-hc', label: '5.5" × 8.5" (Hardcover)', width: 550, height: 850 },
  { value: '6x9-hc', label: '6" × 9" (Hardcover)', width: 600, height: 900 },
  { value: '6.14x9.21-hc', label: '6.14" × 9.21" (Hardcover)', width: 614, height: 921 },
  { value: '7x10-hc', label: '7" × 10" (Hardcover)', width: 700, height: 1000 },
  { value: '8.25x11-hc', label: '8.25" × 11" (Hardcover)', width: 825, height: 1100 }
];

const preflightWarningSignature = (issues: PreflightIssue[]): string => (
  issues
    .filter(issue => issue.severity === 'warning')
    .map(issue => `${preflightIssueKey(issue)}:${issue.message}:${JSON.stringify(issue.metrics || {})}`)
    .sort()
    .join('|')
);

const quickCreatePreflightCounts = (
  clientIssues: PreflightIssue[],
  exportReport: ExportPreflightReport | null
): { errors: number; warnings: number } => {
  const uniqueIssues = new Map<string, 'error' | 'warning'>();
  const addIssue = (issue: PreflightIssue | ExportPreflightIssue) => {
    const key = [
      issue.severity,
      issue.code,
      issue.pageId || 'book',
      issue.elementId || 'none'
    ].join(':');
    uniqueIssues.set(key, issue.severity);
  };
  clientIssues.forEach(addIssue);
  exportReport?.issues.forEach(addIssue);
  const severities = Array.from(uniqueIssues.values());
  return {
    errors: severities.filter(severity => severity === 'error').length,
    warnings: severities.filter(severity => severity === 'warning').length
  };
};

// Mirrors the current backend product limit for GPT Image edit inputs.
const MAX_OPENAI_REFERENCE_IMAGES = 5;

interface BatchGenerationSummary {
  success: number;
  failed: number;
  skipped: number;
  total: number;
  wasCancelled: boolean;
}

interface EditorWorkspacePreferences {
  pageStripExpanded: boolean;
  inspectorExpanded: boolean;
  pageStripWidth: number;
  inspectorWidth: number;
}

const workspacePreferenceKey = (storybookId?: string) => (
  `tiny-little-fox:editor-workspace:${storybookId || 'new'}`
);

const readWorkspacePreferences = (storybookId?: string): Partial<EditorWorkspacePreferences> => {
  if (typeof window === 'undefined') return {};
  try {
    const value = window.localStorage.getItem(workspacePreferenceKey(storybookId));
    if (!value) return {};
    const parsed = JSON.parse(value);
    return parsed && typeof parsed === 'object'
      ? parsed as Partial<EditorWorkspacePreferences>
      : {};
  } catch {
    return {};
  }
};

const writeWorkspacePreferences = (
  storybookId: string | undefined,
  preferences: EditorWorkspacePreferences
) => {
  if (typeof window === 'undefined') return;
  try {
    window.localStorage.setItem(workspacePreferenceKey(storybookId), JSON.stringify(preferences));
  } catch {
    // The editor remains fully usable when private browsing blocks storage.
  }
};

const formatTimeSince = (date: Date): string => {
  const seconds = Math.floor((new Date().getTime() - date.getTime()) / 1000);
  if (seconds < 60) return 'just now';
  const minutes = Math.floor(seconds / 60);
  if (minutes < 60) return `${minutes}m ago`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours}h ago`;
  return 'today';
};

// Helper function to merge arrays by a field (e.g., 'name'), keeping all items from both
// and preferring items from the 'local' array when duplicates are found
const mergeArraysByField = <T extends Record<string, any>>(
  fresh: T[] | undefined,
  local: T[] | undefined,
  field: keyof T
): T[] => {
  const freshArr = fresh || [];
  const localArr = local || [];
  
  // Create a map from fresh items
  const merged = new Map<any, T>();
  for (const item of freshArr) {
    merged.set(item[field], item);
  }
  
  // Add/override with local items
  for (const item of localArr) {
    merged.set(item[field], item);
  }
  
  return Array.from(merged.values());
};

type ProductionPageLayoutInput = Omit<
  BuildPageLayoutInput,
  'measureTextBlock' | 'typography'
> & {
  typography: StoryTextTypography;
};

const buildProductionPageLayout = async (
  input: ProductionPageLayoutInput
): Promise<DeterministicPageLayout> => {
  const fontSize = Math.max(
    input.preferredFontSize || MIN_READABLE_NARRATION_FONT_SIZE,
    input.minimumFontSize || MIN_READABLE_NARRATION_FONT_SIZE
  );
  const samples = input.textBoxes
    .filter(textBox => Boolean(String(textBox.content || '').trim()))
    .map(textBox => ({
      content: textBox.content,
      fontSize,
      typography: createStoryTextTypography(
        textBox.fontFamily || input.typography.fontFamily,
        textBox.fontWeight || input.typography.fontWeight,
        textBox.fontStyle || input.typography.fontStyle,
        input.typography.padding
      )
    }));
  const measureTextBlock = await createProductionTextBlockMeasurer(samples);

  return buildDeterministicPageLayout({
    ...input,
    measureTextBlock
  });
};

const measureProductionTextHeight = async ({
  content,
  width,
  fontSize,
  fontFamily,
  fontWeight,
  fontStyle,
  padding
}: {
  content: string;
  width: number;
  fontSize: number;
  fontFamily: string;
  fontWeight?: string;
  fontStyle?: 'normal' | 'italic';
  padding: number;
}): Promise<number> => {
  const typography = createStoryTextTypography(
    fontFamily,
    fontWeight || 'normal',
    fontStyle || 'normal',
    padding
  );
  const measureTextBlock = await createProductionTextBlockMeasurer([{
    content,
    fontSize,
    typography
  }]);

  return measureTextBlock({
    content,
    width,
    fontSize,
    typography
  }).height;
};

const StorybookEditor: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();
  const { user, refreshUser } = useAuth();
  const canvasContainerRef = useRef<HTMLDivElement>(null);
  const workspaceRef = useRef<HTMLDivElement>(null);
  const canvasStageRef = useRef<HTMLDivElement>(null);
  const previewTriggerRef = useRef<HTMLButtonElement>(null);
  const previewExitRef = useRef<HTMLButtonElement>(null);
  const pageStripToggleRef = useRef<HTMLButtonElement>(null);
  const inspectorToggleRef = useRef<HTMLButtonElement>(null);
  const pageDrawerCloseRef = useRef<HTMLButtonElement>(null);
  const inspectorDrawerCloseRef = useRef<HTMLButtonElement>(null);
  const initialWorkspacePreferencesRef = useRef(readWorkspacePreferences(id));
  const panelResizeRef = useRef<{
    panel: 'pages' | 'inspector';
    startX: number;
    startWidth: number;
  } | null>(null);
  
  const [storybook, setStorybook] = useState<Storybook | null>(null);
  // Keep a ref to the latest storybook for use in callbacks/closures
  const storybookRef = useRef<Storybook | null>(null);
  // Update ref whenever storybook changes
  storybookRef.current = storybook;
  const pageDomain = useEditorPageDomain({ storybook, setStorybook });
  const {
    currentPageIndex,
    currentPage,
    setCurrentPageIndex,
    replaceCurrentPage
  } = pageDomain;
  const selectionDomain = useEditorSelectionDomain(currentPage);
  const {
    selectedElement,
    selectElement: setSelectedElement,
    clearSelection
  } = selectionDomain;
  const pageHistory = useEditorHistoryDomain<Page>(currentPage?.id, 40);
  const {
    record: recordPageHistory,
    undo: undoPageHistory,
    redo: redoPageHistory
  } = pageHistory;
  
  // Setup flow state (for new storybook creation)
  const [setupFlowStep, setSetupFlowStep] = useState<'none' | 'design' | 'story' | 'font' | 'plot' | 'done'>('none');
  const [showQuickCreate, setShowQuickCreate] = useState(false);
  const [quickCreateBusyAction, setQuickCreateBusyAction] = useState<string | null>(null);
  const [quickCreateError, setQuickCreateError] = useState<string | null>(null);
  const [quickCreateExportPreflight, setQuickCreateExportPreflight] = useState<ExportPreflightReport | null>(null);
  const [generationJob, setGenerationJob] = useState<GenerationJob | null>(null);
  const [generationJobAction, setGenerationJobAction] = useState<GenerationJobAction | null>(null);
  const [generationConnection, setGenerationConnection] = useState<GenerationJobConnectionState>('polling');
  const generationJobSyncedAtRef = useRef<string | null>(null);
  const [showBookReview, setShowBookReview] = useState(false);
  const [bookReview, setBookReview] = useState<BookReviewSnapshot | null>(null);
  const [bookReviewLoading, setBookReviewLoading] = useState(false);
  const [bookReviewError, setBookReviewError] = useState<string | null>(null);
  
  const [customDrawingStyles, setCustomDrawingStyles] = useState<string[]>([]);
  const [loading, setLoading] = useState(true);
  const [showCropModal, setShowCropModal] = useState(false);
  const [cropImageId, setCropImageId] = useState<string | null>(null);
  const [isRephrasing, setIsRephrasing] = useState(false);
  const [isApplyingSafeLayout, setIsApplyingSafeLayout] = useState(false);
  const [preflightIssues, setPreflightIssues] = useState<PreflightIssue[]>([]);
  const [preflightPageHashes, setPreflightPageHashes] = useState<Map<string, string>>(new Map());
  const [showPreflightModal, setShowPreflightModal] = useState(false);
  const [isRunningPreflight, setIsRunningPreflight] = useState(false);
  const [focusedPreflightPageId, setFocusedPreflightPageId] = useState<string | null>(null);
  const [fixingPreflightIssueKey, setFixingPreflightIssueKey] = useState<string | null>(null);
  const [preflightContinueTarget, setPreflightContinueTarget] = useState<'pdf-options' | 'pdf-progress' | null>(null);
  const acknowledgedPreflightWarningsRef = useRef<string | null>(null);
  const preflightImageMetadataCacheRef = useRef<Map<string, { width: number; height: number }>>(new Map());
  const [showRephraseOptionsModal, setShowRephraseOptionsModal] = useState(false);
  const [rephraseOptions, setRephraseOptions] = useState<string[]>([]);
  const [rephraseOriginalText, setRephraseOriginalText] = useState('');
  const [rephraseTargetTextId, setRephraseTargetTextId] = useState<string | null>(null);
  const [rephraseOptionsError, setRephraseOptionsError] = useState('');
  const [showPlotModal, setShowPlotModal] = useState(false);
  const [showStyleModal, setShowStyleModal] = useState(false);
  const [showFontModal, setShowFontModal] = useState(false);
  const [showDesignModal, setShowDesignModal] = useState(false);
  const [savedPlots, setSavedPlots] = useState<Array<{ pageNumber: number; text: string }>>([]);
  const [structuredPlotData, setStructuredPlotData] = useState<any>(null);
  const [plotModalSinglePageIndex, setPlotModalSinglePageIndex] = useState<number | null>(null);
  const generationDomain = useEditorGenerationDomain();
  const {
    showProgressModal,
    progressSteps,
    isCancellingGeneration,
    batchProgress,
    batchSummary,
    generatingPageNumber,
    cancellationRef: cancelGenerationRef,
    busyRef: isGeneratingAIContent,
    batchActiveRef: batchGenerationRef,
    setProgressSteps,
    updateProgressStep
  } = generationDomain;
  const [zoom, setZoom] = useState(1);
  const [zoomMode, setZoomMode] = useState<'fit' | 'manual'>('fit');
  const [fitZoom, setFitZoom] = useState(1);
  const [pan, setPan] = useState({ x: 0, y: 0 });
  const [isPanning, setIsPanning] = useState(false);
  const [panStart, setPanStart] = useState({ x: 0, y: 0 });
  const [pageStripExpanded, setPageStripExpanded] = useState(() => (
    (typeof window === 'undefined'
      || typeof window.matchMedia !== 'function'
      || !window.matchMedia('(max-width: 1024px)').matches)
    && (initialWorkspacePreferencesRef.current.pageStripExpanded ?? true)
  ));
  const [inspectorExpanded, setInspectorExpanded] = useState(() => (
    (typeof window === 'undefined'
      || typeof window.matchMedia !== 'function'
      || !window.matchMedia('(max-width: 1024px)').matches)
    && (initialWorkspacePreferencesRef.current.inspectorExpanded ?? true)
  ));
  const [pageStripWidth, setPageStripWidth] = useState(() => Math.max(
    144,
    Math.min(320, initialWorkspacePreferencesRef.current.pageStripWidth ?? 190)
  ));
  const [inspectorWidth, setInspectorWidth] = useState(() => Math.max(
    280,
    Math.min(520, initialWorkspacePreferencesRef.current.inspectorWidth ?? 360)
  ));
  const [isNarrowViewport, setIsNarrowViewport] = useState(() => (
    typeof window !== 'undefined'
    && typeof window.matchMedia === 'function'
    && window.matchMedia('(max-width: 1024px)').matches
  ));
  const [isDistractionFree, setIsDistractionFree] = useState(false);
  const [isEditingTitle, setIsEditingTitle] = useState(false);
  const [showGrid, setShowGrid] = useState(false);
  const [showPreviousPageOverlay, setShowPreviousPageOverlay] = useState(false);
  const [previousPageOpacity, setPreviousPageOpacity] = useState(0.3);
  const [editedTitle, setEditedTitle] = useState('');
  const [isGeneratingServerPDF, setIsGeneratingServerPDF] = useState(false);
  const [showPdfProgressModal, setShowPdfProgressModal] = useState(false);
  const [showPdfExportModal, setShowPdfExportModal] = useState(false);
  const [pdfExportType, setPdfExportType] = useState<'full' | 'manuscript' | 'front-cover' | 'back-cover'>('full');
  const [pdfBleedEnabled, setPdfBleedEnabled] = useState(true); // KDP bleed enabled by default
  const [showExportProofModal, setShowExportProofModal] = useState(false);
  const [exportProofPhase, setExportProofPhase] = useState<ExportProofModalPhase>('checking');
  const [exportPreflightReport, setExportPreflightReport] = useState<ExportPreflightReport | null>(null);
  const [exportProofArtifact, setExportProofArtifact] = useState<ExportProofArtifact | null>(null);
  const [exportProofError, setExportProofError] = useState<string | null>(null);
  const [exportDiagnosticsUrl, setExportDiagnosticsUrl] = useState<string | null>(null);
  const [exportLayoutOverrideReceipt, setExportLayoutOverrideReceipt] = useState<ExportLayoutOverrideReceipt | null>(null);
  const exportRunCoordinatorRef = useRef(createExportRunCoordinator());
  const exportSavingControllerRef = useRef<AbortController | null>(null);
  useEffect(() => {
    if (!storybook?._id) return;
    setPdfBleedEnabled(storybook.printSettings?.bleedInches !== 0);
  }, [storybook?._id]);
  useEffect(() => () => {
    cancelExportRun(exportRunCoordinatorRef.current);
    exportSavingControllerRef.current = null;
  }, []);
  const [copiedElement, setCopiedElement] = useState<{ element: TextElement | ImageElement; type: 'text' | 'image' } | null>(null);
  const [showAITutorial, setShowAITutorial] = useState(false);
  const [showTutorial, setShowTutorial] = useState(false);
  const hasSeenTutorial = useRef(false);
  
  // Resolution Checker & Image History Modal State
  const [showResolutionChecker, setShowResolutionChecker] = useState(false);
  const [showImageHistory, setShowImageHistory] = useState(false);
  const [imageHistoryElement, setImageHistoryElement] = useState<ImageElement | null>(null);
  
  // Font Settings Modal State
  const [fontSettingsFontSize, setFontSettingsFontSize] = useState(16);
  const [fontSettingsFontFamily, setFontSettingsFontFamily] = useState('Roboto');
  const [fontSettingsFontWeight, setFontSettingsFontWeight] = useState('normal');
  const [fontSettingsFontStyle, setFontSettingsFontStyle] = useState<'normal' | 'italic'>('normal');
  const [fontSettingsTextDecoration, setFontSettingsTextDecoration] = useState<'none' | 'underline' | 'line-through' | 'underline line-through'>('none');
  const [fontSettingsColor, setFontSettingsColor] = useState('#000000');
  const [fontSettingsTextAlign, setFontSettingsTextAlign] = useState<'left' | 'center' | 'right'>('left');
  const [fontSettingsVerticalAlign, setFontSettingsVerticalAlign] = useState<'top' | 'center' | 'bottom'>('center');
  
  // Thumbnail caching - track last saved state of pages to avoid regenerating unchanged thumbnails
  const [lastSavedPageStates, setLastSavedPageStates] = useState<Map<number, string>>(new Map());
  const [cachedThumbnails, setCachedThumbnails] = useState<Map<number, string>>(new Map());

  const workspaceCanvasSize = CANVAS_SIZES.find(size => (
    size.value === (storybook?.canvasSize || '5.5x8.5')
  )) || CANVAS_SIZES[3];
  const effectiveZoom = zoomMode === 'fit' ? fitZoom : zoom;

  const updateFitZoom = useCallback(() => {
    const stage = canvasStageRef.current;
    if (!stage) return;
    const horizontalInset = 48;
    const verticalInset = 48;
    const availableWidth = Math.max(1, stage.clientWidth - horizontalInset);
    const availableHeight = Math.max(1, stage.clientHeight - verticalInset);
    const nextFitZoom = Math.max(0.1, Math.min(
      1,
      availableWidth / workspaceCanvasSize.width,
      availableHeight / workspaceCanvasSize.height
    ));
    setFitZoom(previous => (
      Math.abs(previous - nextFitZoom) < 0.001 ? previous : nextFitZoom
    ));
  }, [workspaceCanvasSize.height, workspaceCanvasSize.width]);

  useEffect(() => {
    if (typeof window.matchMedia !== 'function') return;
    const mediaQuery = window.matchMedia('(max-width: 1024px)');
    const syncResponsivePanels = () => {
      setIsNarrowViewport(mediaQuery.matches);
      if (mediaQuery.matches) {
        setPageStripExpanded(false);
        setInspectorExpanded(false);
        return;
      }
      const preferences = readWorkspacePreferences(id);
      setPageStripExpanded(preferences.pageStripExpanded ?? true);
      setInspectorExpanded(preferences.inspectorExpanded ?? true);
    };
    syncResponsivePanels();
    mediaQuery.addEventListener('change', syncResponsivePanels);
    return () => mediaQuery.removeEventListener('change', syncResponsivePanels);
  }, [id]);

  useEffect(() => {
    if (isNarrowViewport) return;
    writeWorkspacePreferences(id, {
      pageStripExpanded,
      inspectorExpanded,
      pageStripWidth,
      inspectorWidth
    });
  }, [id, inspectorExpanded, inspectorWidth, isNarrowViewport, pageStripExpanded, pageStripWidth]);

  useEffect(() => {
    const stage = canvasStageRef.current;
    if (!stage) return;
    const animationFrame = window.requestAnimationFrame(updateFitZoom);
    const resizeObserver = typeof ResizeObserver === 'undefined'
      ? null
      : new ResizeObserver(updateFitZoom);
    resizeObserver?.observe(stage);
    window.addEventListener('resize', updateFitZoom);
    return () => {
      window.cancelAnimationFrame(animationFrame);
      resizeObserver?.disconnect();
      window.removeEventListener('resize', updateFitZoom);
    };
  }, [updateFitZoom, storybook, isDistractionFree]);

  useEffect(() => {
    const handleMouseMove = (event: MouseEvent) => {
      const resize = panelResizeRef.current;
      if (!resize) return;
      const deltaX = event.clientX - resize.startX;
      if (resize.panel === 'pages') {
        setPageStripWidth(Math.max(144, Math.min(320, resize.startWidth + deltaX)));
      } else {
        setInspectorWidth(Math.max(280, Math.min(520, resize.startWidth - deltaX)));
      }
    };
    const handleMouseUp = () => {
      if (!panelResizeRef.current) return;
      panelResizeRef.current = null;
      document.body.classList.remove('is-resizing-editor-panel');
    };
    window.addEventListener('mousemove', handleMouseMove);
    window.addEventListener('mouseup', handleMouseUp);
    return () => {
      window.removeEventListener('mousemove', handleMouseMove);
      window.removeEventListener('mouseup', handleMouseUp);
      document.body.classList.remove('is-resizing-editor-panel');
    };
  }, []);

  const beginPanelResize = (
    panel: 'pages' | 'inspector',
    event: React.MouseEvent<HTMLDivElement>
  ) => {
    if (event.button !== 0) return;
    event.preventDefault();
    panelResizeRef.current = {
      panel,
      startX: event.clientX,
      startWidth: panel === 'pages' ? pageStripWidth : inspectorWidth
    };
    document.body.classList.add('is-resizing-editor-panel');
  };

  const handlePanelResizeKeyDown = (
    panel: 'pages' | 'inspector',
    event: React.KeyboardEvent<HTMLDivElement>
  ) => {
    const currentWidth = panel === 'pages' ? pageStripWidth : inspectorWidth;
    const minimum = panel === 'pages' ? 144 : 280;
    const maximum = panel === 'pages' ? 320 : 520;
    let nextWidth = currentWidth;
    if (event.key === 'Home') nextWidth = minimum;
    if (event.key === 'End') nextWidth = maximum;
    if (event.key === 'ArrowLeft') nextWidth += panel === 'pages' ? -12 : 12;
    if (event.key === 'ArrowRight') nextWidth += panel === 'pages' ? 12 : -12;
    if (nextWidth === currentWidth) return;
    event.preventDefault();
    const clampedWidth = Math.max(minimum, Math.min(maximum, nextWidth));
    if (panel === 'pages') setPageStripWidth(clampedWidth);
    else setInspectorWidth(clampedWidth);
  };

  const fitPage = useCallback(() => {
    setZoomMode('fit');
    setPan({ x: 0, y: 0 });
    window.requestAnimationFrame(updateFitZoom);
  }, [updateFitZoom]);

  const closeResponsivePanel = useCallback((panel: 'pages' | 'inspector') => {
    closeResponsivePanelAndRestoreFocus(
      panel,
      currentPanel => {
        if (currentPanel === 'pages') setPageStripExpanded(false);
        else setInspectorExpanded(false);
      },
      currentPanel => currentPanel === 'pages'
        ? pageStripToggleRef.current
        : inspectorToggleRef.current
    );
  }, []);

  const togglePageStrip = () => {
    const nextExpanded = !pageStripExpanded;
    setPageStripExpanded(nextExpanded);
    if (!isNarrowViewport) return;
    if (nextExpanded) {
      setInspectorExpanded(false);
      window.requestAnimationFrame(() => pageDrawerCloseRef.current?.focus());
    }
  };

  const toggleInspector = () => {
    const nextExpanded = !inspectorExpanded;
    setInspectorExpanded(nextExpanded);
    if (!isNarrowViewport) return;
    if (nextExpanded) {
      setPageStripExpanded(false);
      window.requestAnimationFrame(() => inspectorDrawerCloseRef.current?.focus());
    }
  };

  const enterDistractionFree = () => {
    setSelectedElement(null);
    setPan({ x: 0, y: 0 });
    setZoomMode('fit');
    setIsDistractionFree(true);
  };

  const exitDistractionFree = useCallback(() => {
    setIsDistractionFree(false);
    window.requestAnimationFrame(() => {
      window.requestAnimationFrame(() => previewTriggerRef.current?.focus());
    });
  }, []);

  useEffect(() => {
    if (!isDistractionFree) return;
    const focusFrame = window.requestAnimationFrame(() => previewExitRef.current?.focus());
    const handlePreviewKeyDown = (event: KeyboardEvent) => {
      if (event.key !== 'Escape') return;
      event.preventDefault();
      exitDistractionFree();
    };
    window.addEventListener('keydown', handlePreviewKeyDown);
    return () => {
      window.cancelAnimationFrame(focusFrame);
      window.removeEventListener('keydown', handlePreviewKeyDown);
    };
  }, [exitDistractionFree, isDistractionFree]);

  useEffect(() => {
    if (!isNarrowViewport || (!pageStripExpanded && !inspectorExpanded)) return;
    const handleDrawerKeyDown = (event: KeyboardEvent) => {
      handleResponsivePanelEscape(
        event,
        pageStripExpanded ? 'pages' : 'inspector',
        closeResponsivePanel
      );
    };
    window.addEventListener('keydown', handleDrawerKeyDown);
    return () => window.removeEventListener('keydown', handleDrawerKeyDown);
  }, [closeResponsivePanel, inspectorExpanded, isNarrowViewport, pageStripExpanded]);

  useEffect(() => {
    loadStorybook();
    loadUserDrawingStyles();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  // Update font settings when storybook loads
  useEffect(() => {
    if (storybook?.defaultTextProperties) {
      const props = storybook.defaultTextProperties;
      setFontSettingsFontSize(props.fontSize);
      setFontSettingsFontFamily(props.fontFamily);
      setFontSettingsFontWeight(props.fontWeight);
      setFontSettingsFontStyle(props.fontStyle || 'normal');
      setFontSettingsTextDecoration(props.textDecoration || 'none');
      setFontSettingsColor(props.color);
      setFontSettingsTextAlign(props.textAlign || 'left');
      setFontSettingsVerticalAlign(props.verticalAlign || 'center');
    }
  }, [storybook?.defaultTextProperties]);

  // Load plots from storybook
  useEffect(() => {
    if (storybook?.plots) {
      setSavedPlots(storybook.plots);
    }
  }, [storybook?.plots]);

  // The thumbnail cache is keyed by every visual page property and the exact
  // renderer/font asset version. This prevents stale thumbnails after a style,
  // crop, flip, fit, feather, or typography change.
  const getPageContentHash = (page: Page): string => JSON.stringify({
    renderer: PRODUCTION_FONT_ASSET_VERSION,
    page: { ...page, thumbnail: undefined }
  });

  // A page badge remains valid only while the checked page content is
  // unchanged. Editing a page immediately hides its stale issues without
  // forcing an expensive image/font preflight on every keystroke.
  const currentPreflightCheckedPageIds = new Set(
    (storybook?.pages || [])
      .filter(page => preflightPageHashes.get(page.id) === getPageContentHash(page))
      .map(page => page.id)
  );
  const currentPreflightIssues = preflightIssues.filter(issue => (
    currentPreflightCheckedPageIds.has(issue.pageId)
  ));
  const currentPreflightSummary = summarizePreflight(currentPreflightIssues);
  const currentQuickCreatePreflightCounts = quickCreatePreflightCounts(
    currentPreflightIssues,
    quickCreateExportPreflight
  );

  const waitForCanonicalImages = async (root: HTMLElement): Promise<void> => {
    const images = Array.from(root.querySelectorAll('img'));
    await Promise.all(images.map(async image => {
      if (image.complete && image.naturalWidth > 0) return;
      await image.decode();
      if (image.naturalWidth <= 0) {
        throw new Error(`Unable to load thumbnail image: ${image.src}`);
      }
    }));
  };

  const generateThumbnail = async (
    pageIndex: number,
    forceRegenerate = false,
    sourceStorybook?: Storybook
  ): Promise<string | null> => {
    const currentStorybook = sourceStorybook || storybookRef.current;
    if (!currentStorybook) return null;

    const page = currentStorybook.pages[pageIndex];
    if (!page) return null;

    const currentHash = getPageContentHash(page);
    const lastSavedHash = lastSavedPageStates.get(pageIndex);
    if (!forceRegenerate && lastSavedHash === currentHash) {
      return cachedThumbnails.get(pageIndex) || page.thumbnail || null;
    }

    const sizeConfig = CANVAS_SIZES.find(
      size => size.value === (currentStorybook.canvasSize || '5.5x8.5')
    ) || CANVAS_SIZES[3];
    const thumbnailWidth = 112;
    const thumbnailScale = thumbnailWidth / sizeConfig.width;
    const renderHost = document.createElement('div');
    renderHost.dataset.testid = 'canonical-page-thumbnail';
    renderHost.setAttribute('aria-hidden', 'true');
    Object.assign(renderHost.style, {
      position: 'fixed',
      left: '-100000px',
      top: '0',
      width: `${sizeConfig.width}px`,
      height: `${sizeConfig.height}px`,
      pointerEvents: 'none'
    });
    renderHost.innerHTML = renderPageHtml(page, {
      canvasSize: currentStorybook.canvasSize || '5.5x8.5',
      resolveImageUrl: (url: string) => getImageUrl(url),
      className: 'thumbnail-canonical-page',
      ariaLabel: `Thumbnail source for page ${page.pageNumber}`
    });
    document.body.appendChild(renderHost);

    try {
      const canonicalPage = renderHost.querySelector<HTMLElement>('.canonical-page');
      if (!canonicalPage) throw new Error('The canonical renderer did not create a page node.');
      await document.fonts.ready;
      await waitForCanonicalImages(canonicalPage);
      const canvas = await html2canvas(canonicalPage, {
        backgroundColor: page.backgroundColor || '#ffffff',
        scale: thumbnailScale,
        width: sizeConfig.width,
        height: sizeConfig.height,
        useCORS: true,
        allowTaint: false,
        logging: false,
        imageTimeout: 15000
      });
      return canvas.toDataURL('image/png');
    } catch (error) {
      console.error('Canonical thumbnail generation failed:', error);
      return null;
    } finally {
      renderHost.remove();
    }
  };

  // Generate thumbnails for pages that don't have them and persist to DB
  const generateMissingThumbnails = async (sb: Storybook, pageIndices: number[]) => {
    if (!id) return;
    
    console.log(`Starting background thumbnail generation for ${pageIndices.length} pages...`);
    let generatedCount = 0;
    
    for (const pageIndex of pageIndices) {
      try {
        // Skip thumbnail generation if AI content generation is in progress
        // This prevents race conditions where thumbnails overwrite page changes
        if (isGeneratingAIContent.current) {
          console.log(`⏸️ Skipping thumbnail generation for page ${pageIndex} - AI generation in progress`);
          continue;
        }
        
        // Wait a bit between generations to avoid overwhelming the browser
        if (generatedCount > 0) {
          await new Promise(resolve => setTimeout(resolve, 5000));
        }
        
        // The load response is authoritative here. React may not have committed
        // it to storybookRef yet when this background task starts.
        const thumbnail = await generateThumbnail(pageIndex, true, sb);
        if (thumbnail) {
          // Double-check: skip if AI generation started during thumbnail generation
          if (isGeneratingAIContent.current) {
            console.log(`⏸️ Skipping thumbnail save for page ${pageIndex} - AI generation in progress`);
            continue;
          }
          
          // Update local state with the new thumbnail only (don't save to DB during background task)
          // This prevents race conditions with AI page generation
          setStorybook(prev => {
            if (!prev) return prev;
            const pages = [...prev.pages];
            // Only update the thumbnail, don't replace the entire page
            pages[pageIndex] = {
              ...pages[pageIndex],
              thumbnail
            };
            return { ...prev, pages };
          });
          
          // Update local cache
          setCachedThumbnails(prev => new Map(prev).set(pageIndex, thumbnail));
          
          generatedCount++;
          console.log(`Generated thumbnail for page ${pageIndex} (${generatedCount}/${pageIndices.length})`);
        }
      } catch (error) {
        console.warn(`Failed to generate thumbnail for page ${pageIndex}:`, error);
      }
    }
    
    console.log(`Background thumbnail generation complete: ${generatedCount}/${pageIndices.length} successful`);
  };

  const saveDomain = useEditorSaveDomain({
    storybookId: id,
    storybook,
    setStorybook,
    currentPageIndex,
    plots: savedPlots,
    loading,
    isGenerating: () => isGeneratingAIContent.current,
    generateThumbnail,
    getPageContentHash,
    onPageSaved: (pageIndex, pageHash, thumbnail) => {
      setLastSavedPageStates(previous => new Map(previous).set(pageIndex, pageHash));
      if (thumbnail) {
        setCachedThumbnails(previous => new Map(previous).set(pageIndex, thumbnail));
      }
    }
  });
  const {
    saving,
    setSaving,
    lastSaved,
    setLastSaved,
    saveConflict,
    clearSaveConflict,
    saveStorybook,
    safeSaveWithMerge,
    hasPendingSaves
  } = saveDomain;

  const loadStorybook = async () => {
    if (!id) return;
    try {
      const response = await storybookAPI.getById(id);
      console.log('=== LOADED STORYBOOK ===');
      console.log('storyStylePrompt:', response.storybook.storyStylePrompt);
      console.log('visualStylePrompt:', response.storybook.visualStylePrompt);
      console.log('useReferenceBook:', response.storybook.useReferenceBook);
      console.log('referenceBook:', response.storybook.referenceBook);
      console.log('storyType:', response.storybook.storyType);
      console.log('storyTone:', response.storybook.storyTone);
      console.log('drawingType:', response.storybook.drawingType);
      console.log('drawingStyle:', response.storybook.drawingStyle);
      setStorybook(response.storybook);
      
      // Load saved plots and structured plot data
      if (response.storybook.plots && response.storybook.plots.length > 0) {
        setSavedPlots(response.storybook.plots);
      }
      if (response.storybook.structuredPlotData) {
        setStructuredPlotData(response.storybook.structuredPlotData);
      }
      
      // Initialize thumbnail cache with existing thumbnails and page states
      const initialStates = new Map<number, string>();
      const initialThumbnails = new Map<number, string>();
      const pagesNeedingThumbnails: number[] = [];
      
      response.storybook.pages.forEach((page: Page, index: number) => {
        const pageHash = getPageContentHash(page);
        initialStates.set(index, pageHash);
        if (page.thumbnail) {
          initialThumbnails.set(index, page.thumbnail);
        } else {
          // Track pages that don't have thumbnails
          pagesNeedingThumbnails.push(index);
        }
      });
      setLastSavedPageStates(initialStates);
      setCachedThumbnails(initialThumbnails);
      console.log(`Initialized thumbnail cache for ${initialThumbnails.size} pages`);
      
      // Generate and persist missing thumbnails in the background
      if (pagesNeedingThumbnails.length > 0) {
        console.log(`Generating ${pagesNeedingThumbnails.length} missing thumbnails in background...`);
        // Don't await - let this happen in background
        generateMissingThumbnails(response.storybook, pagesNeedingThumbnails).catch(err => 
          console.warn('Failed to generate missing thumbnails:', err)
        );
      }
      
      // Prefetch signed URLs for gallery images to avoid proxying later
      prefetchSignedUrls(response.storybook).catch(err => console.warn('prefetchSignedUrls failed', err));
    } catch (error) {
      console.error('Failed to load storybook:', error);
      navigate('/dashboard');
    } finally {
      setLoading(false);
    }
  };

  const loadUserDrawingStyles = async () => {
    try {
      const response = await userAPI.getProfile();
      setCustomDrawingStyles(response.user.customDrawingStyles || []);
    } catch (error) {
      console.error('Failed to load custom drawing styles:', error);
    }
  };

  // Show tutorial for new storybooks (only once per session)
  // Skip if we're in setup flow mode
  useEffect(() => {
    if (storybook && !hasSeenTutorial.current && setupFlowStep === 'none') {
      // Check if storybook is new (has no characters, backgrounds, or scenes)
      const isNewStorybook = 
        (!storybook.characters || storybook.characters.length === 0) &&
        (!storybook.backgrounds || storybook.backgrounds.length === 0) &&
        (!storybook.scenes || storybook.scenes.length === 0);
      
      // Only show tutorial if not coming from wizard setup flow
      const isSetupMode = searchParams.get('setup') === 'true';
      const isQuickCreateMode = searchParams.get('quickCreate') === 'true' || Boolean(storybook.quickCreate);
      
      if (isNewStorybook && !isSetupMode && !isQuickCreateMode) {
        // Show AI tutorial first, followed by manual tools tutorial
        setTimeout(() => {
          setShowAITutorial(true);
          hasSeenTutorial.current = true;
        }, 800);
      }
    }
  }, [storybook, setupFlowStep, searchParams]);

  // Setup flow: auto-open modals in sequence when coming from wizard
  useEffect(() => {
    if (!loading && storybook && searchParams.get('setup') === 'true' && setupFlowStep === 'none') {
      // Clear the setup param from URL so refresh doesn't restart the flow
      searchParams.delete('setup');
      setSearchParams(searchParams, { replace: true });
      
      // Start the setup flow with Design modal
      setSetupFlowStep('design');
      setShowDesignModal(true);
    }
  }, [loading, storybook, searchParams, setupFlowStep, setSearchParams]);

  useEffect(() => {
    if (!loading && storybook?.quickCreate && searchParams.get('quickCreate') === 'true') {
      setShowQuickCreate(true);
      setShowAITutorial(false);
      setShowTutorial(false);
    }
  }, [loading, searchParams, storybook?.quickCreate]);

  // Discover the last persisted job after every editor reopen. A missing job is
  // normal for older books, so it does not surface as a workflow error.
  useEffect(() => {
    if (loading || !id) return;
    let cancelled = false;
    void generationJobAPI.getLatest(id).then(response => {
      if (!cancelled) setGenerationJob(response.job || null);
    }).catch(error => {
      if (error?.response?.status !== 404) console.warn('Could not load generation progress:', error);
    });
    return () => { cancelled = true; };
  }, [id, loading]);

  useEffect(() => {
    const updateConnection = () => setGenerationConnection(navigator.onLine ? 'polling' : 'offline');
    window.addEventListener('online', updateConnection);
    window.addEventListener('offline', updateConnection);
    updateConnection();
    return () => {
      window.removeEventListener('online', updateConnection);
      window.removeEventListener('offline', updateConnection);
    };
  }, []);

  const generationJobId = generationJob?.id;
  const generationJobStatus = generationJob?.status;
  const generationJobUpdatedAt = generationJob?.updatedAt;

  useEffect(() => {
    if (!id || !generationJobId || !generationJobStatus || !['queued', 'running', 'paused'].includes(generationJobStatus)) return;
    let cancelled = false;
    const poll = async () => {
      if (!navigator.onLine) {
        setGenerationConnection('offline');
        return;
      }
      try {
        const response = await generationJobAPI.get(id, generationJobId);
        if (!cancelled) {
          setGenerationJob(response.job);
          setGenerationConnection('polling');
        }
      } catch (error) {
        if (!cancelled) setGenerationConnection(navigator.onLine ? 'reconnecting' : 'offline');
      }
    };
    const timer = window.setInterval(() => void poll(), 4000);
    return () => {
      cancelled = true;
      window.clearInterval(timer);
    };
  }, [generationJobId, generationJobStatus, id]);

  // Terminal job state is persisted by the worker. Reload once per saved
  // snapshot so completed pages and Quick Create approvals appear immediately.
  useEffect(() => {
    if (!id || !generationJobId || !generationJobStatus || ['queued', 'running', 'paused'].includes(generationJobStatus)) return;
    const signature = `${generationJobId}:${generationJobUpdatedAt}:${generationJobStatus}`;
    if (generationJobSyncedAtRef.current === signature) return;
    generationJobSyncedAtRef.current = signature;
    void storybookAPI.getById(id).then(response => {
      setStorybook(response.storybook);
      if (response.storybook.structuredPlotData) setStructuredPlotData(response.storybook.structuredPlotData);
      if (response.storybook.plots) setSavedPlots(response.storybook.plots);
      setQuickCreateBusyAction(null);
      void refreshUser();
    }).catch(error => console.warn('Could not refresh the completed background draft:', error));
  }, [generationJobId, generationJobStatus, generationJobUpdatedAt, id, refreshUser]);

  // Handle setup flow progression when modals close
  useEffect(() => {
    if (setupFlowStep === 'design' && !showDesignModal) {
      // Design modal closed, open Story settings
      setSetupFlowStep('story');
      setShowStyleModal(true);
    } else if (setupFlowStep === 'story' && !showStyleModal) {
      // Story modal closed, open Font settings  
      setSetupFlowStep('font');
      setShowFontModal(true);
    } else if (setupFlowStep === 'font' && !showFontModal) {
      // Font modal closed, open Plot modal
      setSetupFlowStep('plot');
      setShowPlotModal(true);
    } else if (setupFlowStep === 'plot' && !showPlotModal) {
      // Plot modal closed, setup complete
      setSetupFlowStep('done');
    }
  }, [setupFlowStep, showDesignModal, showStyleModal, showFontModal, showPlotModal]);

  // Prefetch signed URLs for all storybook images (characters, assets, backgrounds, scenes, pages)
  const prefetchSignedUrls = async (sb: Storybook | null) => {
    if (!sb) return;
    const urls = new Set<string>();

    // Collect from top-level collections
    (sb.characters || []).forEach(c => c.imageUrl && urls.add(c.imageUrl));
    (sb.assets || []).forEach(a => a.imageUrl && urls.add(a.imageUrl));
    (sb.backgrounds || []).forEach(b => b.imageUrl && urls.add(b.imageUrl));
    (sb.scenes || []).forEach(s => s.imageUrl && urls.add(s.imageUrl));

    // Collect from page image elements
    (sb.pages || []).forEach(p => p.imageElements?.forEach(img => img.url && urls.add(img.url)));

    // Filter only GCS URLs
    const gcsUrls = Array.from(urls).filter(u => (
      u.startsWith('https://storage.googleapis.com/') && !getSignedUrlFromCache(u)
    ));
    if (gcsUrls.length === 0) return;

    // A large existing book can contain hundreds of objects. Keep the initial
    // signed-URL fill bounded and skip entries that are already fresh.
    await runWithConcurrency(gcsUrls, 6, async u => {
      try {
        const resp = await imageAPI.getSignedUrl(u);
        const signed = resp?.url || resp?.signedUrl || resp;
        let expiresAt: number | undefined;
        if (resp?.expiresAt) {
          if (typeof resp.expiresAt === 'number') expiresAt = resp.expiresAt;
          else expiresAt = Date.parse(resp.expiresAt);
        }
        if (signed) setSignedUrlInCache(u, signed, expiresAt);
      } catch (err) {
        console.warn('Failed to get signed url for', u, err);
      }
    });
  };

  // Background refresher: refresh any cached signed URLs that are expiring soon.
  // NOTE: don't re-create this effect on every `storybook` state change (that happens frequently
  // during dragging). Use a single interval and trigger an initial refresh only once after
  // the storybook first loads.
  React.useEffect(() => {
    const REFRESH_INTERVAL_MS = 60 * 1000; // check every 60s
    const REFRESH_BEFORE_MS = 2 * 60 * 1000; // refresh entries expiring within 2 minutes

    let mounted = true;

    const refreshOnce = async () => {
      try {
        const entries = getAllEntries();
        const now = Date.now();
        const keys = Object.keys(entries);
        await runWithConcurrency(keys, 6, async orig => {
          const entry = entries[orig];
          if (!entry || !entry.expiresAt) return;
          // If already expired, attempt refresh; if expiring soon, refresh
          if (entry.expiresAt <= now + REFRESH_BEFORE_MS) {
            try {
              const resp = await imageAPI.getSignedUrl(orig);
              const signed = resp?.url || resp?.signedUrl || resp;
              let expiresAt: number | undefined;
              if (resp?.expiresAt) {
                if (typeof resp.expiresAt === 'number') expiresAt = resp.expiresAt;
                else expiresAt = Date.parse(resp.expiresAt);
              }
              if (signed) setSignedUrlInCache(orig, signed, expiresAt);
            } catch (err) {
              console.warn('Failed to refresh signed url for', orig, err);
              // If refresh failed and entry already expired in the past long time, remove it
              if (entry.expiresAt < now - (60 * 60 * 1000)) {
                removeEntry(orig);
              }
            }
          }
        });
      } catch (err) {
        console.warn('Error during signed-url refresh:', err);
      }
    };

    // Start interval (single interval for component lifetime)
    const idInterval = window.setInterval(() => {
      if (!mounted) return;
      refreshOnce();
    }, REFRESH_INTERVAL_MS);

    return () => {
      mounted = false;
      clearInterval(idInterval);
    };
  }, []);

  // Run a one-time immediate refresh after the storybook first loads to populate/refresh cache.
  const _hasRunInitialSignedUrlRefresh = React.useRef(false);
  React.useEffect(() => {
    if (storybook && !_hasRunInitialSignedUrlRefresh.current) {
      _hasRunInitialSignedUrlRefresh.current = true;
      (async () => {
        try {
          const entries = getAllEntries();
          const now = Date.now();
          const REFRESH_BEFORE_MS = 2 * 60 * 1000;
          const keys = Object.keys(entries);
          await runWithConcurrency(keys, 6, async orig => {
            const entry = entries[orig];
            if (!entry || !entry.expiresAt) return;
            if (entry.expiresAt <= now + REFRESH_BEFORE_MS) {
              try {
                const resp = await imageAPI.getSignedUrl(orig);
                const signed = resp?.url || resp?.signedUrl || resp;
                let expiresAt: number | undefined;
                if (resp?.expiresAt) {
                  if (typeof resp.expiresAt === 'number') expiresAt = resp.expiresAt;
                  else expiresAt = Date.parse(resp.expiresAt);
                }
                if (signed) setSignedUrlInCache(orig, signed, expiresAt);
              } catch (err) {
                console.warn('Failed to refresh signed url for', orig, err);
              }
            }
          });
        } catch (err) {
          console.warn('Error during initial signed-url refresh:', err);
        }
      })();
    }
  }, [storybook]);

  // Handle page selection with auto-save of current page
  const handlePageSelect = async (newPageIndex: number) => {
    if (isNarrowViewport && pageStripExpanded) closeResponsivePanel('pages');
    if (newPageIndex === currentPageIndex) return;
    
    // Don't auto-save during AI generation - it handles its own saves
    if (isGeneratingAIContent.current) {
      setCurrentPageIndex(newPageIndex);
      return;
    }
    
    // Save current page before navigating (fire and forget to keep UI responsive)
    if (storybook && id) {
      console.log(`📄 Auto-saving page ${currentPageIndex + 1} before navigating to page ${newPageIndex + 1}...`);
      saveStorybook().catch(err => {
        console.error('Auto-save on page change failed:', err);
      });
    }
    
    // Navigate immediately for responsiveness
    setCurrentPageIndex(newPageIndex);
  };

  const addPage = () => {
    if (!storybook) return;

    const newPage: Page = {
      id: crypto.randomUUID(),
      pageNumber: currentPageIndex + 2,
      backgroundColor: '#ffffff',
      textElements: [],
      imageElements: [],
      role: 'content',
      layout: { template: 'custom', version: PAGE_LAYOUT_VERSION }
    };
    const insertion = insertPageAfterCurrent(storybook, currentPageIndex, newPage);
    const insertIndex = insertion.insertedIndex;
    setStorybook(insertion.storybook);
    
    // Also add empty entry to structuredPlotData.pages if it exists
    if (structuredPlotData?.pages) {
      // Calculate content page index (excluding system pages)
      // The new page is at insertIndex in the full pages array
      // We need to find where to insert in the plot data (which only has content pages)
      const systemPagesBeforeInsert = storybook.pages.slice(0, insertIndex).filter(p => p.isSystemPage).length;
      const plotInsertIndex = insertIndex - systemPagesBeforeInsert;
      
      const newPlotPage = {
        backgroundName: '',
        characterNames: [],
        scenePrompt: '',
        textBoxes: []
      };
      
      const updatedPlotPages = [
        ...structuredPlotData.pages.slice(0, plotInsertIndex),
        newPlotPage,
        ...structuredPlotData.pages.slice(plotInsertIndex)
      ];
      
      setStructuredPlotData({
        ...structuredPlotData,
        pages: updatedPlotPages
      });
    }
    
    // Navigate to the newly created page
    setCurrentPageIndex(insertIndex);
  };

  const deletePage = (pageIndex: number) => {
    if (!storybook || storybook.pages.length <= 3) return; // Keep minimum 3 pages: Title cover, Page 1, Back cover
    
    const pageToDelete = storybook.pages[pageIndex];
    
    // Prevent deletion of system pages (Title cover and Back cover)
    if (pageToDelete.isSystemPage) {
      alert('Cannot delete Title cover or Back cover pages.');
      return;
    }
    
    // Also remove from structuredPlotData.pages if it exists
    if (structuredPlotData?.pages) {
      // Calculate content page index (excluding system pages)
      const systemPagesBeforeDelete = storybook.pages.slice(0, pageIndex).filter(p => p.isSystemPage).length;
      const plotDeleteIndex = pageIndex - systemPagesBeforeDelete;
      
      if (plotDeleteIndex >= 0 && plotDeleteIndex < structuredPlotData.pages.length) {
        const updatedPlotPages = structuredPlotData.pages.filter((_: any, i: number) => i !== plotDeleteIndex);
        setStructuredPlotData({
          ...structuredPlotData,
          pages: updatedPlotPages
        });
      }
    }
    
    const updatedStorybook = deletePageAtIndex(storybook, pageIndex);
    setStorybook(updatedStorybook);
    if (currentPageIndex >= updatedStorybook.pages.length) {
      setCurrentPageIndex(updatedStorybook.pages.length - 1);
    }
  };

  const handleApplyPlots = (plots: Array<{ pageNumber: number; text: string }>, plotData?: any) => {
    if (!storybook) return;

    // Save plots for future use
    setSavedPlots(plots);
    if (plotData) {
      setStructuredPlotData(plotData);
    }
    
    // Persist to database
    if (id) {
      const updatedStorybook = {
        ...storybook,
        plots,
        structuredPlotData: plotData || null
      };
      storybookAPI.update(id, updatedStorybook).catch(error => {
        console.error('Failed to save plots:', error);
      });
    }
  };

  const handleApplyStructuredPlot = async (plotData: any) => {
    if (!storybook || !id) return;
    
    console.log('📖 Applying structured plot data:', plotData);
    setLoading(true);
    
    try {
      // Plan and paginate every source page before any character/background
      // generation. This makes story order and continuation count final before
      // an image request can spend tokens.
      const structuredSizeConfig = CANVAS_SIZES.find(
        size => size.value === (storybook.canvasSize || '5.5x8.5')
      ) || CANVAS_SIZES[3];
      const structuredCanvasSize = {
        width: structuredSizeConfig.width,
        height: structuredSizeConfig.height
      };
      const structuredDefaultProps = storybook.defaultTextProperties || {
        fontSize: 18,
        fontFamily: 'Roboto',
        fontWeight: 'normal',
        color: '#2B2118'
      };
      const structuredTypography = createStoryTextTypography(
        structuredDefaultProps.fontFamily,
        structuredDefaultProps.fontWeight,
        structuredDefaultProps.fontStyle || 'normal'
      );
      const structuredFontSize = Math.max(
        structuredDefaultProps.fontSize,
        MIN_READABLE_NARRATION_FONT_SIZE
      );
      const plannedStructuredPages: any[] = [];
      for (const sourcePage of plotData.pages || []) {
        const sourcePageId = crypto.randomUUID();
        const sourceBlocks = (sourcePage.textBoxes || []).map((block: any, sourceIndex: number) => ({
          ...block,
          sourceTextBoxId: block.sourceTextBoxId || `${sourcePageId}:text:${sourceIndex}`
        }));
        const styleResolution = resolvePageDesignStyle(storybook.designStyle, sourceBlocks);
        const requestedTemplate = resolveCanonicalTemplate(styleResolution.resolvedStyle);
        // Structured batch creation does not run per-page semantic vision. A
        // full-bleed caption without regions would be unprovable at preflight,
        // so batch pages use the protected equivalent by default.
        const firstTemplate = (requestedTemplate === 'full-bleed-caption'
          ? 'image-top-text-bottom'
          : requestedTemplate || 'image-top-text-bottom') as Exclude<PageLayoutTemplate, 'custom'>;
        const measurer = sourceBlocks.length > 0
          ? await createProductionTextBlockMeasurer(sourceBlocks.map((block: any) => ({
              content: block.content,
              fontSize: structuredFontSize,
              typography: structuredTypography
            })))
          : (() => { throw new Error('Empty narration must not be measured.'); });
        const flow = planNarrationPageFlow({
          sourcePageId,
          sourceBlocks,
          firstTemplate,
          buildLayout: (blocks, template) => buildDeterministicPageLayout({
            canvasSize: structuredCanvasSize,
            textBoxes: [...blocks],
            template,
            preferredFontSize: structuredFontSize,
            minimumFontSize: MIN_READABLE_NARRATION_FONT_SIZE,
            typography: structuredTypography,
            measureTextBlock: measurer
          })
        });
        flow.pages.forEach(flowPage => plannedStructuredPages.push({
          ...sourcePage,
          textBoxes: flowPage.blocks,
          _layout: flowPage.layout,
          _sourcePageId: sourcePageId,
          _continuationIndex: flowPage.continuationIndex,
          _continuationCount: flowPage.continuationCount,
          ...(!flowPage.layout.imageRequired
            ? { backgroundName: undefined, characterNames: [], scenePrompt: sourcePage.scenePrompt }
            : {})
        }));
      }

      const illustratedCharacterNames = new Set<string>(plannedStructuredPages
        .filter(page => page._layout?.imageRequired)
        .flatMap(page => page.characterNames || []));
      const illustratedBackgroundNames = new Set<string>(plannedStructuredPages
        .filter(page => page._layout?.imageRequired && page.backgroundName)
        .map(page => page.backgroundName));

      // Step 1: Generate character images
      console.log('👤 Generating characters...');
      const generatedCharacters = new Map<string, string>(); // name -> imageUrl
      
      for (const char of (plotData.characters || []).filter((candidate: any) => (
        illustratedCharacterNames.has(candidate.name)
      ))) {
        try {
          console.log(`  - Generating character: ${char.name}`);
          const imageUrl = await generateCharacter(char.name, char.description);
          if (imageUrl) {
            generatedCharacters.set(char.name, imageUrl);
          }
        } catch (error) {
          console.error(`Failed to generate character ${char.name}:`, error);
        }
      }
      
      // Step 2: Generate background images
      console.log('🖼️ Generating backgrounds...');
      const generatedBackgrounds = new Map<string, string>(); // name -> imageUrl
      
      for (const bg of (plotData.backgrounds || []).filter((candidate: any) => (
        illustratedBackgroundNames.has(candidate.name)
      ))) {
        try {
          console.log(`  - Generating background: ${bg.name}`);
          const imageUrl = await generateBackground(bg.name, bg.description);
          if (imageUrl) {
            generatedBackgrounds.set(bg.name, imageUrl);
          }
        } catch (error) {
          console.error(`Failed to generate background ${bg.name}:`, error);
        }
      }
      
      // Step 3: Reload storybook to get updated character and background data
      await loadStorybook();
      const freshStorybook = await storybookAPI.getById(id);
      if (!freshStorybook.success || !freshStorybook.storybook) {
        throw new Error('Failed to reload storybook');
      }
      
      const updatedStorybook = freshStorybook.storybook;
      
      // Step 4: Create pages with proper metadata
      console.log('📄 Creating pages...');
      const newPages: Page[] = [];
      
      for (const pageData of plannedStructuredPages) {
        const canvasSizeString = updatedStorybook.canvasSize || '5.5x8.5';
        const pageSizeConfig = CANVAS_SIZES.find(size => size.value === canvasSizeString) || CANVAS_SIZES[3];
        const canvasSize = { width: pageSizeConfig.width, height: pageSizeConfig.height };
        const textBoxes = pageData.textBoxes || [];
        const defaultProps = updatedStorybook.defaultTextProperties || {
          fontSize: 18,
          fontFamily: 'Roboto',
          fontWeight: 'normal',
          color: '#2B2118',
          textAlign: 'left' as const,
          verticalAlign: 'top' as const
        };
        const pageLayout: DeterministicPageLayout = pageData._layout;
        
        // Find background image URL
        let backgroundImageUrl: string | undefined;
        if (pageLayout.imageRequired && pageData.backgroundName) {
          const bgEntry = updatedStorybook.backgrounds?.find((bg: any) => bg.name === pageData.backgroundName);
          backgroundImageUrl = bgEntry?.imageUrl;
        }

        if (backgroundImageUrl && pageLayout.imageRequired) {
          try {
            const resized = await aiAPI.resizeImageToCanvas(
              backgroundImageUrl,
              pageLayout.imageBounds.width,
              pageLayout.imageBounds.height
            );
            if (resized.success && resized.imageUrl) {
              backgroundImageUrl = resized.imageUrl;
            }
          } catch (resizeError) {
            console.warn('Could not fit structured-plot background to safe panel:', resizeError);
          }
        }
        
        // Create page
        const page: Page = {
          id: pageData._continuationIndex === 0 ? pageData._sourcePageId : crypto.randomUUID(),
          pageNumber: newPages.length + 1,
          backgroundColor: pageLayout?.panelBackgroundColor || '#ffffff',
          imageElements: [],
          textElements: [],
          isSystemPage: false,
          role: 'content',
          layout: {
            template: pageLayout.canonicalTemplate,
            version: PAGE_LAYOUT_VERSION,
            sourcePageId: pageData._sourcePageId,
            continuationIndex: pageData._continuationIndex,
            continuationCount: pageData._continuationCount,
            allowSubjectOverlap: false
          },
          aiScenePrompt: pageData.scenePrompt || undefined,
          aiCharacterNames: pageLayout.imageRequired && pageData.characterNames?.length
            ? pageData.characterNames
            : undefined,
          aiBackgroundName: pageLayout.imageRequired ? pageData.backgroundName || undefined : undefined,
          aiTextBoxes: textBoxes.length ? textBoxes : undefined
        };
        
        // Add background image as first image element if available
        if (backgroundImageUrl && pageLayout.imageRequired) {
          page.imageElements.push({
            id: crypto.randomUUID(),
            url: backgroundImageUrl,
            x: pageLayout?.imageBounds.x || 0,
            y: pageLayout?.imageBounds.y || 0,
            width: pageLayout?.imageBounds.width || canvasSize.width,
            height: pageLayout?.imageBounds.height || canvasSize.height,
            rotation: 0,
            shape: 'rect',
            feather: 0,
            layoutRole: 'illustration',
            fit: 'cover',
            bleed: pageLayout.canonicalTemplate === 'full-bleed-caption' ? 'full' : 'none'
          });
        }
        
        const layoutElements = pageLayout?.textElements || textBoxes.map((_: any, index: number) => ({
          textBoxIndex: index,
          x: 40,
          y: 40 + index * 80,
          width: canvasSize.width - 80,
          height: 70,
          fontSize: defaultProps.fontSize,
          fontFamily: resolveProductionFontFamily(defaultProps.fontFamily),
          fontWeight: defaultProps.fontWeight,
          fontStyle: defaultProps.fontStyle || 'normal',
          lineHeight: STORY_TEXT_LINE_HEIGHT,
          padding: STORY_TEXT_PADDING,
          color: defaultProps.color,
          backgroundColor: 'transparent',
          textAlign: defaultProps.textAlign || 'left',
          verticalAlign: defaultProps.verticalAlign || 'top',
          rotation: 0
        }));

        for (const layoutElement of layoutElements) {
          const textBox = textBoxes[layoutElement.textBoxIndex];
          if (!textBox) continue;

          // Format content based on type
          let content = textBox.content;
          if (textBox.type === 'dialogue' && textBox.character) {
            content = `${textBox.character}: "${textBox.content}"`;
          }
          
          const textElement: TextElement = {
            id: crypto.randomUUID(),
            content,
            x: layoutElement.x,
            y: layoutElement.y,
            fontSize: layoutElement.fontSize || defaultProps.fontSize,
            fontFamily: pageLayout
              ? layoutElement.fontFamily
              : resolveProductionFontFamily(defaultProps.fontFamily),
            fontWeight: pageLayout ? layoutElement.fontWeight : defaultProps.fontWeight,
            fontStyle: pageLayout ? layoutElement.fontStyle : defaultProps.fontStyle,
            color: layoutElement.color || defaultProps.color,
            rotation: layoutElement.rotation || 0,
            width: layoutElement.width,
            height: layoutElement.height,
            textAlign: layoutElement.textAlign || defaultProps.textAlign,
            verticalAlign: layoutElement.verticalAlign || defaultProps.verticalAlign,
            backgroundColor: pageLayout ? layoutElement.backgroundColor : layoutElement.backgroundColor,
            backgroundOpacity: pageLayout.panelBackgroundOpacity,
            borderWidth: 0,
            borderStyle: 'none',
            layoutRole: textBox.type === 'dialogue'
              ? 'dialogue'
              : pageLayout.canonicalTemplate === 'title'
                ? 'title'
                : pageLayout.canonicalTemplate === 'full-bleed-caption'
                  ? 'caption'
                  : 'narration',
            sourceTextBoxId: textBox.sourceTextBoxId
          };
          
          page.textElements.push(textElement);
        }
        
        newPages.push(page);
      }
      
      // Step 5: Update storybook with new pages using safeSaveWithMerge
      const savedStorybook = await safeSaveWithMerge((freshData: Storybook) => {
        const mergedPages = [...freshData.pages];
        const backCoverIndex = mergedPages.findIndex(page => (
          page.role === 'back-cover'
          || (page.isSystemPage && /back/i.test(page.pageTitle || ''))
        ));
        mergedPages.splice(backCoverIndex >= 0 ? backCoverIndex : mergedPages.length, 0, ...newPages);
        let contentPageNumber = 1;
        mergedPages.forEach(page => {
          if (!page.isSystemPage) {
            page.pageNumber = contentPageNumber;
            contentPageNumber += 1;
          }
        });
        return {
          ...freshData,
          pages: mergedPages,
          // Merge characters - keep all fresh + our new ones
          characters: mergeArraysByField(freshData.characters, updatedStorybook.characters, 'name'),
          // Merge backgrounds - keep all fresh + our new ones
          backgrounds: mergeArraysByField(freshData.backgrounds, updatedStorybook.backgrounds, 'name'),
        };
      }, 'structured plot pages');
      
      if (savedStorybook) {
        const firstNewPageIndex = savedStorybook.pages.findIndex(page => page.id === newPages[0]?.id);
        setCurrentPageIndex(firstNewPageIndex >= 0 ? firstNewPageIndex : 0);
      }
      
      console.log('✅ Structured plot applied successfully');
      console.log(`Created ${plotData.characters?.length || 0} characters, ${plotData.backgrounds?.length || 0} backgrounds, and ${newPages.length} pages!`);
      
    } catch (error: any) {
      console.error('Failed to apply structured plot:', error);
    } finally {
      setLoading(false);
    }
  };

  // Regenerate the current page using structured plot data
  const handleRegenerateCurrentPage = async () => {
    if (!storybook || !id || !structuredPlotData || !currentPage) return;
    
    // Get the page number (content pages are 1-indexed, system pages have special numbers)
    const pageNumber = currentPage.pageNumber;
    
    // Find the corresponding page in structured plot data
    const plotPage = structuredPlotData.pages?.find((p: any, idx: number) => idx + 1 === pageNumber);
    
    if (!plotPage) {
      alert(`No structured plot data found for page ${pageNumber}. Please generate a plot first.`);
      return;
    }
    
    // Confirm with user
    const confirmRegenerate = window.confirm(
      `This will regenerate Page ${pageNumber} and replace the current content. Are you sure?`
    );
    
    if (!confirmRegenerate) return;
    
    try {
      // Keep the current page intact until every AI/layout step succeeds. The
      // final ID-targeted flow replacement is the only write.
      await handleGeneratePage({
        ...plotPage,
        pageNumber: pageNumber
      }, { replaceExisting: true, targetPageId: currentPage.id });
    } catch (error) {
      console.error('Failed to regenerate page:', error);
      isGeneratingAIContent.current = false;
    }
    // Note: isGeneratingAIContent flag will be cleared by handleGeneratePage's finally block
  };

  const handleGeneratePage = async (
    pageData: any,
    options?: { replaceExisting?: boolean; targetPageId?: string }
  ): Promise<boolean> => {
    if (!storybook || !id) return false;
    
    const replaceExisting = options?.replaceExisting ?? false;
    console.log('🎨 Generating individual page:', pageData, replaceExisting ? '(replacing existing)' : '');
    
    // Set flag to prevent auto-save during page generation
    isGeneratingAIContent.current = true;
    
    // Validate that we have at least some content to generate (scene prompt, characters, or background)
    const hasScenePrompt = pageData.scenePrompt && pageData.scenePrompt.trim().length > 0;
    const hasCharacters = pageData.characterNames && pageData.characterNames.length > 0;
    const hasBackground = pageData.backgroundName && pageData.backgroundName.trim().length > 0;
    const hasTextBoxes = pageData.textBoxes && pageData.textBoxes.length > 0;
    
    if (!hasScenePrompt && !hasCharacters && !hasBackground && !hasTextBoxes) {
      isGeneratingAIContent.current = false;
      alert('Page must have at least a scene prompt, characters, background, or text content to generate');
      return false;
    }
    if (hasCharacters) {
      const readiness = identityReadinessForBook(storybook);
      if (!readiness.ready) {
        isGeneratingAIContent.current = false;
        alert(`Complete Cast & Style identity approval before illustrating this page. ${readiness.issues[0]?.message || ''}`.trim());
        return false;
      }
    }
    
    // Ensure textBoxes is always an array (even if empty)
    if (!pageData.textBoxes) {
      pageData.textBoxes = [];
    }
    
    // Determine target content page number (1-indexed from pageData)
    // This is the content page number (1 = first content page, 2 = second content page, etc.)
    // NOT the array index and NOT the pageNumber property which includes system pages
    const targetContentPageNumber = pageData.pageNumber || (storybook.pages.filter(p => !p.isSystemPage).length + 1);
    
    const resolveGenerationTarget = (book: Storybook) => {
      const contentPages = book.pages
        .map((page, index) => ({ page, index }))
        .filter(({ page }) => !page.isSystemPage);
      const stableTargetIndex = options?.targetPageId
        ? book.pages.findIndex(page => page.id === options.targetPageId && !page.isSystemPage)
        : -1;
      let pageIndex = stableTargetIndex >= 0
        ? stableTargetIndex
        : targetContentPageNumber <= contentPages.length
          ? contentPages[targetContentPageNumber - 1].index
          : contentPages.length > 0
            ? contentPages[contentPages.length - 1].index + 1
            : book.pages.length;
      let insert = false;
      const existingPage = book.pages[pageIndex];
      if (existingPage?.isSystemPage) {
        insert = true;
        const firstContentIndex = book.pages.findIndex(page => !page.isSystemPage);
        pageIndex = firstContentIndex >= 0 ? firstContentIndex : book.pages.length;
      } else if (existingPage && !replaceExisting) {
        insert = existingPage.imageElements.length > 0 || existingPage.textElements.length > 0;
      }
      return { pageIndex, shouldInsert: insert };
    };
    let resolvedTarget = resolveGenerationTarget(storybook);
    let targetPageIndex = resolvedTarget.pageIndex;
    let shouldInsertNewPage = resolvedTarget.shouldInsert;
    
    // Initialize progress steps
    const steps = [
      { id: 'load', message: 'Loading storybook data...', status: 'pending' as const },
      { id: 'check-chars', message: 'Checking for missing characters...', status: 'pending' as const },
      { id: 'check-bg', message: 'Checking for missing background...', status: 'pending' as const },
      { id: 'scene', message: 'Composing scene with characters and background...', status: 'pending' as const },
      { id: 'layout', message: 'Analyzing scene and planning text layout...', status: 'pending' as const },
      { id: 'create-page', message: 'Creating page with optimized layout...', status: 'pending' as const },
      { id: 'save', message: 'Saving storybook...', status: 'pending' as const }
    ];
    
    generationDomain.startPage(steps, targetContentPageNumber);
    
    try {
      // Step 1: Load fresh storybook data
      updateProgressStep('load', 'in-progress');
      const freshStorybook = await storybookAPI.getById(id);
      if (!freshStorybook.success || !freshStorybook.storybook) {
        throw new Error('Failed to load storybook');
      }
      let updatedStorybook = freshStorybook.storybook;
      // Batch generation can insert continuation pages before this call. Resolve
      // the stable target ID against the fresh book instead of a stale ordinal.
      resolvedTarget = resolveGenerationTarget(updatedStorybook);
      targetPageIndex = resolvedTarget.pageIndex;
      shouldInsertNewPage = resolvedTarget.shouldInsert;

      // Resolve the page template before generating or resizing imagery. Long
      // narration is moved into a protected panel even on legacy full-scene
      // books, so we never spend image tokens before detecting text overflow.
      const canvasSizeString = updatedStorybook.canvasSize || '5.5x8.5';
      const sizeConfig = CANVAS_SIZES.find(s => s.value === canvasSizeString) || CANVAS_SIZES[3];
      const canvasSize = { width: sizeConfig.width, height: sizeConfig.height };
      const generationDefaultTextProps = updatedStorybook.defaultTextProperties || {
        fontSize: 18,
        fontFamily: 'Roboto',
        fontWeight: 'normal',
        color: '#2B2118',
        textAlign: 'left' as const,
        verticalAlign: 'top' as const
      };
      const designStyleResolution = resolvePageDesignStyle(
        updatedStorybook.designStyle,
        pageData.textBoxes
      );
      const canonicalTemplate = resolveCanonicalTemplate(designStyleResolution.resolvedStyle);
      const firstTemplate = (canonicalTemplate || 'full-bleed-caption') as Exclude<PageLayoutTemplate, 'custom'>;
      const targetExistingPage = !shouldInsertNewPage
        ? updatedStorybook.pages[targetPageIndex]
        : undefined;
      const plannedSourcePageId = targetExistingPage
        ? targetExistingPage.layout?.continuationIndex === 0
          ? targetExistingPage.layout.sourcePageId || targetExistingPage.id
          : targetExistingPage.id
        : crypto.randomUUID();
      const sourcePageData = {
        ...pageData,
        textBoxes: pageData.textBoxes.map((textBox: any, sourceIndex: number) => ({
          ...textBox,
          sourceTextBoxId: textBox.sourceTextBoxId || `${plannedSourcePageId}:text:${sourceIndex}`
        }))
      };
      const productionTypography = createStoryTextTypography(
        generationDefaultTextProps.fontFamily,
        generationDefaultTextProps.fontWeight,
        generationDefaultTextProps.fontStyle || 'normal'
      );
      const planningFontSize = Math.max(
        generationDefaultTextProps.fontSize,
        MIN_READABLE_NARRATION_FONT_SIZE
      );
      const planningMeasurer = sourcePageData.textBoxes.length > 0
        ? await createProductionTextBlockMeasurer(sourcePageData.textBoxes.map((textBox: any) => ({
            content: textBox.content,
            fontSize: planningFontSize,
            typography: createStoryTextTypography(
              textBox.fontFamily || productionTypography.fontFamily,
              textBox.fontWeight || productionTypography.fontWeight,
              textBox.fontStyle || productionTypography.fontStyle,
              productionTypography.padding
            )
          })))
        : (() => { throw new Error('Empty narration must not be measured.'); });
      const narrationFlow = planNarrationPageFlow({
        sourcePageId: plannedSourcePageId,
        sourceBlocks: sourcePageData.textBoxes,
        firstTemplate,
        sourceMetadata: {
          scenePrompt: sourcePageData.scenePrompt,
          characterNames: sourcePageData.characterNames,
          backgroundName: sourcePageData.backgroundName
        },
        buildLayout: (blocks, template) => buildDeterministicPageLayout({
          canvasSize,
          textBoxes: [...blocks],
          template,
          preferredFontSize: planningFontSize,
          minimumFontSize: MIN_READABLE_NARRATION_FONT_SIZE,
          typography: productionTypography,
          measureTextBlock: planningMeasurer
        })
      });
      pageData = {
        ...pageData,
        textBoxes: narrationFlow.pages[0].blocks
      };
      const semanticLayoutRequested = designStyleResolution.resolvedStyle === 'custom'
        || (firstTemplate === 'full-bleed-caption' && pageData.textBoxes.length > 0);
      let deterministicPageLayout: DeterministicPageLayout | null = semanticLayoutRequested
        ? null
        : narrationFlow.pages[0].layout;
      const pageNeedsIllustration = semanticLayoutRequested
        ? true
        : narrationFlow.pages[0].layout.imageRequired;
      if (!pageNeedsIllustration) {
        pageData = { ...pageData, scenePrompt: '', characterNames: [], backgroundName: '' };
      }
      const protectedSceneAspectRatio = deterministicPageLayout?.imageRequired
        ? resolveProviderAspectRatio(deterministicPageLayout.imageBounds)
        : undefined;

      if (designStyleResolution.autoAdjusted && designStyleResolution.reason) {
        console.log(`📐 Layout automatically adjusted: ${designStyleResolution.reason}`);
      }
      if (narrationFlow.pages.length > 1) {
        console.log(`📖 Narration planned across ${narrationFlow.pages.length} pages before image generation.`);
      }
      updateProgressStep('load', 'completed');
      
      // Step 2: Check and generate missing characters
      updateProgressStep('check-chars', 'in-progress');
      const existingCharacterNames = (updatedStorybook.characters || []).map((c: any) => c.name);
      const missingCharacterNames = (pageData.characterNames || []).filter(
        (name: string) => !existingCharacterNames.includes(name)
      );
      
      console.log('🔍 Character check:');
      console.log('   Existing characters:', existingCharacterNames);
      console.log('   Required characters:', pageData.characterNames);
      console.log('   Missing characters:', missingCharacterNames);
      
      if (missingCharacterNames.length > 0 && structuredPlotData) {
        const charToGenerate = structuredPlotData.characters?.filter(
          (char: any) => missingCharacterNames.includes(char.name)
        ) || [];
        
        for (const char of charToGenerate) {
          updateProgressStep('check-chars', 'in-progress', `Generating character: ${char.name}...`);
          await generateCharacter(char.name, char.description, [], 'gemini');
          
          // Verify character was saved by checking backend with retries
          updateProgressStep('check-chars', 'in-progress', `Verifying ${char.name} was saved...`);
          let verified = null;
          let retries = 0;
          const maxRetries = 10;
          let delay = 500;
          
          while (retries < maxRetries) {
            await new Promise(resolve => setTimeout(resolve, delay));
            try {
              verified = await storybookAPI.getById(id);
              const charExists = verified.storybook.characters?.some((c: any) => c.name === char.name);
              
              if (charExists) {
                console.log(`✅ Verified character "${char.name}" saved to database`);
                break;
              }
            } catch (fetchError) {
              console.warn(`⚠️ Error fetching during verification:`, fetchError);
            }
            
            retries++;
            delay = Math.min(delay * 1.5, 3000);
            console.log(`⏳ Character "${char.name}" not found yet, retry ${retries}/${maxRetries} (next delay: ${delay}ms)...`);
            
            if (retries === maxRetries) {
              console.warn(`⚠️ Could not verify character save: ${char.name} after ${maxRetries} attempts`);
            }
          }
          
          // Update with verified data - merge characters only
          if (verified) {
            const newCharacters = verified.storybook.characters;
            console.log('🔍 Verified characters from DB:', newCharacters?.map((c: any) => ({ name: c.name, hasImage: !!c.imageUrl })));
            updatedStorybook = {
              ...updatedStorybook,
              characters: newCharacters
            };
          }
        }
        
        // Final reload to ensure we have all characters
        const finalReload = await storybookAPI.getById(id);
        const finalCharacters = finalReload.storybook.characters;
        console.log('🔍 Final characters from DB:', finalCharacters?.map((c: any) => ({ name: c.name, hasImage: !!c.imageUrl, thumbnailUrl: c.thumbnailUrl })));
        updatedStorybook = {
          ...updatedStorybook,
          characters: finalCharacters
        };
        console.log('📚 All characters loaded:', updatedStorybook.characters?.map((c: any) => `${c.name} (${c.imageUrl ? 'has image' : 'NO IMAGE'})`));
      }
      updateProgressStep('check-chars', 'completed', 
        missingCharacterNames.length > 0 ? `Generated ${missingCharacterNames.length} character(s)` : 'All characters exist'
      );
      
      // Step 3: Check and generate missing background
      updateProgressStep('check-bg', 'in-progress');
      let backgroundImageUrl: string | undefined;
      if (pageData.backgroundName) {
        const bgEntry = (updatedStorybook.backgrounds || []).find((bg: any) => bg.name === pageData.backgroundName);
        if (!bgEntry && structuredPlotData) {
          const bgToGenerate = structuredPlotData.backgrounds?.find(
            (bg: any) => bg.name === pageData.backgroundName
          );
          if (bgToGenerate) {
            updateProgressStep('check-bg', 'in-progress', `Generating background: ${bgToGenerate.name}...`);
            await generateBackground(bgToGenerate.name, bgToGenerate.description, [], 'gemini');
            
            // Verify background was saved by checking backend with retries
            updateProgressStep('check-bg', 'in-progress', `Verifying ${bgToGenerate.name} was saved...`);
            let verified = null;
            let retries = 0;
            const maxRetries = 10;
            let delay = 500;
            
            while (retries < maxRetries) {
              await new Promise(resolve => setTimeout(resolve, delay));
              try {
                verified = await storybookAPI.getById(id);
                const newBg = verified.storybook.backgrounds?.find((bg: any) => bg.name === pageData.backgroundName);
                
                if (newBg) {
                  console.log(`✅ Verified background "${bgToGenerate.name}" saved to database`);
                  backgroundImageUrl = newBg.imageUrl;
                  break;
                }
              } catch (fetchError) {
                console.warn(`⚠️ Error fetching during verification:`, fetchError);
              }
              
              retries++;
              delay = Math.min(delay * 1.5, 3000);
              console.log(`⏳ Background "${bgToGenerate.name}" not found yet, retry ${retries}/${maxRetries} (next delay: ${delay}ms)...`);
              
              if (retries === maxRetries) {
                console.warn(`⚠️ Could not verify background save: ${bgToGenerate.name} after ${maxRetries} attempts`);
              }
            }
            
            // Update with verified data - merge backgrounds only
            if (verified) {
              updatedStorybook = {
                ...updatedStorybook,
                backgrounds: verified.storybook.backgrounds
              };
              console.log('📚 After background generation, characters still present:', updatedStorybook.characters?.map((c: any) => c.name));
            }
          }
        } else {
          backgroundImageUrl = bgEntry?.imageUrl;
        }
        const approvedBackground = (updatedStorybook.backgrounds || [])
          .find((bg: any) => bg.name === pageData.backgroundName);
        if (backgroundImageUrl && (
          approvedBackground?.approvalStatus !== 'approved'
          || !approvedBackground?.assetId
          || !/^[a-f0-9]{64}$/.test(approvedBackground?.contentSha256 || '')
        )) {
          throw new Error(`Regenerate or approve ${pageData.backgroundName} as a versioned background reference before using it in a scene.`);
        }
      }
      updateProgressStep('check-bg', 'completed', 
        backgroundImageUrl ? 'Background ready' : 'No background needed'
      );
      
      // Step 4: Compose scene with characters and background
      updateProgressStep('scene', 'in-progress');
      let sceneImageUrl: string | undefined;
      const scenePromptForGeneration = deterministicPageLayout
        ? `${pageData.scenePrompt || ''}\n\nLAYOUT REQUIREMENT: Compose the complete illustration inside a ${deterministicPageLayout.imageBounds.width}:${deterministicPageLayout.imageBounds.height} image frame. Keep every face, body, and important object away from the frame edges. Do not render words, captions, labels, or typography; the application adds narration in a separate protected panel.`
        : pageData.scenePrompt;
      
      console.log('🎬 Scene composition check:');
      console.log('   Scene prompt:', pageData.scenePrompt);
      console.log('   Character names:', pageData.characterNames);
      console.log('   Available characters:', updatedStorybook.characters?.map((c: any) => c.name));
      
      if (pageData.scenePrompt && pageData.characterNames && pageData.characterNames.length > 0) {
        updateProgressStep('scene', 'in-progress', 'Preparing to compose scene...');
        
        // Resolve each identity once, then derive names, images, and heights
        // from the same ordered records so a missing image cannot shift labels.
        const characterReferences = pageData.characterNames.map((name: string) => {
          const character = updatedStorybook.characters?.find((candidate: any) =>
            candidate.name === name || candidate.name.toLowerCase() === name.toLowerCase()
          );
          console.log(`   Looking for character "${name}":`, character ? `Found (${character.imageUrl})` : 'Not found');
          return {
            requestedName: name,
            name: character?.name || name,
            imageUrl: approvedCharacterAnchor(character),
            height: character?.heightCategory || 'medium'
          };
        });
        const missingCharacterNames = characterReferences
          .filter((reference: any) => !reference.imageUrl)
          .map((reference: any) => reference.requestedName);
        if (missingCharacterNames.length > 0) {
          throw new Error(
            `Add an approved image for ${missingCharacterNames.join(', ')} before generating this page.`
          );
        }
        const resolvedCharacterNames = characterReferences.map((reference: any) => reference.name);
        const characterImages = characterReferences.map((reference: any) => reference.imageUrl as string);
        const characterHeights = characterReferences.map((reference: any) => reference.height);
        
        console.log('   Character images found:', characterImages.length);
        console.log('   Background URL:', backgroundImageUrl);
        
        if (characterImages.length > 0) {
          updateProgressStep('scene', 'in-progress', `Composing scene with ${characterImages.length} character(s)...`);
          
          const response = await aiAPI.composeScene(
            scenePromptForGeneration,
            characterImages,
            backgroundImageUrl,
            updatedStorybook.storyType,
            updatedStorybook.drawingType,
            updatedStorybook.drawingStyle,
            updatedStorybook.storyTone,
            canvasSizeString,
            updatedStorybook.visualStylePrompt,
            'full-color',
            'green',
            'gemini',
            resolvedCharacterNames,
            characterHeights,
            undefined,
            undefined,
            protectedSceneAspectRatio,
            manualAIIdempotencyKey('page-scene', id)
          );
          
          sceneImageUrl = response.imageUrl;
          console.log('   Composed scene URL:', sceneImageUrl);
          
          // Track the composed scene to add to the storybook during final save
          // Don't save here to avoid race conditions - the final safeSaveWithMerge will handle it
          if (sceneImageUrl) {
            const sceneName = `${pageData.backgroundName || 'Scene'} - Page ${targetContentPageNumber}`;
            const newScene = {
              name: sceneName,
              prompt: pageData.scenePrompt,
              imageUrl: sceneImageUrl,
              thumbnailUrl: response.thumbnailUrl,
              characterNames: resolvedCharacterNames,
              backgroundName: pageData.backgroundName
            };
            
            // Add scene to local state - will be merged during final save
            updatedStorybook = {
              ...updatedStorybook,
              scenes: [...(updatedStorybook.scenes || []), newScene]
            };
            console.log('📝 Scene tracked for final save:', sceneName);
          }
          
          updateProgressStep('scene', 'completed', 'Scene composed successfully');
        } else {
          console.warn('   ⚠️ No character images found - skipping scene composition');
          updateProgressStep('scene', 'completed', 'No characters found - using background only');
          sceneImageUrl = backgroundImageUrl;
        }
      } else if (backgroundImageUrl) {
        // Use background as scene if no characters
        sceneImageUrl = backgroundImageUrl;
        console.log('   Using background as scene (no characters specified)');
        updateProgressStep('scene', 'completed', 'Using background as scene');
      } else {
        console.log('   No scene composition needed');
        updateProgressStep('scene', 'completed', 'Scene composition not needed');
      }
      
      // Resize/crop the scene to the illustration region. Safe templates use
      // the exact image-panel aspect ratio, so the renderer never stretches a
      // full-page image or places it beneath narration.
      if (sceneImageUrl) {
        const resizeTarget = deterministicPageLayout?.imageBounds || {
          x: 0,
          y: 0,
          width: canvasSize.width,
          height: canvasSize.height
        };
        updateProgressStep('layout', 'in-progress', 'Fitting illustration to the page template...');
        console.log('🔧 Resizing scene image to layout region:', resizeTarget);
        
        try {
          const resizeResponse = await aiAPI.resizeImageToCanvas(
            sceneImageUrl,
            resizeTarget.width,
            resizeTarget.height
          );
          
          if (resizeResponse.success && resizeResponse.imageUrl) {
            sceneImageUrl = resizeResponse.imageUrl;
            console.log('✅ Scene resized to fit canvas:', sceneImageUrl);
          } else {
            console.warn('⚠️ Failed to resize scene, using original');
          }
        } catch (resizeError) {
          console.warn('⚠️ Scene resize failed, using original image:', resizeError);
          // Continue with original scene image - don't throw
        }
      }
      
      // Step 5a: Apply auto-crop if enabled (BEFORE vision AI analysis)
      const cropSettings = updatedStorybook.autoCropSettings;
      let processedSceneUrl = sceneImageUrl;
      let sceneX = deterministicPageLayout?.imageBounds.x || 0;
      let sceneY = deterministicPageLayout?.imageBounds.y || 0;
      let sceneWidth = deterministicPageLayout?.imageBounds.width || canvasSize.width;
      let sceneHeight = deterministicPageLayout?.imageBounds.height || canvasSize.height;
      let sceneShape: 'rect' | 'ellipse' | undefined = deterministicPageLayout ? 'rect' : undefined;
      let sceneFeather: number | undefined = deterministicPageLayout ? 0 : undefined;
      
      console.log('🔧 Auto-crop check:', {
        hasSettings: !!cropSettings,
        enabled: cropSettings?.enabled && !deterministicPageLayout,
        hasSceneImage: !!sceneImageUrl,
        settings: cropSettings
      });
      
      if (cropSettings?.enabled && sceneImageUrl && !deterministicPageLayout) {
        console.log('✂️ Auto-crop is ENABLED, processing scene image BEFORE vision AI...');
        updateProgressStep('layout', 'in-progress', 'Processing scene image...');
        
        try {
          // Get the full image URL
          const fullSceneUrl = getImageUrl(sceneImageUrl);
          
          // Calculate available area on canvas based on padding percentages
          const paddingLeftPx = canvasSize.width * (cropSettings.paddingLeft / 100);
          const paddingRightPx = canvasSize.width * (cropSettings.paddingRight / 100);
          const paddingTopPx = canvasSize.height * (cropSettings.paddingTop / 100);
          const paddingBottomPx = canvasSize.height * (cropSettings.paddingBottom / 100);
          
          const availableWidth = canvasSize.width - paddingLeftPx - paddingRightPx;
          const availableHeight = canvasSize.height - paddingTopPx - paddingBottomPx;
          
          console.log('✂️ Canvas padding calculation:', {
            canvasSize,
            padding: { top: paddingTopPx, bottom: paddingBottomPx, left: paddingLeftPx, right: paddingRightPx },
            availableArea: { width: availableWidth, height: availableHeight }
          });
          
          // Load image to get its original dimensions
          const img = await new Promise<HTMLImageElement>((resolve, reject) => {
            const image = new Image();
            image.crossOrigin = 'anonymous';
            image.onload = () => resolve(image);
            image.onerror = () => reject(new Error('Failed to load image'));
            image.src = fullSceneUrl;
          });
          
          const originalWidth = img.width;
          const originalHeight = img.height;
          const imageAspect = originalWidth / originalHeight;
          const availableAspect = availableWidth / availableHeight;
          
          console.log('✂️ Image dimensions:', { originalWidth, originalHeight, imageAspect, availableAspect });
          
          // Use "contain" strategy: fit the entire image within the available area
          // without cropping, then position it smartly relative to the text area.
          // This preserves the full image content while still reserving space for text.
          
          // Calculate contain-fit dimensions (no cropping)
          let displayWidth: number;
          let displayHeight: number;
          
          if (imageAspect > availableAspect) {
            // Image is wider than available area — constrained by width
            displayWidth = availableWidth;
            displayHeight = Math.round(availableWidth / imageAspect);
          } else {
            // Image is taller than available area — constrained by height
            displayHeight = availableHeight;
            displayWidth = Math.round(availableHeight * imageAspect);
          }
          
          console.log('✂️ Contain-fit dimensions (no crop):', {
            displayWidth, displayHeight,
            availableWidth, availableHeight,
            widthGap: availableWidth - displayWidth,
            heightGap: availableHeight - displayHeight
          });
          
          // Apply feather effect on the original (uncropped) image
          updateProgressStep('layout', 'in-progress', 'Fitting scene to layout...');
          
          const minDim = Math.min(originalWidth, originalHeight);
          const featherPx = Math.round(minDim * (cropSettings.featherPercent / 100) / 2);
          
          if (featherPx > 0 || cropSettings.cropShape === 'ellipse') {
            const featheredCanvas = await createFeatheredCanvas(
              img,
              originalWidth,
              originalHeight,
              featherPx,
              cropSettings.cropShape,
              0
            );
            
            const blob = await new Promise<Blob>((resolve) => {
              featheredCanvas.toBlob((b) => resolve(b!), 'image/png');
            });
            
            const formData = new FormData();
            formData.append('image', blob, `scene-fitted.png`);
            formData.append('imageName', `scene-fitted-page-${targetContentPageNumber}`);
            
            const uploadUrl = `${process.env.REACT_APP_API_URL || '/api'}/storybooks/${id}/upload`;
            const resp = await fetch(uploadUrl, {
              method: 'POST',
              headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` },
              body: formData
            });
            
            if (resp.ok) {
              const data = await resp.json();
              if (data?.imageUrl) {
                processedSceneUrl = data.imageUrl;
              }
            }
          }
          
          // Smart positioning: push image toward the edge opposite the text area
          // to maximize visual impact and minimize empty gaps near text.
          const hasTopPadding = cropSettings.paddingTop > 0;
          const hasBottomPadding = cropSettings.paddingBottom > 0;
          
          // Horizontal centering within available area
          sceneX = paddingLeftPx + Math.round((availableWidth - displayWidth) / 2);
          
          // Vertical positioning: push away from text area
          if (hasBottomPadding && !hasTopPadding) {
            // Text at bottom — push image to top of available area
            sceneY = paddingTopPx;
          } else if (hasTopPadding && !hasBottomPadding) {
            // Text at top — push image to bottom of available area
            sceneY = paddingTopPx + (availableHeight - displayHeight);
          } else {
            // Text on both sides or neither — center vertically
            sceneY = paddingTopPx + Math.round((availableHeight - displayHeight) / 2);
          }
          
          sceneWidth = displayWidth;
          sceneHeight = displayHeight;
          sceneShape = cropSettings.cropShape;
          sceneFeather = cropSettings.featherPercent;
          
          console.log('✅ Processed scene for vision AI:', {
            url: processedSceneUrl,
            position: { x: sceneX, y: sceneY },
            size: { width: sceneWidth, height: sceneHeight },
            shape: sceneShape,
            feather: sceneFeather
          });
          
        } catch (cropError) {
          console.warn('⚠️ Failed to process scene:', cropError);
          // Continue with original scene image
        }
      }
      
      // Step 5b: Analyze scene with vision AI to get optimal text layout
      updateProgressStep('layout', 'in-progress');
      let layout: any;
      let detectedSubjectRegions: NormalizedRegion[] = [];
      let detectedSaliencyRegions: NormalizedRegion[] = [];

      if (pageData.textBoxes.length === 0) {
        layout = { textElements: [] };
        updateProgressStep('layout', 'completed', 'No text layout needed');
      } else if (deterministicPageLayout) {
        layout = await resolveTextLayout(
          deterministicPageLayout,
          async () => {
            throw new Error('Vision layout must not run for a protected text panel.');
          }
        );
        updateProgressStep(
          'layout',
          'completed',
          `Protected ${deterministicPageLayout.template === 'text-bottom' ? 'bottom' : 'top'} reading panel applied`
        );
      } else if (processedSceneUrl || sceneImageUrl) {
        // Vision returns normalized semantic regions only. Final geometry is
        // still solved locally with the same measured template used by PDF.
        updateProgressStep('layout', 'in-progress', 'Finding important subjects and safe regions...');
        
        const imageUrl = processedSceneUrl || sceneImageUrl;
        const imageBounds = cropSettings?.enabled ? {
          x: sceneX,
          y: sceneY,
          width: sceneWidth,
          height: sceneHeight
        } : undefined;
        
        console.log('🤖 Calling vision AI with:', {
          imageUrl,
          canvasSize,
          imageBounds,
          textBoxCount: pageData.textBoxes?.length
        });
        
        const layoutResponse = await aiAPI.analyzeSceneLayout(
          imageUrl!,
          canvasSize,
          pageData.textBoxes,
          imageBounds
        );
        
        if (!layoutResponse.success || !layoutResponse.layout) {
          throw new Error('Failed to analyze scene subjects');
        }

        const semanticAnalysis = layoutResponse.layout;
        const imageSubjectRegions: NormalizedRegion[] = Array.isArray(semanticAnalysis.subjectRegions)
          ? semanticAnalysis.subjectRegions
          : [];
        const imageSaliencyRegions: NormalizedRegion[] = Array.isArray(semanticAnalysis.saliencyRegions)
          ? semanticAnalysis.saliencyRegions
          : [];
        const analyzedImageBounds = imageBounds || {
          x: sceneX,
          y: sceneY,
          width: sceneWidth,
          height: sceneHeight
        };
        detectedSubjectRegions = mapImageRegionsToCanvas(
          imageSubjectRegions,
          analyzedImageBounds,
          canvasSize
        );
        detectedSaliencyRegions = mapImageRegionsToCanvas(
          imageSaliencyRegions,
          analyzedImageBounds,
          canvasSize
        );
        const semanticTemplates: Array<Exclude<PageLayoutTemplate, 'custom' | 'text-only' | 'title'>> = [
          'full-bleed-caption',
          'image-top-text-bottom',
          'text-top-image-bottom',
          'split',
          'dialogue'
        ];
        const importantRegionCount = detectedSubjectRegions.length + detectedSaliencyRegions.length;
        const suggestedTemplate = designStyleResolution.resolvedStyle === 'custom'
          ? (semanticTemplates.includes(semanticAnalysis.preferredTemplate)
              ? semanticAnalysis.preferredTemplate
              : 'image-top-text-bottom')
          : firstTemplate === 'full-bleed-caption' && importantRegionCount === 0
            ? 'image-top-text-bottom'
            : firstTemplate;
        deterministicPageLayout = await buildProductionPageLayout({
          canvasSize,
          textBoxes: pageData.textBoxes,
          template: suggestedTemplate,
          preferredFontSize: planningFontSize,
          minimumFontSize: MIN_READABLE_NARRATION_FONT_SIZE,
          typography: productionTypography,
          subjectRegions: [...detectedSubjectRegions, ...detectedSaliencyRegions]
        });
        if (deterministicPageLayout.requiresSplit) {
          deterministicPageLayout = await buildProductionPageLayout({
            canvasSize,
            textBoxes: pageData.textBoxes,
            template: 'image-top-text-bottom',
            preferredFontSize: planningFontSize,
            minimumFontSize: MIN_READABLE_NARRATION_FONT_SIZE,
            typography: productionTypography
          });
        }
        if (deterministicPageLayout.canonicalTemplate === 'full-bleed-caption'
          && deterministicPageLayout.warnings.some(warning => /intersects an important subject/i.test(warning))) {
          deterministicPageLayout = await buildProductionPageLayout({
            canvasSize,
            textBoxes: pageData.textBoxes,
            template: 'image-top-text-bottom',
            preferredFontSize: planningFontSize,
            minimumFontSize: MIN_READABLE_NARRATION_FONT_SIZE,
            typography: productionTypography
          });
        }
        detectedSubjectRegions = mapImageRegionsToCanvas(
          imageSubjectRegions,
          deterministicPageLayout.imageBounds,
          canvasSize
        );
        detectedSaliencyRegions = mapImageRegionsToCanvas(
          imageSaliencyRegions,
          deterministicPageLayout.imageBounds,
          canvasSize
        );
        narrationFlow.pages[0].layout = deterministicPageLayout;
        layout = await resolveTextLayout(deterministicPageLayout, async () => {
          throw new Error('Semantic analysis must never return final pixel layout.');
        });

        if (deterministicPageLayout.imageRequired) {
          const bounds = deterministicPageLayout.imageBounds;
          const alreadyMatches = sceneX === bounds.x
            && sceneY === bounds.y
            && sceneWidth === bounds.width
            && sceneHeight === bounds.height;
          if (!alreadyMatches) {
            const resizeResponse = await aiAPI.resizeImageToCanvas(
              imageUrl!,
              bounds.width,
              bounds.height
            );
            if (!resizeResponse.success || !resizeResponse.imageUrl) {
              throw new Error('The final illustration could not be fitted to the semantic-safe template.');
            }
            processedSceneUrl = resizeResponse.imageUrl;
            sceneX = bounds.x;
            sceneY = bounds.y;
            sceneWidth = bounds.width;
            sceneHeight = bounds.height;
            sceneShape = 'rect';
            sceneFeather = 0;
          }
        }
        updateProgressStep(
          'layout',
          'completed',
          `Local ${deterministicPageLayout.canonicalTemplate} template protects ${detectedSubjectRegions.length + detectedSaliencyRegions.length} important region(s)`
        );
      } else {
        deterministicPageLayout = await buildProductionPageLayout({
          canvasSize,
          textBoxes: pageData.textBoxes,
          template: 'text-only',
          preferredFontSize: planningFontSize,
          minimumFontSize: MIN_READABLE_NARRATION_FONT_SIZE,
          typography: productionTypography
        });
        narrationFlow.pages[0].layout = deterministicPageLayout;
        layout = await resolveTextLayout(deterministicPageLayout, async () => {
          throw new Error('Text-only layout does not use vision coordinates.');
        });
        updateProgressStep('layout', 'completed', 'Using the full-page text-only template');
      }
      
      // Step 6: Create the page with layout
      updateProgressStep('create-page', 'in-progress');
      
      // Preserve page ID when replacing an existing page (important for maintaining references)
      const existingPageId = targetExistingPage
          ? updatedStorybook.pages.find((page: Page) => page.id === plannedSourcePageId)?.id
          || targetExistingPage.id
        : undefined;
      
      const newPage: Page = {
        id: existingPageId || plannedSourcePageId, // Keep existing ID when replacing
        pageNumber: targetContentPageNumber,
        backgroundColor: deterministicPageLayout?.panelBackgroundColor || '#ffffff',
        imageElements: [],
        textElements: [],
        isSystemPage: false,
        role: 'content',
        layout: {
          template: deterministicPageLayout?.canonicalTemplate || firstTemplate,
          version: PAGE_LAYOUT_VERSION,
          sourcePageId: plannedSourcePageId,
          continuationIndex: 0,
          continuationCount: narrationFlow.pages.length,
          allowSubjectOverlap: false
        },
        subjectRegions: detectedSubjectRegions,
        saliencyRegions: detectedSaliencyRegions,
        // Store AI regeneration data on the page itself so it persists through reordering
        aiScenePrompt: sourcePageData.scenePrompt || undefined,
        aiCharacterNames: sourcePageData.characterNames?.length > 0 ? sourcePageData.characterNames : undefined,
        aiBackgroundName: sourcePageData.backgroundName || undefined,
        aiTextBoxes: pageData.textBoxes?.length > 0 ? pageData.textBoxes : undefined,
      };
      
      // Add composed scene or background as base image (using pre-processed values)
      if (processedSceneUrl || sceneImageUrl) {
        updateProgressStep('create-page', 'in-progress', 'Adding scene to page...');
        
        // Use the already-processed values from the auto-crop step
        const finalSceneUrl = processedSceneUrl || sceneImageUrl;
        
        console.log('📸 Adding scene to page:', {
          url: finalSceneUrl,
          position: { x: sceneX, y: sceneY },
          size: { width: sceneWidth, height: sceneHeight },
          shape: sceneShape,
          feather: sceneFeather
        });
        
        // Add the scene image element
        newPage.imageElements.push({
          id: crypto.randomUUID(),
          url: finalSceneUrl!,
          x: sceneX,
          y: sceneY,
          width: sceneWidth,
          height: sceneHeight,
          rotation: 0,
          shape: sceneShape,
          feather: sceneFeather,
          layoutRole: 'illustration',
          fit: 'cover',
          bleed: deterministicPageLayout?.canonicalTemplate === 'full-bleed-caption'
            ? 'full'
            : 'none'
        });
      }
      
      // Add text elements using either deterministic panel geometry or the
      // legacy vision suggestions for explicit full-scene/custom layouts.
      const defaultProps = generationDefaultTextProps;
      
      for (const textElement of layout.textElements || []) {
        const textBox = pageData.textBoxes[textElement.textBoxIndex];
        if (!textBox) continue;
        
        let content = textBox.content;
        // Remove character name from dialogue (e.g., "Finn: 'Hello'" -> "Hello")
        if (textBox.type === 'dialogue' && textBox.character) {
          const namePattern = new RegExp(`^${textBox.character}:\\s*["']?(.+?)["']?$`, 'i');
          const match = content.match(namePattern);
          if (match) {
            content = match[1];
          }
        }
        
        updateProgressStep('create-page', 'in-progress', `Adding text box ${textElement.textBoxIndex + 1}...`);
        
        // Enforce minimum dimensions for text boxes to ensure they're visible and editable
        const MIN_TEXT_WIDTH = 50;
        const MIN_TEXT_HEIGHT = 30;
        
        // Measure custom/full-scene text with the exact loaded production font
        // stack. The deterministic path has already measured and stacked its
        // blocks, so its planned height remains authoritative.
        const fontSize = textElement.fontSize || defaultProps.fontSize;
        const textWidth = Math.max(textElement.width || MIN_TEXT_WIDTH, MIN_TEXT_WIDTH);
        const measuredFontFamily = deterministicPageLayout
          ? textElement.fontFamily
          : resolveProductionFontFamily(defaultProps.fontFamily);
        const measuredFontWeight = deterministicPageLayout
          ? textElement.fontWeight
          : defaultProps.fontWeight;
        const measuredFontStyle = deterministicPageLayout
          ? textElement.fontStyle
          : defaultProps.fontStyle;
        const configuredDialogueStyleId = textBox.type === 'dialogue'
          ? updatedStorybook.dialogueBoxStyle?.styleId
          : undefined;
        const measurementPadding = configuredDialogueStyleId
          && configuredDialogueStyleId !== 'normal'
          ? STORY_BUBBLE_PADDING
          : STORY_TEXT_PADDING;
        const calculatedHeight = deterministicPageLayout
          ? textElement.height || MIN_TEXT_HEIGHT
          : await measureProductionTextHeight({
              content,
              width: textWidth,
              fontSize,
              fontFamily: measuredFontFamily,
              fontWeight: measuredFontWeight,
              fontStyle: measuredFontStyle,
              padding: measurementPadding
            });
        
        // Deterministic geometry has already measured and stacked every block.
        // Do not enlarge it here or later blocks can be pushed into one another.
        const finalHeight = resolvePlannedTextHeight(
          textElement.height || MIN_TEXT_HEIGHT,
          calculatedHeight,
          MIN_TEXT_HEIGHT,
          Boolean(deterministicPageLayout)
        );
        
        // Protected reading panels intentionally suppress speech-bubble tails;
        // Canvas adds 30px for a tail, which would violate the solved bounds.
        const isDialogue = textBox.type === 'dialogue' && !deterministicPageLayout;
        const dialogueStyle = isDialogue && updatedStorybook.dialogueBoxStyle ? {
          backgroundColor: updatedStorybook.dialogueBoxStyle.backgroundColor,
          backgroundOpacity: updatedStorybook.dialogueBoxStyle.backgroundOpacity,
          borderColor: updatedStorybook.dialogueBoxStyle.borderColor,
          borderWidth: updatedStorybook.dialogueBoxStyle.borderWidth,
          borderStyle: updatedStorybook.dialogueBoxStyle.borderStyle,
          textColor: updatedStorybook.dialogueBoxStyle.textColor,
          styleId: updatedStorybook.dialogueBoxStyle.styleId
        } : {};
        
        // Check if using a speech bubble style (styleId set)
        const usingSpeechBubble = dialogueStyle.styleId && dialogueStyle.styleId !== 'normal';
        
        // Determine bubble direction based on text box position
        // If text is on right side of canvas, use left-pointing bubble (tail points left toward character)
        // If text is on left side of canvas, use right-pointing bubble (tail points right toward character)
        let finalStyleId = dialogueStyle.styleId;
        if (usingSpeechBubble && isDialogue) {
          const textCenterX = textElement.x + (textWidth / 2);
          const canvasCenterX = canvasSize.width / 2;
          const isTextOnRightSide = textCenterX > canvasCenterX;
          
          // Convert base style to directional variant
          const baseStyle = dialogueStyle.styleId || 'speech-bubble';
          if (baseStyle === 'speech-bubble' || baseStyle === 'speech-bubble-right') {
            // For speech bubble: left side text gets right-pointing, right side text gets left-pointing
            finalStyleId = isTextOnRightSide ? 'speech-bubble' : 'speech-bubble-right';
          } else if (baseStyle.includes('-left') || baseStyle.includes('-right')) {
            // For other bubble types, adjust direction based on position
            const baseWithoutDirection = baseStyle.replace(/-left$/, '').replace(/-right$/, '');
            finalStyleId = isTextOnRightSide ? `${baseWithoutDirection}-left` : `${baseWithoutDirection}-right`;
          }
          
          console.log(`📍 Speech bubble direction: text at x=${textElement.x}, center=${textCenterX}, canvas center=${canvasCenterX}, isRight=${isTextOnRightSide}, styleId=${finalStyleId}`);
        }
        
        newPage.textElements.push({
          id: crypto.randomUUID(),
          content,
          x: textElement.x,
          y: textElement.y,
          fontSize: fontSize,
          fontFamily: measuredFontFamily,
          fontWeight: measuredFontWeight,
          fontStyle: measuredFontStyle,
          color: dialogueStyle.textColor || textElement.color || defaultProps.color,
          rotation: textElement.rotation || 0,
          width: textWidth,
          height: finalHeight,
          textAlign: (textElement.textAlign as 'left' | 'center' | 'right') || defaultProps.textAlign,
          verticalAlign: (textElement.verticalAlign as 'top' | 'center' | 'bottom') || defaultProps.verticalAlign,
          // When using speech bubble, let the bubble provide styling, otherwise use dialogueStyle
          backgroundColor: deterministicPageLayout
            ? textElement.backgroundColor
            : usingSpeechBubble ? '#ffffff' : (dialogueStyle.backgroundColor || textElement.backgroundColor || undefined),
          backgroundOpacity: deterministicPageLayout
            ? deterministicPageLayout.panelBackgroundOpacity
            : usingSpeechBubble ? 1 : dialogueStyle.backgroundOpacity,
          borderColor: deterministicPageLayout ? undefined : usingSpeechBubble ? '#000000' : dialogueStyle.borderColor,
          borderWidth: deterministicPageLayout ? 0 : usingSpeechBubble ? 2 : dialogueStyle.borderWidth,
          borderStyle: deterministicPageLayout ? 'none' : usingSpeechBubble ? 'solid' : dialogueStyle.borderStyle,
          styleId: deterministicPageLayout ? undefined : finalStyleId,
          layoutRole: textBox.type === 'dialogue'
            ? 'dialogue'
            : deterministicPageLayout?.canonicalTemplate === 'title'
              ? 'title'
              : deterministicPageLayout?.canonicalTemplate === 'full-bleed-caption'
                ? 'caption'
                : 'narration',
          sourceTextBoxId: textBox.sourceTextBoxId
        });
      }

      const continuationPages: Page[] = narrationFlow.pages.slice(1).map((flowPage) => {
        const continuationId = crypto.randomUUID();
        const textElements = flowPage.layout.textElements.flatMap((planned) => {
          const textBox = flowPage.blocks[planned.textBoxIndex] as any;
          if (!textBox) return [];
          let content = textBox.content;
          if (textBox.type === 'dialogue' && textBox.character) {
            const escapedName = String(textBox.character).replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
            const match = content.match(new RegExp(`^${escapedName}:\\s*["']?(.+?)["']?$`, 'i'));
            if (match) content = match[1];
          }
          return [{
            id: crypto.randomUUID(),
            content,
            x: planned.x,
            y: planned.y,
            width: planned.width,
            height: planned.height,
            fontSize: planned.fontSize,
            fontFamily: planned.fontFamily,
            fontWeight: planned.fontWeight,
            fontStyle: planned.fontStyle,
            color: planned.color,
            rotation: 0,
            textAlign: planned.textAlign,
            verticalAlign: 'top' as const,
            backgroundColor: planned.backgroundColor,
            backgroundOpacity: flowPage.layout.panelBackgroundOpacity,
            borderWidth: 0,
            borderStyle: 'none',
            layoutRole: textBox.type === 'dialogue' ? 'dialogue' as const : 'narration' as const,
            sourceTextBoxId: textBox.sourceTextBoxId
          }];
        });

        return {
          id: continuationId,
          pageNumber: targetContentPageNumber + flowPage.continuationIndex,
          backgroundColor: flowPage.layout.panelBackgroundColor,
          imageElements: [],
          textElements,
          isSystemPage: false,
          role: 'content',
          layout: {
            template: 'text-only',
            version: PAGE_LAYOUT_VERSION,
            sourcePageId: plannedSourcePageId,
            continuationIndex: flowPage.continuationIndex,
            continuationCount: flowPage.continuationCount,
            allowSubjectOverlap: false
          },
          aiScenePrompt: sourcePageData.scenePrompt || undefined,
          aiCharacterNames: sourcePageData.characterNames?.length > 0
            ? sourcePageData.characterNames
            : undefined,
          aiBackgroundName: sourcePageData.backgroundName || undefined,
          aiTextBoxes: flowPage.blocks.length > 0 ? flowPage.blocks : undefined
        };
      });
      const generatedPages = [newPage, ...continuationPages];
      
      // Debug: Log what's in the newPage before saving
      console.log('📄 Page to save:', {
        pageNumber: newPage.pageNumber,
        imageCount: newPage.imageElements.length,
        textCount: newPage.textElements.length,
        images: newPage.imageElements.map(img => ({
          id: img.id,
          url: img.url?.substring(0, 50) + '...',
          characterName: img.characterName,
          shape: img.shape,
          feather: img.feather,
          x: img.x,
          y: img.y,
          width: img.width,
          height: img.height
        }))
      });
      
      updateProgressStep('create-page', 'completed', 
        `${generatedPages.length} page(s) created with one illustration and ${generatedPages.reduce((count, page) => count + page.textElements.length, 0)} text box(es)`
      );
      
      // Step 7: Save storybook - use safeSaveWithMerge to prevent race conditions
      // This fetches fresh data from DB and only updates the specific page we generated
      updateProgressStep('save', 'in-progress');
      
      const finalStorybook = await safeSaveWithMerge((freshData: Storybook) => {
        const liveTarget = resolveGenerationTarget(freshData);
        targetPageIndex = liveTarget.pageIndex;
        shouldInsertNewPage = liveTarget.shouldInsert;
        let mergedPages: Page[];
        
        if (shouldInsertNewPage) {
          // Insert new page at target position and renumber all following pages
          updateProgressStep('save', 'in-progress', 'Inserting new page and renumbering...');
          mergedPages = [...freshData.pages];
          
          // Insert the complete narration flow atomically at the target index.
          mergedPages.splice(targetPageIndex, 0, ...generatedPages);
          
          console.log(`✨ Inserted new page at position ${targetContentPageNumber}, shifted ${mergedPages.length - targetContentPageNumber} pages down`);
        } else {
          // Either replace existing blank page or append new page
          if (targetPageIndex < freshData.pages.length) {
            // Replace the complete prior flow, not just its first page. This
            // makes regeneration idempotent when continuation counts change.
            updateProgressStep('save', 'in-progress', `Replacing blank page ${targetContentPageNumber}...`);
            mergedPages = [...freshData.pages];
            const livePage = mergedPages[targetPageIndex];
            const liveSourcePageId = livePage.layout?.continuationIndex === 0
              ? livePage.layout.sourcePageId || livePage.id
              : livePage.id;
            const flowIndexes = mergedPages
              .map((page, index) => ({ page, index }))
              .filter(({ page }) => (
                page.id === livePage.id
                || (livePage.layout?.continuationIndex === 0
                  && page.layout?.sourcePageId === liveSourcePageId)
              ))
              .map(({ index }) => index);
            const insertionIndex = Math.min(...flowIndexes);
            const flowIndexSet = new Set(flowIndexes);
            mergedPages = mergedPages.filter((_, index) => !flowIndexSet.has(index));
            mergedPages.splice(insertionIndex, 0, ...generatedPages);
            targetPageIndex = insertionIndex;
            
            console.log(`✨ Replaced blank page ${targetContentPageNumber} with generated content`);
          } else {
            // Append new page
            updateProgressStep('save', 'in-progress', 'Adding new page at end...');
            mergedPages = [...freshData.pages, ...generatedPages];
            
            console.log(`✨ Appended new page ${targetContentPageNumber} at end`);
          }
        }

        // Renumber content pages only after the complete flow is inserted, so
        // covers retain their special numbers and later story order is stable.
        let contentPageNumber = 1;
        mergedPages.forEach((page) => {
          if (!page.isSystemPage) {
            page.pageNumber = contentPageNumber;
            contentPageNumber += 1;
          }
        });
        
        // Debug: Log what's being saved
        console.log('💾 Saving merged pages - target page images:', {
          targetPageIndex,
          pageImageCount: mergedPages[targetPageIndex]?.imageElements?.length,
          pageImages: mergedPages[targetPageIndex]?.imageElements?.map((img: any) => ({
            url: img.url?.substring(0, 50) + '...',
            characterName: img.characterName,
            shape: img.shape,
            feather: img.feather
          }))
        });
        
        // Merge: use fresh data but update pages, and merge any characters/backgrounds we generated
        return {
          ...freshData,
          pages: mergedPages,
          // Merge characters - keep all fresh characters plus any new ones from this generation
          characters: mergeArraysByField(freshData.characters, updatedStorybook.characters, 'name'),
          // Merge backgrounds - keep all fresh backgrounds plus any new ones from this generation
          backgrounds: mergeArraysByField(freshData.backgrounds, updatedStorybook.backgrounds, 'name'),
          // Merge scenes - keep all fresh scenes plus any new ones from this generation
          scenes: mergeArraysByField(freshData.scenes, updatedStorybook.scenes, 'name'),
        };
      }, `page ${targetContentPageNumber}`);
      
      if (!finalStorybook) {
        throw new Error('Failed to save storybook - could not merge changes');
      }
      
      // Debug: Log what was saved
      console.log('💾 After save - page images:', {
        targetPageIndex,
        savedPageImageCount: finalStorybook.pages[targetPageIndex]?.imageElements?.length,
        savedPageImages: finalStorybook.pages[targetPageIndex]?.imageElements?.map((img: any) => ({
          url: img.url?.substring(0, 50) + '...',
          characterName: img.characterName,
          shape: img.shape,
          feather: img.feather
        }))
      });
      
      // Reload storybook to ensure all generated assets appear in galleries
      await loadStorybook();
      setCurrentPageIndex(targetPageIndex); // Navigate to the generated page
      updateProgressStep('save', 'completed');
      
      console.log('✅ Page generated successfully - all assets loaded in galleries');
      
      // Keep progress modal open so user can see completion and close it themselves
      return true;
    } catch (error: any) {
      console.error('Failed to generate page:', error);
      
      // Mark current in-progress step as error
      setProgressSteps(prev => prev.map(step => 
        step.status === 'in-progress' ? { ...step, status: 'error' as const, error: error.message } : step
      ));
      
      // Reload storybook to restore consistent state after error
      try {
        await loadStorybook();
      } catch (loadError) {
        console.error('Failed to reload storybook after generation error:', loadError);
      }
      
      // Keep progress modal open so user can see the error and close it themselves
      return false;
    } finally {
      // Clear flag to allow auto-save again (only if not in batch mode)
      // In batch mode, handleGenerateAllPages manages the flag
      if (!batchGenerationRef.current) {
        isGeneratingAIContent.current = false;
      }
    }
  };

  const handleCancelGeneration = generationDomain.requestCancellation;

  const handleGenerateAllPages = async (
    pages: any[],
    options?: { replaceExisting?: boolean }
  ): Promise<BatchGenerationSummary> => {
    if (!storybook || !id || pages.length === 0) {
      return { success: 0, failed: 0, skipped: pages.length, total: pages.length, wasCancelled: false };
    }
    
    const replaceExisting = options?.replaceExisting ?? false;
    console.log('🚀 Starting batch generation of', pages.length, 'pages', replaceExisting ? '(replacing existing)' : '');
    
    generationDomain.startBatch(pages.length);
    
    let successCount = 0;
    let failureCount = 0;
    const originalContentPages = storybook.pages.filter(page => (
      !page.isSystemPage && (page.layout?.continuationIndex ?? 0) === 0
    ));
    const stableTargetIds = pages.map((page, index) => {
      const contentPageNumber = page.pageNumber || index + 1;
      return originalContentPages[contentPageNumber - 1]?.id;
    });
    
    try {
      for (let i = 0; i < pages.length; i++) {
        // Check for cancellation before each page
        if (cancelGenerationRef.current) {
          console.log('⚠️ Batch generation cancelled by user at page', i + 1);
          break;
        }
        
        generationDomain.trackBatchPage(i + 1, pages.length);
        
        try {
          const generated = await handleGeneratePage(pages[i], {
            replaceExisting,
            targetPageId: stableTargetIds[i]
          });
          if (!generated) {
            console.error(`Page ${i + 1} was not generated; continuing with the next page`);
            failureCount++;
            continue;
          }
          successCount++;
          
          // Note: handleGeneratePage already calls loadStorybook() on success
          // No need to call it again here - just wait for DB consistency
          console.log(`💾 Page ${i + 1} saved successfully`);
          
          // Small delay between pages to avoid rate limiting and ensure DB writes complete
          if (i < pages.length - 1 && !cancelGenerationRef.current) {
            await new Promise(resolve => setTimeout(resolve, 1500));
          }
        } catch (error) {
          console.error(`Failed to generate page ${i + 1}:`, error);
          failureCount++;
          // Reload storybook to get consistent state after error
          try {
            await loadStorybook();
          } catch (loadError) {
            console.error('Failed to reload storybook after error:', loadError);
          }
          // Continue with next page unless cancelled
          if (cancelGenerationRef.current) break;
        }
      }
    } finally {
      // The summary transition below clears the shared generation fence.
    }
    
    const wasCancelled = cancelGenerationRef.current;
    
    // Calculate summary
    const failedCount = failureCount;
    const skippedCount = wasCancelled
      ? Math.max(0, pages.length - successCount - failedCount)
      : 0;
    
    // Set summary to display in modal
    const summary: BatchGenerationSummary = {
      success: successCount,
      failed: failedCount,
      skipped: skippedCount,
      total: pages.length,
      wasCancelled
    };
    generationDomain.finishBatch(summary);
    
    if (wasCancelled) {
      console.log(`✅ Batch generation stopped - generated ${successCount}/${pages.length} pages before cancellation`);
    } else {
      console.log(`✅ Batch generation complete - generated ${successCount}/${pages.length} pages`);
    }
    return summary;
  };

  const handleAddPlotToPage = (pageIndex: number, text: string, canvasWidth: number = 800, canvasHeight: number = 500) => {
    if (!storybook || !storybook.pages[pageIndex] || !text.trim()) return;
    
    // Calculate text box dimensions that fit within canvas with margins
    const margin = 40;
    const maxWidth = canvasWidth - (margin * 2);
    const maxHeight = canvasHeight - (margin * 2);
    
    // Use reasonable defaults but ensure they don't exceed canvas
    const textWidth = Math.min(maxWidth, canvasWidth * 0.85);
    const textHeight = Math.min(maxHeight, canvasHeight * 0.7);
    
    // Use default text properties if available, otherwise use defaults
    const defaultProps = storybook.defaultTextProperties || {
      fontSize: 16,
      fontFamily: 'Roboto',
      fontWeight: 'normal',
      color: '#000000',
      textAlign: 'left' as const,
      verticalAlign: 'top' as const
    };
    
    const plotTextElement: TextElement = {
      id: crypto.randomUUID(),
      content: text,
      x: margin,
      y: margin,
      fontSize: defaultProps.fontSize,
      fontFamily: defaultProps.fontFamily,
      fontWeight: defaultProps.fontWeight,
      color: defaultProps.color,
      rotation: 0,
      width: textWidth,
      height: textHeight,
      textAlign: defaultProps.textAlign,
      verticalAlign: defaultProps.verticalAlign
    };
    
    const updatedPages = [...storybook.pages];
    updatedPages[pageIndex] = {
      ...updatedPages[pageIndex],
      textElements: [...updatedPages[pageIndex].textElements, plotTextElement]
    };
    
    setStorybook({
      ...storybook,
      pages: updatedPages
    });
  };

  const addTextElement = () => {
    if (!storybook || !currentPage) return;
    
    // Use default text properties if available, otherwise use defaults
    const defaultProps = storybook.defaultTextProperties || {
      fontSize: 24,
      fontFamily: 'Roboto',
      fontWeight: 'normal',
      color: '#000000',
      textAlign: 'center' as const,
      verticalAlign: 'top' as const
    };
    
    const newText: TextElement = {
      id: crypto.randomUUID(),
      content: 'New Text',
      x: 100,
      y: 100,
      fontSize: defaultProps.fontSize,
      fontFamily: defaultProps.fontFamily,
      fontWeight: defaultProps.fontWeight,
      color: defaultProps.color,
      rotation: 0,
      width: 200,
      height: 50,
      textAlign: defaultProps.textAlign,
      verticalAlign: defaultProps.verticalAlign
    };
    
    updateCurrentPage({
      ...currentPage,
      textElements: [...currentPage.textElements, newText]
    });
    setSelectedElement(newText.id);
  };

  const addTextBoxElement = (styleId: string) => {
    if (!storybook || !currentPage) return;
    
    // Get the style
    const style = getTextBoxStyle(styleId);
    if (!style) return;
    
    // Use default text properties if available, otherwise use defaults
    const defaultProps = storybook.defaultTextProperties || {
      fontSize: 24,
      fontFamily: 'Roboto',
      fontWeight: 'normal',
      color: '#000000',
      textAlign: 'center' as const,
      verticalAlign: 'top' as const
    };
    
    // Create base text element with default font settings
    const baseText: TextElement = {
      id: crypto.randomUUID(),
      content: 'New Text',
      x: 100,
      y: 100,
      fontSize: defaultProps.fontSize,
      fontFamily: defaultProps.fontFamily,
      fontWeight: defaultProps.fontWeight,
      color: defaultProps.color,
      rotation: 0,
      width: 200,
      height: 100,
      textAlign: defaultProps.textAlign,
      verticalAlign: defaultProps.verticalAlign || 'top'
    };
    
    // Apply style to the text element
    const styledText = applyStyleToText(baseText, style) as TextElement;
    
    updateCurrentPage({
      ...currentPage,
      textElements: [...currentPage.textElements, styledText]
    });
    setSelectedElement(styledText.id);
  };

  const addImageElement = async (imageUrl: string, x: number = 150, y: number = 150, prompt?: string, characterName?: string) => {
    if (!storybook || !currentPage) return;
    try {
      const isCloudStorage = imageUrl.startsWith('https://storage.googleapis.com/');
      let width: number | undefined;
      let height: number | undefined;

      const tryLoad = async (src: string, withCrossOrigin = true) => {
        return await new Promise<HTMLImageElement>((resolve, reject) => {
          const img = new Image();
          if (withCrossOrigin) img.crossOrigin = 'anonymous';
          const timeout = window.setTimeout(() => {
            img.onload = null;
            img.onerror = null;
            reject(new Error('timeout'));
          }, 5000);
          img.onload = () => {
            clearTimeout(timeout);
            resolve(img);
          };
          img.onerror = () => {
            clearTimeout(timeout);
            reject(new Error('failed'));
          };
          img.src = src;
        });
      };

      if (isCloudStorage) {
        // Try cached signed URL first
        const cached = getSignedUrlFromCache(imageUrl);
        let img: HTMLImageElement | null = null;
        if (cached) {
          try {
            img = await tryLoad(cached, true);
          } catch (e) {
            try {
              img = await tryLoad(cached, false);
            } catch (e2) {
              img = null;
            }
          }
        }

        // If no cached or failed, request a signed URL and try
        if (!img) {
          try {
            const signedResp = await imageAPI.getSignedUrl(imageUrl);
            const signedUrl = signedResp?.url || signedResp?.signedUrl || signedResp;
            let expiresAt: number | undefined;
            if (signedResp?.expiresAt) {
              expiresAt = typeof signedResp.expiresAt === 'number' ? signedResp.expiresAt : Date.parse(signedResp.expiresAt);
            }
            if (signedUrl) setSignedUrlInCache(imageUrl, signedUrl, expiresAt);
            try {
              img = await tryLoad(signedUrl, true);
            } catch (e) {
              try {
                img = await tryLoad(signedUrl, false);
              } catch (e2) {
                img = null;
              }
            }
          } catch (err) {
            // ignore and fall through to proxy attempt
          }
        }

        // As last resort, use proxy URL
        if (!img) {
          try {
            const proxy = getImageUrl(imageUrl);
            img = await tryLoad(proxy, true).catch(() => tryLoad(proxy, false) as Promise<HTMLImageElement>);
          } catch (err) {
            img = null;
          }
        }

        if (img) {
          const maxSize = 300;
          const ar = img.naturalWidth && img.naturalHeight ? img.naturalWidth / img.naturalHeight : 1;
          if (ar > 1) {
            width = Math.min(maxSize, img.naturalWidth);
            height = Math.round(width / ar);
          } else {
            height = Math.min(maxSize, img.naturalHeight);
            width = Math.round(height * ar);
          }
        }
      } else {
        // Non-cloud images: try to load and measure
        try {
          const img = await new Promise<HTMLImageElement>((resolve, reject) => {
            const i = new Image();
            i.crossOrigin = 'anonymous';
            const t = window.setTimeout(() => reject(new Error('timeout')), 5000);
            i.onload = () => { clearTimeout(t); resolve(i); };
            i.onerror = () => { clearTimeout(t); reject(new Error('failed')); };
            i.src = getImageUrl(imageUrl);
          });
          const maxSize = 300;
          const ar = img.naturalWidth / img.naturalHeight;
          if (ar > 1) {
            width = Math.min(maxSize, img.naturalWidth);
            height = Math.round(width / ar);
          } else {
            height = Math.min(maxSize, img.naturalHeight);
            width = Math.round(height * ar);
          }
        } catch (err) {
          console.warn('Failed to measure non-cloud image', err);
        }
      }

      // Ensure we have sensible defaults if measurement failed
      if (!width || !height) {
        width = 200;
        height = 200;
      }

      const newImage: ImageElement = {
        id: crypto.randomUUID(),
        url: imageUrl,
        x: x - width / 2,
        y: y - height / 2,
        width,
        height,
        rotation: 0,
        prompt,
        characterName,
        imageHistory: [{
          action: 'created',
          timestamp: new Date().toISOString(),
          newUrl: imageUrl,
          details: characterName ? `Added ${characterName} to page` : 'Added image to page',
          resolution: { width, height }
        }]
      };

      updateCurrentPage({
        ...currentPage,
        imageElements: [...currentPage.imageElements, newImage]
      });
      setSelectedElement(newImage.id);
    } catch (error) {
      console.error('Failed to add image element:', error);
      // Fallback: add with default dimensions (maintain non-square fallback)
      const newImage: ImageElement = {
        id: crypto.randomUUID(),
        url: imageUrl,
        x: x - 150,
        y: y - 100,
        width: 300,
        height: 200,
        rotation: 0,
        prompt,
        characterName,
        imageHistory: [{
          action: 'created',
          timestamp: new Date().toISOString(),
          newUrl: imageUrl,
          details: characterName ? `Added ${characterName} to page` : 'Added image to page'
        }]
      };
      updateCurrentPage({
        ...currentPage,
        imageElements: [...currentPage.imageElements, newImage]
      });
      setSelectedElement(newImage.id);
    }
  };

  const calculateTextHeight = (
    text: string,
    fontSize: number,
    fontFamily: string,
    width: number,
    fontWeight?: string,
    padding: number = STORY_TEXT_PADDING,
    fontStyle: 'normal' | 'italic' = 'normal'
  ): number => {
    const probe = document.createElement('div');
    Object.assign(probe.style, {
      position: 'fixed',
      left: '-100000px',
      top: '0',
      visibility: 'hidden',
      pointerEvents: 'none',
      width: `${Math.max(1, width)}px`,
      height: 'auto',
      boxSizing: 'border-box',
      margin: '0',
      border: '0',
      padding: `${padding}px`,
      fontFamily: toCssFontFamily(fontFamily, text),
      fontSize: `${fontSize}px`,
      fontWeight: fontWeight || 'normal',
      fontStyle,
      direction: resolveTextDirection(text),
      unicodeBidi: 'plaintext',
      lineHeight: String(STORY_TEXT_LINE_HEIGHT),
      whiteSpace: 'pre-wrap',
      overflowWrap: 'break-word',
      wordBreak: 'break-word'
    });
    probe.textContent = text;
    document.body.appendChild(probe);

    try {
      return Math.max(
        Math.ceil(Math.max(probe.scrollHeight, probe.getBoundingClientRect().height)),
        fontSize + padding * 2
      );
    } finally {
      probe.remove();
    }
  };

  // Function to adjust all text elements font size by a delta (ALL PAGES)
  const adjustAllTextFontSizeAllPages = (delta: number) => {
    if (!storybook) return;
    
    const updatedPages = storybook.pages.map(page => {
      const updatedTextElements = page.textElements.map(el => {
        const newFontSize = Math.max(8, Math.min(120, el.fontSize + delta)); // Clamp between 8 and 120
        
        // Recalculate height based on new font size
        const hasSpeechBubble = el.styleId === 'speech-bubble' || el.styleId === 'thought-bubble';
        const padding = hasSpeechBubble ? 15 : 10;
        const calculatedHeight = calculateTextHeight(
          el.content,
          newFontSize,
          el.fontFamily,
          el.width,
          el.fontWeight,
          padding,
          el.fontStyle || 'normal'
        );
        
        return {
          ...el,
          fontSize: newFontSize,
          height: calculatedHeight
        };
      });
      
      return {
        ...page,
        textElements: updatedTextElements
      };
    });
    
    setStorybook({
      ...storybook,
      pages: updatedPages
    });
  };

  // Function to change all text elements to a specific font family (ALL PAGES)
  const changeAllTextFontFamilyAllPages = (fontFamily: string) => {
    if (!storybook) return;
    
    const updatedPages = storybook.pages.map(page => {
      const updatedTextElements = page.textElements.map(el => {
        // Recalculate height based on new font family
        const hasSpeechBubble = el.styleId === 'speech-bubble' || el.styleId === 'thought-bubble';
        const padding = hasSpeechBubble ? 15 : 10;
        const calculatedHeight = calculateTextHeight(
          el.content,
          el.fontSize,
          fontFamily,
          el.width,
          el.fontWeight,
          padding,
          el.fontStyle || 'normal'
        );
        
        return {
          ...el,
          fontFamily,
          height: calculatedHeight
        };
      });
      
      return {
        ...page,
        textElements: updatedTextElements
      };
    });
    
    // Also save as default
    const currentDefaults = storybook.defaultTextProperties;
    setStorybook({
      ...storybook,
      pages: updatedPages,
      defaultTextProperties: {
        fontSize: currentDefaults?.fontSize ?? 16,
        fontFamily: fontFamily,
        fontWeight: currentDefaults?.fontWeight ?? 'normal',
        color: currentDefaults?.color ?? '#000000',
        fontStyle: currentDefaults?.fontStyle,
        textDecoration: currentDefaults?.textDecoration,
        textAlign: currentDefaults?.textAlign,
        verticalAlign: currentDefaults?.verticalAlign
      }
    });
  };

  // Function to change all text elements font weight (ALL PAGES)
  const changeAllTextFontWeightAllPages = (fontWeight: string) => {
    if (!storybook) return;
    
    const updatedPages = storybook.pages.map(page => {
      const updatedTextElements = page.textElements.map(el => {
        // Recalculate height based on new font weight
        const hasSpeechBubble = el.styleId === 'speech-bubble' || el.styleId === 'thought-bubble';
        const padding = hasSpeechBubble ? 15 : 10;
        const calculatedHeight = calculateTextHeight(
          el.content,
          el.fontSize,
          el.fontFamily,
          el.width,
          fontWeight,
          padding,
          el.fontStyle || 'normal'
        );
        
        return {
          ...el,
          fontWeight,
          height: calculatedHeight
        };
      });
      
      return {
        ...page,
        textElements: updatedTextElements
      };
    });
    
    setStorybook({
      ...storybook,
      pages: updatedPages
    });
  };

  // Function to change all text elements text align (ALL PAGES)
  const changeAllTextAlignAllPages = (textAlign: 'left' | 'center' | 'right') => {
    if (!storybook) return;
    
    const updatedPages = storybook.pages.map(page => {
      const updatedTextElements = page.textElements.map(el => ({
        ...el,
        textAlign
      }));
      
      return {
        ...page,
        textElements: updatedTextElements
      };
    });
    
    setStorybook({
      ...storybook,
      pages: updatedPages
    });
  };

  // Function to change all text elements vertical align (ALL PAGES)
  const changeAllVerticalAlignAllPages = (verticalAlign: 'top' | 'center' | 'bottom') => {
    if (!storybook) return;
    
    const updatedPages = storybook.pages.map(page => {
      const updatedTextElements = page.textElements.map(el => ({
        ...el,
        verticalAlign
      }));
      
      return {
        ...page,
        textElements: updatedTextElements
      };
    });
    
    setStorybook({
      ...storybook,
      pages: updatedPages
    });
  };

  const updateTextElement = (id: string, updates: Partial<TextElement>) => {
    if (!currentPage) return;
    
    const element = currentPage.textElements.find(el => el.id === id);
    if (!element) return;
    
    // Auto-resize height ONLY if content, fontSize, fontFamily, or fontWeight changes
    // Do NOT auto-resize if height is explicitly provided (manual resize) or if only width changes during resize
    const isManualResize = updates.height !== undefined;
    const shouldAutoCalculateHeight = !isManualResize && (
      updates.content !== undefined || 
      updates.fontSize !== undefined || 
      updates.fontFamily !== undefined || 
      updates.fontWeight !== undefined ||
      updates.fontStyle !== undefined
    );
    
    if (shouldAutoCalculateHeight) {
      const newContent = updates.content ?? element.content;
      const newFontSize = updates.fontSize ?? element.fontSize;
      const newFontFamily = updates.fontFamily ?? element.fontFamily;
      const newWidth = updates.width ?? element.width;
      const newFontWeight = updates.fontWeight ?? element.fontWeight;
      const newFontStyle = updates.fontStyle ?? element.fontStyle ?? 'normal';
      
      // Determine padding based on style (speech bubble uses 15px, normal uses 10px)
      const hasSpeechBubble = element.styleId === 'speech-bubble' || element.styleId === 'thought-bubble';
      const padding = hasSpeechBubble ? 15 : 10;
      
      const calculatedHeight = calculateTextHeight(
        newContent,
        newFontSize,
        newFontFamily,
        newWidth,
        newFontWeight,
        padding,
        newFontStyle
      );
      updates.height = calculatedHeight;
    }
    
    // If text properties are being updated, save them as defaults
    if (updates.fontSize !== undefined || updates.fontFamily !== undefined || 
        updates.fontWeight !== undefined || updates.color !== undefined ||
        updates.fontStyle !== undefined || updates.textDecoration !== undefined ||
        updates.textAlign !== undefined || updates.verticalAlign !== undefined) {
      if (element && storybook) {
        const newDefaults = {
          fontSize: updates.fontSize ?? element.fontSize,
          fontFamily: updates.fontFamily ?? element.fontFamily,
          fontWeight: updates.fontWeight ?? element.fontWeight,
          fontStyle: updates.fontStyle ?? element.fontStyle,
          textDecoration: updates.textDecoration ?? element.textDecoration,
          color: updates.color ?? element.color,
          textAlign: updates.textAlign ?? element.textAlign ?? 'center',
          verticalAlign: updates.verticalAlign ?? element.verticalAlign ?? 'top'
        };
        
        setStorybook({
          ...storybook,
          defaultTextProperties: newDefaults
        });
      }
    }
    
    updateCurrentPage({
      ...currentPage,
      textElements: currentPage.textElements.map(el =>
        el.id === id ? { ...el, ...updates } : el
      )
    });
  };

  const updateImageElement = (id: string, updates: Partial<ImageElement>) => {
    if (!currentPage) return;
    updateCurrentPage({
      ...currentPage,
      imageElements: currentPage.imageElements.map(el =>
        el.id === id ? { ...el, ...updates } : el
      )
    });
  };

  const bringToFront = (id: string, type: 'text' | 'image') => {
    if (!currentPage) return;
    
    if (type === 'text') {
      const element = currentPage.textElements.find(el => el.id === id);
      if (!element) return;
      
      const filteredElements = currentPage.textElements.filter(el => el.id !== id);
      updateCurrentPage({
        ...currentPage,
        textElements: [...filteredElements, element]
      });
    } else {
      const element = currentPage.imageElements.find(el => el.id === id);
      if (!element) return;
      
      const filteredElements = currentPage.imageElements.filter(el => el.id !== id);
      updateCurrentPage({
        ...currentPage,
        imageElements: [...filteredElements, element]
      });
    }
  };

  const sendToBack = (id: string, type: 'text' | 'image') => {
    if (!currentPage) return;
    
    if (type === 'text') {
      const element = currentPage.textElements.find(el => el.id === id);
      if (!element) return;
      
      const filteredElements = currentPage.textElements.filter(el => el.id !== id);
      updateCurrentPage({
        ...currentPage,
        textElements: [element, ...filteredElements]
      });
    } else {
      const element = currentPage.imageElements.find(el => el.id === id);
      if (!element) return;
      
      const filteredElements = currentPage.imageElements.filter(el => el.id !== id);
      updateCurrentPage({
        ...currentPage,
        imageElements: [element, ...filteredElements]
      });
    }
  };

  const handleCopyElement = (element: TextElement | ImageElement, type: 'text' | 'image') => {
    setCopiedElement({ element, type });
  };

  const handlePasteElement = async (x: number, y: number) => {
    if (!copiedElement || !currentPage) return;
    
    const { element, type } = copiedElement;
    
    if (type === 'text') {
      const textElement = element as TextElement;
      const newText: TextElement = {
        ...textElement,
        id: crypto.randomUUID(),
        x: x - textElement.width / 2, // Center at paste position
        y: y - textElement.height / 2,
        content: textElement.content // Keep the same content
      };
      
      updateCurrentPage({
        ...currentPage,
        textElements: [...currentPage.textElements, newText]
      });
      setSelectedElement(newText.id);
    } else {
      const imageElement = element as ImageElement;
      const newImage: ImageElement = {
        ...imageElement,
        id: crypto.randomUUID(),
        x: x - imageElement.width / 2, // Center at paste position
        y: y - imageElement.height / 2
      };
      
      updateCurrentPage({
        ...currentPage,
        imageElements: [...currentPage.imageElements, newImage]
      });
      setSelectedElement(newImage.id);
    }
  };

  const deleteElement = (id: string) => {
    if (!currentPage) return;
    updateCurrentPage({
      ...currentPage,
      textElements: currentPage.textElements.filter(el => el.id !== id),
      imageElements: currentPage.imageElements.filter(el => el.id !== id)
    });
    setSelectedElement(null);
  };

  const updateCurrentPage = (updatedPage: Page) => {
    if (!currentPage || currentPage === updatedPage) return;
    recordPageHistory(currentPage);
    replaceCurrentPage(updatedPage);
  };

  const undoCurrentPage = useCallback((): boolean => {
    if (!currentPage) return false;
    const snapshot = undoPageHistory(currentPage);
    if (!snapshot) return false;
    replaceCurrentPage(snapshot);
    clearSelection();
    return true;
  }, [clearSelection, currentPage, replaceCurrentPage, undoPageHistory]);

  const redoCurrentPage = useCallback((): boolean => {
    if (!currentPage) return false;
    const snapshot = redoPageHistory(currentPage);
    if (!snapshot) return false;
    replaceCurrentPage(snapshot);
    clearSelection();
    return true;
  }, [clearSelection, currentPage, redoPageHistory, replaceCurrentPage]);

  useEffect(() => {
    const handleHistoryShortcut = (event: KeyboardEvent) => {
      if (!event.ctrlKey && !event.metaKey) return;
      const target = event.target as HTMLElement | null;
      if (target?.closest('input, textarea, select, [contenteditable="true"]')) return;
      const key = event.key.toLowerCase();
      const handled = key === 'y' || (key === 'z' && event.shiftKey)
        ? redoCurrentPage()
        : key === 'z'
          ? undoCurrentPage()
          : false;
      if (handled) event.preventDefault();
    };
    window.addEventListener('keydown', handleHistoryShortcut);
    return () => window.removeEventListener('keydown', handleHistoryShortcut);
  }, [redoCurrentPage, undoCurrentPage]);

  const updateStoryInfo = (field: 'storyType' | 'drawingType' | 'drawingStyle' | 'storyTone' | 'canvasSize' | 'backgroundColor', value: string) => {
    if (!storybook) return;

    if (field === 'backgroundColor') {
      // Update the current page's background color
      const pages = [...storybook.pages];
      const idx = currentPageIndex;
      const page = pages[idx];
      if (!page) return;
      pages[idx] = { ...page, backgroundColor: value };
      setStorybook({ ...storybook, pages });
      return;
    }

    setStorybook({
      ...storybook,
      [field]: value
    });
  };

  const handleStylePromptsSave = async (data: {
    storyStylePrompt?: string;
    visualStylePrompt?: string;
    storyType?: string;
    storyTone?: string;
    drawingMedium?: string;
    artStyle?: string;
    useReferenceBook?: boolean;
    referenceBook?: string;
    dialogueStyle?: 'prose' | 'script';
  }) => {
    if (!storybook) return;

    const updatedStorybook = {
      ...storybook,
      storyStylePrompt: data.storyStylePrompt,
      visualStylePrompt: data.visualStylePrompt,
      storyType: data.storyType || storybook.storyType,
      storyTone: data.storyTone || storybook.storyTone,
      drawingType: data.drawingMedium || storybook.drawingType,
      drawingStyle: data.artStyle || storybook.drawingStyle,
      useReferenceBook: data.useReferenceBook,
      referenceBook: data.referenceBook,
      dialogueStyle: data.dialogueStyle
    };

    setStorybook(updatedStorybook);

    // Immediately save to backend
    try {
      await storybookAPI.update(id!, updatedStorybook);
      setLastSaved(new Date());
    } catch (error) {
      console.error('Failed to save story settings:', error);
    }
  };

  const handleDesignSettingsSave = async (data: {
    autoCropSettings?: {
      enabled: boolean;
      paddingTop: number;
      paddingBottom: number;
      paddingLeft: number;
      paddingRight: number;
      featherPercent: number;
      cropShape: 'rect' | 'ellipse';
    };
    designStyle?: BookDesignStyle;
    dialogueBoxStyle?: {
      backgroundColor: string;
      backgroundOpacity: number;
      borderWidth: number;
      borderStyle: 'none' | 'solid' | 'dashed' | 'dotted' | 'double';
      borderColor: string;
      borderRadius: number;
      textColor: string;
      styleId?: string;
    };
  }) => {
    if (!storybook) return;

    const updatedStorybook = {
      ...storybook,
      autoCropSettings: data.autoCropSettings,
      designStyle: data.designStyle,
      dialogueBoxStyle: data.dialogueBoxStyle
    };

    setStorybook(updatedStorybook);

    // Immediately save to backend
    try {
      await storybookAPI.update(id!, updatedStorybook);
      setLastSaved(new Date());
    } catch (error) {
      console.error('Failed to save design settings:', error);
    }
  };

  const invalidateExportRun = () => {
    const activeController = exportRunCoordinatorRef.current.controller;
    cancelExportRun(exportRunCoordinatorRef.current);
    if (activeController && exportSavingControllerRef.current === activeController) {
      exportSavingControllerRef.current = null;
      setSaving(false);
    }
  };

  const beginExportRun = (): ExportRunContext => {
    const previousController = exportRunCoordinatorRef.current.controller;
    const run = startExportRun(exportRunCoordinatorRef.current);
    if (previousController && exportSavingControllerRef.current === previousController) {
      exportSavingControllerRef.current = null;
      setSaving(false);
    }
    return run;
  };

  const isExportRunCurrent = (run: ExportRunContext): boolean => (
    isActiveExportRun(exportRunCoordinatorRef.current, run)
  );

  const exportRunWasCancelled = (run: ExportRunContext, error?: any): boolean => (
    isExportRunCancellation(exportRunCoordinatorRef.current, run, error)
  );

  const saveStorybookForExport = async (run: ExportRunContext): Promise<Storybook> => {
    if (!storybook || !id) throw new Error('The storybook is unavailable.');
    if (hasPendingSaves()) {
      throw new Error('Pending editor changes are still saving. Wait a moment, then run export again.');
    }
    if (saving || isGeneratingAIContent.current) {
      throw new Error('Wait for the current save or generation to finish before exporting.');
    }
    exportSavingControllerRef.current = run.controller;
    setSaving(true);
    try {
      const response = await storybookAPI.update(id, {
        ...storybook,
        // Avoid the API concurrency-metadata bridge mutating live React page
        // objects if this request is aborted after the server has accepted it.
        pages: storybook.pages.map(page => ({ ...page })),
        printSettings: {
          schemaVersion: 1,
          bleedInches: pdfBleedEnabled ? 0.125 : 0,
          safeMarginInches: storybook.printSettings?.safeMarginInches ?? 0.25
        },
        plots: savedPlots.length > 0 ? savedPlots : undefined
      }, run.controller.signal);
      const persisted = response.storybook as Storybook;
      if (isExportRunCurrent(run)) {
        setStorybook(persisted);
        setLastSaved(new Date());
      }
      return persisted;
    } catch (error: any) {
      if (exportRunWasCancelled(run, error)) throw error;
      throw new Error(
        error?.response?.data?.error
          || error?.message
          || 'Your latest edits could not be saved for export.'
      );
    } finally {
      if (isExportRunCurrent(run) && exportSavingControllerRef.current === run.controller) {
        exportSavingControllerRef.current = null;
        setSaving(false);
      }
    }
  };

  const pagesForPdfExport = (
    book: Storybook,
    exportType: 'full' | 'manuscript' | 'front-cover' | 'back-cover'
  ): Page[] => {
    if (exportType === 'manuscript') {
      return book.pages.filter(page => page.role === 'content' || (!page.role && !page.isSystemPage));
    }
    if (exportType === 'front-cover') {
      return book.pages.filter((page, index) => (
        page.role === 'front-cover'
        || (!page.role && (index === 0 || /title|front/i.test(page.pageTitle || '')))
      )).slice(0, 1);
    }
    if (exportType === 'back-cover') {
      return book.pages.filter((page, index, pages) => (
        page.role === 'back-cover'
        || (!page.role && (index === pages.length - 1 || /back/i.test(page.pageTitle || '')))
      )).slice(-1);
    }
    return book.pages;
  };

  const probePreflightImageMetadata = async (sourceUrl: string): Promise<{ width: number; height: number }> => {
    const resolvedUrl = getImageUrl(sourceUrl);
    const cached = preflightImageMetadataCacheRef.current.get(resolvedUrl);
    if (cached) return cached;

    return new Promise((resolve, reject) => {
      const image = new window.Image();
      let settled = false;
      const timeoutId = window.setTimeout(() => {
        if (settled) return;
        settled = true;
        image.onload = null;
        image.onerror = null;
        reject(new Error('image metadata timed out'));
      }, 8000);
      const finish = (measurement?: { width: number; height: number }, error?: Error) => {
        if (settled) return;
        settled = true;
        window.clearTimeout(timeoutId);
        image.onload = null;
        image.onerror = null;
        if (!measurement || measurement.width <= 0 || measurement.height <= 0) {
          reject(error || new Error('image metadata is unavailable'));
          return;
        }
        preflightImageMetadataCacheRef.current.set(resolvedUrl, measurement);
        resolve(measurement);
      };
      image.onload = () => finish({ width: image.naturalWidth, height: image.naturalHeight });
      image.onerror = () => finish(undefined, new Error('image metadata could not be loaded'));
      image.src = resolvedUrl;
      if (image.complete) {
        finish({ width: image.naturalWidth, height: image.naturalHeight });
      }
    });
  };

  const runLayoutPreflight = async (
    book: Storybook = storybook!,
    showResults = true
  ): Promise<PreflightIssue[]> => {
    if (!book) return [];
    setIsRunningPreflight(true);
    try {
      const size = CANVAS_SIZES.find(entry => entry.value === (book.canvasSize || '5.5x8.5'))
        || CANVAS_SIZES[3];
      const issues = await preflightBook(book.pages, {
        canvasSize: { width: size.width, height: size.height },
        printSettings: {
          ...(book.printSettings || {
            schemaVersion: 1,
            safeMarginInches: 0.25
          }),
          schemaVersion: 1,
          bleedInches: pdfBleedEnabled ? 0.125 : 0,
          safeMarginInches: book.printSettings?.safeMarginInches ?? 0.25
        },
        minimumFontSize: MIN_READABLE_NARRATION_FONT_SIZE,
        probeImageMetadata: ({ imageElement }) => probePreflightImageMetadata(imageElement.url),
        measureTextOverflow: async ({ textElement }) => {
          if (!String(textElement.content || '').trim()) return false;
          const padding = textElement.styleId?.includes('bubble')
            ? STORY_BUBBLE_PADDING
            : STORY_TEXT_PADDING;
          const measuredHeight = await measureProductionTextHeight({
            content: textElement.content,
            width: textElement.width,
            fontSize: textElement.fontSize,
            fontFamily: textElement.fontFamily,
            fontWeight: textElement.fontWeight,
            fontStyle: textElement.fontStyle,
            padding
          });
          return measuredHeight > textElement.height + 0.5;
        }
      });
      setPreflightIssues(issues);
      setPreflightPageHashes(new Map(book.pages.map(page => [page.id, getPageContentHash(page)])));
      if (showResults) setShowPreflightModal(true);
      return issues;
    } finally {
      setIsRunningPreflight(false);
    }
  };

  const handleCloseExportProof = () => {
    invalidateExportRun();
    setShowExportProofModal(false);
    setExportPreflightReport(null);
    setExportProofArtifact(null);
    setExportDiagnosticsUrl(null);
    setExportLayoutOverrideReceipt(null);
    setExportProofError(null);
    setExportProofPhase('checking');
  };

  const handleOpenPdfExport = () => {
    if (!storybook || isRunningPreflight) return;
    invalidateExportRun();
    setExportPreflightReport(null);
    setExportProofArtifact(null);
    setExportDiagnosticsUrl(null);
    setExportLayoutOverrideReceipt(null);
    setExportProofError(null);
    setShowPdfExportModal(true);
  };

  const generateAuthoritativeExportProof = async (
    report: ExportPreflightReport,
    existingRun?: ExportRunContext,
    layoutOverride?: ExportLayoutOverrideAcknowledgement
  ) => {
    if (!id) return;
    const run = existingRun || beginExportRun();
    if (!isExportRunCurrent(run)) return;
    setExportProofPhase('generating');
    setExportProofArtifact(null);
    setExportDiagnosticsUrl(null);
    setExportLayoutOverrideReceipt(null);
    setExportProofError(null);
    try {
      const response = await storybookAPI.generateExportProof(
        id,
        report,
        report.geometry.bleedInches === 0.125,
        run.controller.signal,
        layoutOverride
      );
      if (!isExportRunCurrent(run)) return;
      const proofReport = (response?.report || report) as ExportPreflightReport;
      if (proofReport.reportId !== report.reportId
        || proofReport.inputHash !== report.inputHash
        || proofReport.bookRevision !== report.bookRevision) {
        throw new Error('The export server returned a proof for a different saved snapshot.');
      }
      const overrideReceipt = verifiedLayoutOverrideReceipt(response?.layoutOverride, proofReport);
      if (layoutOverride && !overrideReceipt) {
        throw new Error('The export server did not confirm the acknowledged layout issue set.');
      }
      if (!layoutOverride && response?.layoutOverride) {
        throw new Error('The export server returned an unexpected layout override receipt.');
      }
      const proof = validateServerExportProofDescriptor(response?.proof);
      const verifiedProof = await fetchVerifiedExportProof(
        proof,
        run.controller.signal
      );
      if (!isExportRunCurrent(run)) return;
      setExportPreflightReport(proofReport);
      setExportProofArtifact({
        kind: 'blob',
        blob: new Blob([verifiedProof.bytes], { type: verifiedProof.contentType }),
        reportId: proofReport.reportId,
        inputHash: proofReport.inputHash,
        bookRevision: proofReport.bookRevision,
        filename: proof.filename || 'storybook-proof.pdf',
        mimeType: 'application/pdf',
        byteLength: verifiedProof.sizeBytes,
        sha256: verifiedProof.sha256
      });
      setExportDiagnosticsUrl(proof.diagnosticsUrl || null);
      setExportLayoutOverrideReceipt(overrideReceipt);
      setExportProofPhase('ready');
    } catch (error: any) {
      if (exportRunWasCancelled(run, error)) return;
      const currentReport = error?.exportReport as ExportPreflightReport | undefined;
      if (currentReport) setExportPreflightReport(currentReport);
      const message = error?.response?.data?.error
        || error?.message
        || 'The PDF proof could not be generated.';
      setExportProofError(message);
      setExportProofPhase(exportProofFailurePhase(
        currentReport?.summary.status,
        error?.response?.data?.code || error?.code
      ));
    }
  };

  const runAuthoritativeExportPreflight = async () => {
    if (!storybook || !id) return;
    const run = beginExportRun();
    setShowPdfExportModal(false);
    setShowExportProofModal(true);
    setExportProofPhase('checking');
    setExportPreflightReport(null);
    setExportProofArtifact(null);
    setExportDiagnosticsUrl(null);
    setExportLayoutOverrideReceipt(null);
    setExportProofError(null);
    try {
      // Export authorization always follows the completed save request. The
      // server then reloads and hashes its own persisted copy of the book.
      await saveStorybookForExport(run);
      if (!isExportRunCurrent(run)) return;
      const response = await storybookAPI.runExportPreflight(
        id,
        pdfExportType,
        pdfBleedEnabled,
        run.controller.signal
      );
      if (!isExportRunCurrent(run)) return;
      const report = response.report;
      setExportPreflightReport(report);
      if (!response.success || report.summary.status === 'blocked') {
        setExportProofError(response.error || null);
        setExportProofPhase('blocked');
        return;
      }
      await generateAuthoritativeExportProof(report, run);
    } catch (error: any) {
      if (exportRunWasCancelled(run, error)) return;
      const report = error?.response?.data?.report as ExportPreflightReport | undefined;
      if (report) setExportPreflightReport(report);
      setExportProofError(
        error?.response?.data?.error || error?.message || 'Export preflight could not be completed.'
      );
      setExportProofPhase(report?.summary.status === 'blocked' ? 'blocked' : 'error');
    }
  };

  const handleGoToExportIssue = (issue: ExportPreflightIssue) => {
    invalidateExportRun();
    if (issue.pageId && storybook) {
      const pageIndex = storybook.pages.findIndex(page => page.id === issue.pageId);
      if (pageIndex >= 0) setCurrentPageIndex(pageIndex);
    } else if (issue.pageIndex !== undefined && storybook?.pages[issue.pageIndex]) {
      setCurrentPageIndex(issue.pageIndex);
    }
    setSelectedElement(issue.elementId || null);
    setShowExportProofModal(false);
    setExportPreflightReport(null);
    setExportProofArtifact(null);
    setExportDiagnosticsUrl(null);
    setExportLayoutOverrideReceipt(null);
    setExportProofError(null);
  };

  const handleRunLayoutCheck = async () => {
    if (!storybook || isRunningPreflight) return;
    setFocusedPreflightPageId(null);
    setPreflightContinueTarget('pdf-options');
    await runLayoutPreflight(storybook, true);
  };

  const handleOpenPagePreflight = (pageId: string) => {
    setFocusedPreflightPageId(pageId);
    setPreflightContinueTarget('pdf-options');
    setShowPreflightModal(true);
  };

  const handleGoToPreflightIssue = (issue: PreflightIssue) => {
    if (!storybook) return;
    const pageIndex = storybook.pages.findIndex(page => page.id === issue.pageId);
    if (pageIndex >= 0) setCurrentPageIndex(pageIndex);
    setSelectedElement(issue.elementId || null);
    setShowPreflightModal(false);
  };

  const handleApplyPreflightFix = async (issue: PreflightIssue) => {
    if (!issue.autoFix?.patch || issue.autoFix.requiresReflow || fixingPreflightIssueKey) return;
    if (saving || isGeneratingAIContent.current) {
      alert('Please wait for the current save or generation to finish before applying a layout fix.');
      return;
    }
    const issueKey = preflightIssueKey(issue);
    const checkedPageHash = preflightPageHashes.get(issue.pageId);
    setFixingPreflightIssueKey(issueKey);
    try {
      // Persist local edits before fetching the fresh merge base so the fix
      // cannot replace work that was visible during this Layout Check.
      await saveStorybook();
      const saved = await safeSaveWithMerge((freshData: Storybook) => {
        const freshPage = freshData.pages.find(page => page.id === issue.pageId);
        if (!freshPage || !checkedPageHash || getPageContentHash(freshPage) !== checkedPageHash) {
          throw new Error('This page changed after Layout Check. Run the check again before applying the fix.');
        }
        return {
          ...freshData,
          pages: freshData.pages.map(page => (
            page.id === issue.pageId
              ? applyPreflightAutoFix(page, issue.autoFix!)
              : page
          ))
        };
      }, `preflight fix for ${issue.code}`);
      if (!saved) throw new Error('The layout fix could not be saved.');

      const pageIndex = saved.pages.findIndex(page => page.id === issue.pageId);
      if (pageIndex >= 0) setCurrentPageIndex(pageIndex);
      setSelectedElement(issue.elementId || null);
      setFocusedPreflightPageId(issue.pageId);
      await runLayoutPreflight(saved, false);
    } catch (error: any) {
      console.error('Failed to apply preflight fix:', error);
      alert(error?.message || 'Failed to apply the layout fix.');
    } finally {
      setFixingPreflightIssueKey(null);
    }
  };

  const handleContinueAfterPreflight = () => {
    const warningSignature = preflightWarningSignature(currentPreflightIssues);
    acknowledgedPreflightWarningsRef.current = warningSignature || null;
    setShowPreflightModal(false);
    if (preflightContinueTarget === 'pdf-progress') {
      void runAuthoritativeExportPreflight();
    } else {
      setShowPdfExportModal(true);
    }
  };

  const persistQuickCreateState = async (
    nextState: QuickCreateState,
    nextOutline?: StructuredPlotData,
    reviewPreflight?: { expectedRevision: number; expectedInputHash: string }
  ): Promise<Storybook> => {
    if (!id) throw new Error('Storybook ID is unavailable');
    const response = await storybookAPI.updateQuickCreate(id, {
      quickCreate: nextState,
      ...(nextOutline ? { structuredPlotData: nextOutline } : {}),
      ...(reviewPreflight ? { reviewPreflight } : {})
    });
    const savedBook = response.storybook as Storybook;
    setStorybook(savedBook);
    if (savedBook.structuredPlotData) setStructuredPlotData(savedBook.structuredPlotData);
    if (savedBook.plots) setSavedPlots(savedBook.plots);
    setLastSaved(new Date());
    return savedBook;
  };

  const quickCreateState = storybook?.quickCreate;

  const handleQuickCreateFailure = (error: any) => {
    const message = error?.response?.data?.error || error?.message || 'Quick Create could not continue.';
    console.error('Quick Create failed:', error);
    setQuickCreateError(message);
  };

  const handleQuickSelectStage = async (stage: QuickCreateStage) => {
    if (!quickCreateState || stage === quickCreateState.stage || quickCreateBusyAction) return;
    setQuickCreateError(null);
    try {
      await persistQuickCreateState({
        ...quickCreateState,
        stage,
        updatedAt: new Date().toISOString()
      });
    } catch (error) {
      handleQuickCreateFailure(error);
    }
  };

  const handleQuickApproveBrief = async () => {
    if (!quickCreateState) return;
    setQuickCreateBusyAction('Saving brief…');
    setQuickCreateError(null);
    try {
      await persistQuickCreateState(approveQuickCreateStage(quickCreateState, 'brief', 'outline'));
    } catch (error) {
      handleQuickCreateFailure(error);
    } finally {
      setQuickCreateBusyAction(null);
    }
  };

  const handleQuickGenerateOutline = async () => {
    if (!storybook || !quickCreateState) return;
    setQuickCreateBusyAction('Writing page-by-page outline…');
    setQuickCreateError(null);
    try {
      const response = await aiAPI.generateQuickCreateOutline({
        prompt: quickCreateState.brief.premise,
        numPages: quickCreateState.brief.pageCount,
        readingAge: quickCreateState.brief.readingAge,
        storyType: quickCreateState.brief.storyType,
        storyTone: quickCreateState.brief.storyTone,
        storyStylePrompt: storybook.storyStylePrompt,
        visualStylePrompt: storybook.visualStylePrompt,
        dialogueStyle: storybook.dialogueStyle,
        drawingType: storybook.drawingType,
        drawingStyle: storybook.drawingStyle
      });
      if (!response?.success || !response.plotData?.pages) {
        throw new Error('The outline service returned no usable page plan.');
      }
      if (response.plotData.pages.length !== quickCreateState.brief.pageCount) {
        throw new Error(
          `The outline returned ${response.plotData.pages.length} pages instead of ${quickCreateState.brief.pageCount}. Please generate it again.`
        );
      }
      const outline = withStableOutlinePageIds(response.plotData as StructuredPlotData);
      const estimate = estimateQuickCreate(
        quickCreateState,
        outline,
        storybook.characters,
        storybook.backgrounds
      );
      const now = new Date().toISOString();
      const existingComposition = resolveQuickCreateComposition(quickCreateState.composition);
      const outlinePageIds = new Set(outline.pages.map(page => page.outlinePageId).filter(Boolean));
      const composition: QuickCreateComposition = {
        ...existingComposition,
        pageOverrides: Object.fromEntries(Object.entries(existingComposition.pageOverrides)
          .filter(([pageId]) => outlinePageIds.has(pageId)))
      };
      const storyboard = buildQuickCreateStoryboard(outline, composition);
      const nextState: QuickCreateState = {
        ...quickCreateState,
        stage: 'outline',
        stageRevisions: {
          ...quickCreateState.stageRevisions,
          outline: quickCreateState.stageRevisions.outline || 1
        },
        outline: {
          generatedAt: now,
          pageCount: outline.pages.length,
          characterCount: outline.characters.length,
          backgroundCount: outline.backgrounds.length
        },
        composition: {
          ...composition,
          storyboardHash: storyboard.storyboardHash
        },
        draft: {
          status: 'not-started',
          completedPages: 0,
          failedPages: 0,
          totalPages: outline.pages.length
        },
        estimate: {
          tokens: estimate.tokens,
          seconds: estimate.seconds,
          basis: 'outline',
          calculatedAt: now
        },
        updatedAt: now
      };
      await persistQuickCreateState(nextState, outline);
    } catch (error) {
      handleQuickCreateFailure(error);
    } finally {
      setQuickCreateBusyAction(null);
    }
  };

  const handleQuickApproveOutline = async () => {
    if (!quickCreateState || !storybook?.structuredPlotData) return;
    setQuickCreateBusyAction('Saving outline approval…');
    setQuickCreateError(null);
    try {
      const ready = {
        ...quickCreateState,
        stageRevisions: {
          ...quickCreateState.stageRevisions,
          outline: quickCreateState.stageRevisions.outline || 1,
          'cast-style': quickCreateState.stageRevisions['cast-style'] || 1
        }
      };
      await persistQuickCreateState(approveQuickCreateStage(ready, 'outline', 'cast-style'));
    } catch (error) {
      handleQuickCreateFailure(error);
    } finally {
      setQuickCreateBusyAction(null);
    }
  };

  const handleQuickPrepareIdentity = async () => {
    const initialBook = storybookRef.current;
    if (!id || !initialBook) return;
    setQuickCreateBusyAction('Building approved identity kits…');
    setQuickCreateError(null);
    try {
      let current = (await storybookAPI.initializeIdentity(id)).storybook as Storybook;
      const approvedArt = current.artBible?.versions.find(version => (
        version.id === current.artBible?.approvedVersionId && version.status === 'approved'
      ));
      if (!approvedArt) {
        const artResponse = await storybookAPI.createArtBibleVersion(id, {
          medium: current.drawingType || 'Digital children’s book illustration',
          palette: ['warm amber', 'soft cream', 'gentle sage', 'muted sky blue', 'deep cocoa accents'],
          lineStyle: current.drawingStyle || 'Soft, clean storybook linework with rounded forms',
          lighting: 'Warm, diffuse daylight with soft shadows and readable faces',
          texture: current.visualStylePrompt || 'Subtle paper texture with simple, text-safe backgrounds',
          negativeStyle: ['photorealism', 'visual clutter', 'embedded text', 'harsh contrast', 'identity drift']
        });
        const approved = await storybookAPI.approveArtBibleVersion(id, artResponse.version.id);
        current = approved.storybook as Storybook;
      }

      const outlineNames = new Set((current.structuredPlotData?.characters || [])
        .map(character => character.name.trim().toLocaleLowerCase()));
      const cast = current.characters.filter(character => (
        outlineNames.size === 0 || outlineNames.has(character.name.trim().toLocaleLowerCase())
      ));
      for (const originalCharacter of cast) {
        const characterId = originalCharacter.characterId;
        if (!characterId) throw new Error(`${originalCharacter.name} does not have a permanent character ID.`);
        let character = current.characters.find(candidate => candidate.characterId === characterId) || originalCharacter;
        let bible = character.bibleVersions?.find(version => (
          version.id === character.approvedBibleVersionId && version.status === 'approved'
        ));
        if (!bible) {
          const identityText = character.prompt || `${character.name}, a children’s storybook character`;
          const species = `${character.name} ${identityText}`.match(
            /\b(fox|rabbit|bunny|bear|dog|puppy|cat|kitten|mouse|owl|deer|bird|human|child|boy|girl)\b/i
          )?.[1]?.toLocaleLowerCase() || 'storybook character';
          const bibleResponse = await storybookAPI.createCharacterBibleVersion(id, characterId, {
            species,
            appearanceTraits: [identityText],
            relativeHeight: character.heightCategory || 'medium',
            clothing: [],
            accessories: [],
            forbiddenChanges: [
              'Do not change species, face shape, markings, body colors, relative height, clothing, or accessories.'
            ]
          });
          const approved = await storybookAPI.approveCharacterBibleVersion(
            id,
            characterId,
            bibleResponse.version.id
          );
          current = approved.storybook as Storybook;
          character = current.characters.find(candidate => candidate.characterId === characterId) || character;
          bible = character.bibleVersions?.find(version => version.id === character.approvedBibleVersionId);
        }
        if (!bible) throw new Error(`Could not approve ${character.name}’s Character Bible.`);

        const approvedReferences = character.referenceVersions?.find(version => (
          version.id === character.approvedReferenceVersionId
          && version.status === 'approved'
          && version.bibleVersionId === bible!.id
        ));
        if (approvedReferences) continue;
        const pendingReferences = character.referenceVersions?.find(version => (
          version.status === 'draft'
          && version.bibleVersionId === bible!.id
          && version.references.length === 4
        ));
        if (pendingReferences) continue;

        const views = [
          { purpose: 'front' as const, direction: 'full-body front view, neutral pose and expression' },
          { purpose: 'three-quarter' as const, direction: 'full-body three-quarter view, neutral pose' },
          { purpose: 'side' as const, direction: 'full-body side profile view, neutral pose' },
          { purpose: 'expression' as const, direction: 'three-quarter view with a warm, clearly readable story expression' }
        ];
        const generatedReferences: Array<{
          assetId: string;
          imageUrl: string;
          thumbnailUrl?: string;
          label: string;
          purpose: typeof views[number]['purpose'];
        }> = [];
        let anchorImage: string | undefined;
        for (const view of views) {
          const response = await aiAPI.generateCharacter(
            [
              `${character.name}. ${character.prompt}`,
              `Create one isolated ${view.direction}.`,
              'Match the supplied anchor exactly when present. Plain transparent background. No props, scenery, border, labels, or text.'
            ].join(' '),
            current.storyType,
            current.drawingType,
            current.drawingStyle,
            current.storyTone,
            anchorImage ? [anchorImage] : [],
            current.visualStylePrompt,
            'gemini',
            'transparent',
            'white',
            [],
            `identity:${id}:${characterId}:${bible.id}:${view.purpose}`
          );
          if (!response?.success || !response.imageUrl || !response.assetId) {
            throw new Error(`Could not generate the ${view.purpose} view for ${character.name}.`);
          }
          anchorImage = anchorImage || response.imageUrl;
          generatedReferences.push({
            assetId: response.assetId,
            imageUrl: response.imageUrl,
            ...(response.thumbnailUrl ? { thumbnailUrl: response.thumbnailUrl } : {}),
            label: `${character.name} ${view.purpose} view`,
            purpose: view.purpose
          });
        }
        const referenceResponse = await storybookAPI.createCharacterReferenceVersion(id, characterId, {
          bibleVersionId: bible.id,
          backgroundMode: 'transparent',
          promptVersion: 'character-reference-v1',
          references: generatedReferences
        });
        current = referenceResponse.storybook as Storybook;
      }

      const refreshed = await storybookAPI.getById(id);
      current = refreshed.storybook as Storybook;
      setStorybook(current);
      if (current.structuredPlotData) setStructuredPlotData(current.structuredPlotData);
      setLastSaved(new Date());
    } catch (error) {
      handleQuickCreateFailure(error);
    } finally {
      setQuickCreateBusyAction(null);
    }
  };

  const handleQuickApproveIdentity = async () => {
    if (!id || !storybookRef.current) return;
    setQuickCreateBusyAction('Approving the shown identity kits…');
    setQuickCreateError(null);
    try {
      let current = storybookRef.current;
      const outlineNames = new Set((current.structuredPlotData?.characters || [])
        .map(character => character.name.trim().toLocaleLowerCase()));
      for (const character of current.characters.filter(candidate => (
        outlineNames.size === 0 || outlineNames.has(candidate.name.trim().toLocaleLowerCase())
      ))) {
        if (!character.characterId) throw new Error(`${character.name} has no permanent identity.`);
        if (character.approvedReferenceVersionId) continue;
        const pending = [...(character.referenceVersions || [])]
          .reverse()
          .find(version => version.status === 'draft' && version.references.length === 4);
        if (!pending) throw new Error(`${character.name} does not have four generated views to approve.`);
        const response = await storybookAPI.approveCharacterReferenceVersion(
          id,
          character.characterId,
          pending.id
        );
        current = response.storybook as Storybook;
      }
      const refreshed = await storybookAPI.getById(id);
      setStorybook(refreshed.storybook as Storybook);
      setLastSaved(new Date());
    } catch (error) {
      handleQuickCreateFailure(error);
    } finally {
      setQuickCreateBusyAction(null);
    }
  };

  const handleQuickUpdateComposition = async (composition: QuickCreateComposition) => {
    if (!quickCreateState || quickCreateBusyAction) return;
    setQuickCreateBusyAction('Saving page storyboard…');
    setQuickCreateError(null);
    try {
      const now = new Date().toISOString();
      const nextState = quickCreateState.approvals['cast-style']?.approved
        ? reviseQuickCreateStage(quickCreateState, 'cast-style', now)
        : { ...quickCreateState, updatedAt: now };
      await persistQuickCreateState({
        ...nextState,
        composition,
        updatedAt: now
      });
    } catch (error) {
      handleQuickCreateFailure(error);
    } finally {
      setQuickCreateBusyAction(null);
    }
  };

  const handleQuickApproveCastStyle = async () => {
    if (!quickCreateState) return;
    setQuickCreateBusyAction('Locking this style revision…');
    setQuickCreateError(null);
    try {
      const ready = {
        ...quickCreateState,
        stageRevisions: {
          ...quickCreateState.stageRevisions,
          'cast-style': quickCreateState.stageRevisions['cast-style'] || 1,
          draft: quickCreateState.stageRevisions.draft || 1
        }
      };
      await persistQuickCreateState(approveQuickCreateStage(ready, 'cast-style', 'draft'));
    } catch (error) {
      handleQuickCreateFailure(error);
    } finally {
      setQuickCreateBusyAction(null);
    }
  };

  const saveQuickCreateReferenceDirection = async (input: {
    referenceBook: string;
    storyStylePrompt: string;
    visualStylePrompt: string;
    seriesTemplateId: 'tiny-little-fox-sequel-v1' | null;
    canvasSize?: string;
    readingAge?: '3-5' | '6-8' | '9-12';
  }) => {
    const latestBook = storybookRef.current;
    if (!latestBook || !id) throw new Error('Storybook ID is unavailable');

    const updatedBook = {
      ...latestBook,
      useReferenceBook: Boolean(input.referenceBook),
      referenceBook: input.referenceBook,
      storyStylePrompt: input.storyStylePrompt,
      visualStylePrompt: input.visualStylePrompt,
      seriesTemplateId: input.seriesTemplateId,
      ...(input.canvasSize ? { canvasSize: input.canvasSize } : {})
    };
    const savedResponse = await storybookAPI.update(id, updatedBook);
    const savedBook = savedResponse.storybook as Storybook;
    const savedWorkflow = savedBook.quickCreate;
    if (!savedWorkflow) {
      setStorybook(savedBook);
      return;
    }

    const changesBrief = Boolean(
      (input.canvasSize && input.canvasSize !== savedWorkflow.brief.canvasSize)
      || (input.readingAge && input.readingAge !== savedWorkflow.brief.readingAge)
    );
    const workflowWithDirection = changesBrief ? {
      ...savedWorkflow,
      brief: {
        ...savedWorkflow.brief,
        ...(input.canvasSize ? { canvasSize: input.canvasSize } : {}),
        ...(input.readingAge ? { readingAge: input.readingAge } : {})
      }
    } : savedWorkflow;
    const outlinePageCount = savedBook.structuredPlotData?.pages.length
      || workflowWithDirection.outline?.pageCount
      || workflowWithDirection.brief.pageCount;
    const hasOutline = Boolean(savedBook.structuredPlotData?.pages.length || workflowWithDirection.outline);
    const nextState = changesBrief
      ? (() => {
        const revised = reviseQuickCreateStage(workflowWithDirection, 'brief');
        return {
          ...revised,
          outline: undefined,
          draft: {
            status: 'not-started' as const,
            completedPages: 0,
            failedPages: 0,
            totalPages: revised.brief.pageCount
          }
        };
      })()
      : reviseQuickCreateReferenceDirection(workflowWithDirection, outlinePageCount);
    const workflowResponse = await storybookAPI.updateQuickCreate(id, {
      quickCreate: nextState,
      ...(hasOutline ? { clearStructuredPlotData: true } : {})
    });
    setStorybook(workflowResponse.storybook as Storybook);
    if (hasOutline) setStructuredPlotData(null);
    setGenerationJob(null);
    setLastSaved(new Date());
  };

  const handleQuickAnalyzeReferenceBook = async (bookName: string) => {
    setQuickCreateBusyAction('Analyzing reference book…');
    setQuickCreateError(null);
    try {
      const response = await aiAPI.generateBookPrompts(bookName);
      if (!response?.success || !response.data?.storyStylePrompt || !response.data?.visualStylePrompt) {
        throw new Error('The reference analysis returned no usable style direction.');
      }
      await saveQuickCreateReferenceDirection({
        referenceBook: bookName,
        storyStylePrompt: response.data.storyStylePrompt,
        visualStylePrompt: response.data.visualStylePrompt,
        seriesTemplateId: null
      });
    } catch (error) {
      handleQuickCreateFailure(error);
    } finally {
      setQuickCreateBusyAction(null);
    }
  };

  const handleQuickUseSeriesTemplate = async (templateId: 'tiny-little-fox-sequel-v1') => {
    setQuickCreateBusyAction('Applying measured sequel template…');
    setQuickCreateError(null);
    try {
      const direction = createSeriesStyleDirection(templateId);
      await saveQuickCreateReferenceDirection({ ...direction, seriesTemplateId: templateId });
    } catch (error) {
      handleQuickCreateFailure(error);
    } finally {
      setQuickCreateBusyAction(null);
    }
  };

  const handleQuickClearReferenceBook = async () => {
    setQuickCreateBusyAction('Clearing reference book…');
    setQuickCreateError(null);
    try {
      await saveQuickCreateReferenceDirection({
        referenceBook: '',
        storyStylePrompt: '',
        visualStylePrompt: '',
        seriesTemplateId: null
      });
    } catch (error) {
      handleQuickCreateFailure(error);
    } finally {
      setQuickCreateBusyAction(null);
    }
  };

  const handleQuickGenerateDraft = async () => {
    const latestBook = storybookRef.current;
    const state = latestBook?.quickCreate;
    const outline = latestBook?.structuredPlotData;
    if (!latestBook || !state || !outline || !id) return;

    const hasKnownBalance = user?.totalTokens !== undefined
      || user?.monthlyCredits !== undefined
      || user?.paidTokens !== undefined;
    const availableTokens = user?.totalTokens
      ?? ((user?.monthlyCredits || 0) + (user?.paidTokens || 0));
    if (hasKnownBalance && state.estimate && availableTokens < state.estimate.tokens) {
      setQuickCreateError(`This draft is estimated at ${state.estimate.tokens} tokens, but ${availableTokens} are available.`);
      return;
    }

    const retryJobId = shouldRetryQuickCreateDraftJob(state, generationJob)
      ? generationJob?.id
      : undefined;
    setQuickCreateBusyAction(retryJobId ? 'Queueing unfinished pages…' : 'Queueing the complete draft…');
    setQuickCreateError(null);
    try {
      const response = retryJobId
        ? await generationJobAPI.retry(id, retryJobId)
        : await generationJobAPI.startDraft(
          id,
          `quick-draft-${id}-${state.stageRevisions.outline || 1}-${state.stageRevisions['cast-style'] || 1}`
        );
      setGenerationJob(response.job);
      setGenerationConnection('polling');
      const refreshed = await storybookAPI.getById(id);
      setStorybook(refreshed.storybook);
    } catch (error) {
      handleQuickCreateFailure(error);
    } finally {
      setQuickCreateBusyAction(null);
    }
  };

  const runGenerationJobAction = async (
    action: GenerationJobAction,
    pageIds?: string[]
  ) => {
    if (!id || !generationJob) return;
    setGenerationJobAction(action);
    setQuickCreateError(null);
    try {
      const response = action === 'pause'
        ? await generationJobAPI.pause(id, generationJob.id)
        : action === 'resume'
          ? await generationJobAPI.resume(id, generationJob.id)
          : action === 'cancel'
            ? await generationJobAPI.cancel(id, generationJob.id)
            : await generationJobAPI.retry(id, generationJob.id, pageIds);
      setGenerationJob(response.job);
      setGenerationConnection('polling');
      const refreshed = await storybookAPI.getById(id);
      setStorybook(refreshed.storybook);
    } catch (error) {
      handleQuickCreateFailure(error);
    } finally {
      setGenerationJobAction(null);
    }
  };

  const openGeneratedPage = (pageId: string) => {
    const index = storybookRef.current?.pages.findIndex(page => page.id === pageId) ?? -1;
    if (index >= 0) {
      setCurrentPageIndex(index);
      closeQuickCreate();
    }
  };

  const refreshBookReview = useCallback(async () => {
    if (!id) return null;
    setBookReviewLoading(true);
    setBookReviewError(null);
    try {
      const [reviewResponse, bookResponse] = await Promise.all([
        bookReviewAPI.get(id),
        storybookAPI.getById(id)
      ]);
      setBookReview(reviewResponse.review);
      setStorybook(bookResponse.storybook);
      if (bookResponse.storybook.structuredPlotData) setStructuredPlotData(bookResponse.storybook.structuredPlotData);
      if (bookResponse.storybook.plots) setSavedPlots(bookResponse.storybook.plots);
      return reviewResponse.review;
    } catch (error: any) {
      const message = error?.response?.data?.error || error?.message || 'The book review could not be loaded.';
      setBookReviewError(message);
      throw new Error(message);
    } finally {
      setBookReviewLoading(false);
    }
  }, [id]);

  const openBookReview = async () => {
    setBookReview(null);
    setShowBookReview(true);
    try {
      await refreshBookReview();
    } catch {
      // The modal keeps the actionable error visible and offers a retry.
    }
  };

  const handleReviewRegenerate = async (pageId: string, layer: ReviewLayer) => {
    if (!id) return;
    const response = await bookReviewAPI.regenerateLayer(id, pageId, layer);
    setGenerationJob(response.job);
    setGenerationConnection('polling');
    await refreshBookReview();
  };

  const handleReviewLayerLock = async (pageId: string, layer: ReviewLayer, locked: boolean) => {
    if (!id) return;
    await bookReviewAPI.setLayerLock(id, pageId, layer, locked);
    await refreshBookReview();
  };

  const handleFixAllSafeIssues = async (issues: ReviewSafeIssue[]) => {
    if (!id || !bookReview) return;
    const pageIds = [...new Set(issues.map(issue => issue.pageId))];
    const response = await bookReviewAPI.fixSafe(id, pageIds, bookReview.revision);
    if (response.review) setBookReview(response.review);
    const refreshed = await storybookAPI.getById(id);
    setStorybook(refreshed.storybook);
  };

  const handleLoadReviewHistory = async (target: ReviewHistoryTarget): Promise<ReviewRevision[]> => {
    if (!id) return [];
    const response = await bookReviewAPI.listRevisions(id, target);
    return (Array.isArray(response.revisions) ? response.revisions : []) as ReviewRevision[];
  };

  const handleRestoreReviewRevision = async (_target: ReviewHistoryTarget, revisionId: string) => {
    if (!id || !bookReview) return;
    await bookReviewAPI.restoreRevision(id, revisionId, bookReview.revision);
    await refreshBookReview();
  };

  const handleQuickRunReview = async () => {
    const latestBook = storybookRef.current;
    if (!latestBook) return;
    setQuickCreateBusyAction('Checking every page…');
    setQuickCreateError(null);
    try {
      await saveStorybook();
      const refreshed = await storybookAPI.getById(id!);
      const savedBook = refreshed.storybook as Storybook;
      setStorybook(savedBook);
      await runLayoutPreflight(savedBook, false);
      const response = await storybookAPI.runExportPreflight(id!, 'full', false);
      setQuickCreateExportPreflight(response.report);
    } catch (error) {
      handleQuickCreateFailure(error);
    } finally {
      setQuickCreateBusyAction(null);
    }
  };

  const handleQuickApproveReview = async () => {
    if (!quickCreateState || currentPreflightSummary.hasErrors) return;
    if (currentPreflightCheckedPageIds.size !== (storybook?.pages.length || 0)) {
      setQuickCreateError('Run the book layout check again before approving this revision.');
      return;
    }
    if (!quickCreateExportPreflight
      || quickCreateExportPreflight.summary.status !== 'ready'
      || quickCreateExportPreflight.bookRevision !== storybook?.revision) {
      setQuickCreateError('Run the authoritative book and export check again before approving this revision.');
      return;
    }
    setQuickCreateBusyAction('Saving review approval…');
    setQuickCreateError(null);
    try {
      const ready = {
        ...quickCreateState,
        stageRevisions: {
          ...quickCreateState.stageRevisions,
          review: quickCreateState.stageRevisions.review || 1
        }
      };
      await persistQuickCreateState(
        approveQuickCreateStage(ready, 'review', 'export'),
        undefined,
        {
          expectedRevision: quickCreateExportPreflight.bookRevision,
          expectedInputHash: quickCreateExportPreflight.inputHash
        }
      );
    } catch (error) {
      handleQuickCreateFailure(error);
    } finally {
      setQuickCreateBusyAction(null);
    }
  };

  const markQuickCreatePdfComplete = async () => {
    const state = storybookRef.current?.quickCreate;
    if (!state || state.stage !== 'export' || !state.approvals.review?.approved || state.status === 'completed') return;
    await persistQuickCreateState({
      ...state,
      status: 'completed',
      updatedAt: new Date().toISOString()
    });
  };

  const closeQuickCreate = () => {
    setShowQuickCreate(false);
    const next = new URLSearchParams(searchParams);
    next.delete('quickCreate');
    setSearchParams(next, { replace: true });
  };

  const orderedFlowPagesFor = (book: Storybook, selectedPage: Page): Page[] => {
    const sourcePageId = selectedPage.layout?.sourcePageId || selectedPage.id;
    const flowPages = book.pages.filter(page => (
      !page.isSystemPage
      && (page.layout?.sourcePageId || page.id) === sourcePageId
    ));
    return (flowPages.length > 0 ? flowPages : [selectedPage]).sort((first, second) => (
      (first.layout?.continuationIndex ?? 0) - (second.layout?.continuationIndex ?? 0)
    ));
  };

  const editableTextBoxesForFlow = (flowPages: Page[]) => {
    const grouped = new Map<string, any>();
    for (const text of flowPages.flatMap(page => page.textElements)) {
      const sourceTextBoxId = text.sourceTextBoxId || text.id;
      const existing = grouped.get(sourceTextBoxId);
      if (existing) {
        existing.content += text.content;
      } else {
        grouped.set(sourceTextBoxId, {
          type: text.layoutRole === 'dialogue' ? 'dialogue' : 'narration',
          content: text.content,
          fontFamily: text.fontFamily,
          fontWeight: text.fontWeight,
          fontStyle: text.fontStyle || 'normal' as const,
          lineHeight: text.lineHeight,
          sourceTextBoxId,
          originalTextElementId: text.id
        });
      }
    }
    return Array.from(grouped.values());
  };

  const handleApplySafeLayoutToCurrentPage = async (
    requestedTemplate?: Exclude<PageLayoutTemplate, 'custom'>
  ) => {
    if (!storybook || !currentPage || !id || currentPage.isSystemPage) return;

    const canvasSizeString = storybook.canvasSize || '5.5x8.5';
    const pageSize = CANVAS_SIZES.find(size => size.value === canvasSizeString) || CANVAS_SIZES[3];
    const existingFlowPages = orderedFlowPagesFor(storybook, currentPage);
    const layoutSourcePage = existingFlowPages[0];
    const flowTextElements = existingFlowPages.flatMap(page => page.textElements);

    if (flowTextElements.length === 0) {
      alert('Add narration before applying a protected text layout.');
      return;
    }

    if (layoutSourcePage.imageElements.length > 1) {
      alert('The one-click layout fix supports a text-only page or one full-scene illustration. Use a deterministic template before combining multiple image layers.');
      return;
    }

    const sourceImage = requestedTemplate === 'text-only'
      ? undefined
      : layoutSourcePage.imageElements[0];
    if (requestedTemplate && requestedTemplate !== 'text-only' && !sourceImage) {
      alert('This template needs one illustration. Add an image or choose Text Only.');
      return;
    }
    const isTemplateManagedImage = Boolean(
      sourceImage
      && layoutSourcePage.layout?.version === PAGE_LAYOUT_VERSION
      && layoutSourcePage.layout.template !== 'custom'
      && sourceImage.layoutRole === 'illustration'
      && sourceImage.fit === 'cover'
    );
    if (requestedTemplate === 'full-bleed-caption'
      && (layoutSourcePage.subjectRegions?.length || 0) + (layoutSourcePage.saliencyRegions?.length || 0) === 0) {
      alert('Full-bleed captions need subject analysis first. Regenerate this page with the Full-bleed Caption design so important faces and objects are protected.');
      return;
    }
    if (sourceImage && !isFullSceneIllustration(sourceImage, pageSize) && !isTemplateManagedImage) {
      alert('This image does not cover the page like a full-scene illustration. Use Design Settings or reposition it manually to avoid enlarging a character or sticker.');
      return;
    }

    const hasDesignedText = flowTextElements.some(text => (
      Boolean(text.styleId) || (text.borderWidth || 0) > 0
    ));
    if (hasDesignedText) {
      alert('This page contains designed dialogue or styled text. The automatic narration fix is disabled so those styles are not removed.');
      return;
    }

    const textBoxes = editableTextBoxesForFlow(existingFlowPages);
    const preferredFontSize = Math.max(
      flowTextElements[0]?.fontSize
        || storybook.defaultTextProperties?.fontSize
        || MIN_READABLE_NARRATION_FONT_SIZE,
      MIN_READABLE_NARRATION_FONT_SIZE
    );
    const flowSourcePageId = layoutSourcePage.layout?.sourcePageId || layoutSourcePage.id;
    setIsApplyingSafeLayout(true);
    try {
      const firstTemplate = requestedTemplate
        || (sourceImage ? 'image-top-text-bottom' as const : 'text-only' as const);
      const measurementAdapter = createAuthoritativeMeasurementAdapter({
        authoritative: true,
        available: true,
        bundledFonts: true,
        engine: 'browser-dom-production-fonts',
        fontAssetVersion: BUNDLED_FONT_ASSET_VERSION,
        fontIdentity: `${BUNDLED_FONT_ASSET_VERSION}:browser-dom`,
        verifyFonts: async () => ({ available: true }),
        measureText: async request => {
          const measuredTypography = {
            ...createStoryTextTypography(
              request.fontFamily,
              request.fontWeight,
              request.fontStyle as 'normal' | 'italic',
              0
            ),
            lineHeight: request.lineHeight,
            padding: 0
          };
          const measure = await createProductionTextBlockMeasurer([{
            content: request.content,
            fontSize: request.fontSize,
            typography: measuredTypography
          }]);
          const result = measure({
            content: request.content,
            width: request.width,
            fontSize: request.fontSize,
            typography: measuredTypography
          });
          return { height: result.height, lineCount: result.lineCount };
        }
      });
      const requestedStrategy = firstTemplate === 'text-only'
        ? 'illustrated-text-page'
        : firstTemplate === 'full-bleed-caption'
          ? 'quiet-zone'
          : firstTemplate === 'split'
            ? 'split-vignette'
            : 'protected-panel';
      const requestedTreatment = requestedStrategy === 'quiet-zone'
        ? 'integrated'
        : requestedStrategy === 'illustrated-text-page'
          ? 'solid-paper'
          : 'paper-fade';
      const exactReferences = (layoutSourcePage.characterReferenceBindings || []).flatMap(binding => (
        binding.referenceAssetIds.map((assetId, index) => ({
          id: `${binding.characterId}:${binding.referenceVersionId}:${index + 1}`,
          assetId,
          version: binding.referenceVersionId,
          sha256: binding.referenceHashes[index],
          kind: 'character',
          role: 'identity'
        }))
      ));
      const productionFlow = await planProductionPageFlow({
        templateId: storybook.seriesTemplateId || undefined,
        canvasSize: { width: pageSize.width, height: pageSize.height },
        readingAge: storybook.quickCreate?.brief.readingAge || '6-8',
        designStyle: storybook.designStyle || 'text-bottom',
        textDensity: storybook.quickCreate?.composition?.textDensity || 'balanced',
        compositionPreference: requestedStrategy === 'quiet-zone'
          ? 'integrated-quiet-zones'
          : requestedStrategy === 'illustrated-text-page'
            ? 'illustrated-text-pages'
            : 'separate-reading-panels',
        treatmentPreference: requestedTreatment === 'solid-paper' ? 'solid-paper' : 'paper-fade',
        referenceBindings: exactReferences,
        pages: [{
          id: flowSourcePageId,
          textBoxes,
          hasIllustration: Boolean(sourceImage),
          imageUrl: sourceImage?.url,
          imageHash: layoutSourcePage.layout?.composition?.schema === 'tiny-fox/page-composition@2'
            ? layoutSourcePage.layout.composition.imageHash
            : undefined,
          placementAnalysis: layoutSourcePage.layout?.composition?.schema === 'tiny-fox/page-composition@2'
            ? layoutSourcePage.layout.composition.placementAnalysis
            : undefined,
          altText: sourceImage?.altText,
          compositionOverride: { strategy: requestedStrategy, treatment: requestedTreatment }
        }]
      }, measurementAdapter);
      if (productionFlow.status !== 'ready' || !productionFlow.providerCallsAllowed) {
        throw new Error(
          productionFlow.reviewReasons[0]?.message
          || 'Pinned production fonts were unavailable. The page was left unchanged for review.'
        );
      }
      const flow: any = {
        pages: productionFlow.pages.map(flowPage => {
          const composition = flowPage.composition;
          const canonicalTemplate = composition.strategy === 'illustrated-text-page'
            ? 'text-only'
            : composition.strategy === 'quiet-zone' || composition.strategy === 'image-only'
              ? 'full-bleed-caption'
              : composition.textBounds.y < composition.imageBounds.y
                ? 'text-top-image-bottom'
                : 'image-top-text-bottom';
          const panel = composition.panelElements[0];
          return {
            pageId: flowPage.id,
            sourcePageId: flowPage.sourcePageId,
            continuationIndex: flowPage.continuationIndex,
            continuationCount: flowPage.continuationCount,
            blocks: flowPage.blocks,
            productionComposition: composition,
            layout: {
              template: canonicalTemplate,
              canonicalTemplate,
              imageBounds: composition.imageBounds,
              panelBounds: panel || composition.textBounds,
              safeBounds: composition.textBounds,
              textElements: composition.textElements.map((element: any, index: number) => ({
                ...element,
                textBoxIndex: index
              })),
              panelBackgroundColor: panel?.backgroundColor || composition.backgroundColor,
              panelBackgroundOpacity: panel ? (panel.opacity ?? 1) : 0,
              fontSize: composition.textElements[0]?.fontSize || preferredFontSize,
              requiresSplit: false,
              allowsImageTextOverlap: composition.strategy === 'quiet-zone',
              imageRequired: composition.imageRequired,
              fitStrategy: 'preferred',
              warnings: composition.warnings || []
            }
          };
        })
      };
      const layout = flow.pages[0].layout;
      let resizedImageUrl = sourceImage?.url;
      const preservesAnalyzedPixels = Boolean(
        sourceImage
        && ['quiet-zone', 'image-only'].includes(flow.pages[0].productionComposition.strategy)
        && layout.imageBounds.x === 0
        && layout.imageBounds.y === 0
        && layout.imageBounds.width === pageSize.width
        && layout.imageBounds.height === pageSize.height
        && isFullSceneIllustration(sourceImage, pageSize)
      );
      if (sourceImage && !preservesAnalyzedPixels) {
        const resizeResponse = await aiAPI.resizeImageToCanvas(
          sourceImage.url,
          layout.imageBounds.width,
          layout.imageBounds.height
        );
        if (!resizeResponse.success || !resizeResponse.imageUrl) {
          throw new Error('The illustration could not be fitted to the protected image region.');
        }
        resizedImageUrl = resizeResponse.imageUrl;
      }
      const primaryComposition = {
        ...flow.pages[0].productionComposition,
        imageElements: (flow.pages[0].productionComposition.imageElements || []).map((image: any) => ({
          ...image,
          ...(resizedImageUrl ? { url: resizedImageUrl } : {})
        })),
        ...(!preservesAnalyzedPixels && sourceImage ? {
          imageHash: '',
          analyzerVersion: undefined,
          analysisHash: undefined,
          placementAnalysis: undefined
        } : {})
      };

      const buildFlowTextElements = (flowPage: typeof flow.pages[number]): TextElement[] => (
        flowPage.layout.textElements.flatMap((planned: any) => {
          const block = flowPage.blocks[planned.textBoxIndex];
          if (!block) return [];
          const original = flowTextElements.find(
            text => text.id === block.originalTextElementId
          ) || flowTextElements[planned.textBoxIndex];
          if (!original) return [];
          return [{
            ...original,
            id: flowPage.continuationIndex === 0 ? original.id : crypto.randomUUID(),
            content: block.content,
            x: planned.x,
            y: planned.y,
            width: planned.width,
            height: planned.height,
            fontSize: planned.fontSize,
            fontFamily: planned.fontFamily,
            fontWeight: planned.fontWeight,
            fontStyle: planned.fontStyle,
            lineHeight: planned.lineHeight,
            padding: planned.padding,
            color: planned.color,
            rotation: 0,
            textAlign: planned.textAlign,
            verticalAlign: 'top' as const,
            backgroundColor: planned.backgroundColor,
            backgroundOpacity: flowPage.layout.panelBackgroundOpacity,
            borderColor: undefined,
            borderWidth: 0,
            borderStyle: 'none',
            styleId: undefined,
            layoutRole: 'narration' as const,
            sourceTextBoxId: block.sourceTextBoxId
          }];
        })
      );

      const updatedPage: Page = {
        ...layoutSourcePage,
        backgroundColor: layout.panelBackgroundColor,
        role: 'content',
        layout: {
          template: layout.canonicalTemplate,
          version: PAGE_LAYOUT_VERSION,
          sourcePageId: flowSourcePageId,
          continuationIndex: 0,
          continuationCount: flow.pages.length,
          allowSubjectOverlap: false,
          ...(storybook.seriesTemplateId ? { seriesTemplateId: storybook.seriesTemplateId } : {}),
          composition: primaryComposition
        },
        imageElements: sourceImage ? [{
          ...sourceImage,
          url: resizedImageUrl!,
          ...(!preservesAnalyzedPixels ? {
            contentSha256: undefined,
            contentByteLength: undefined,
            contentMimeType: undefined
          } : {}),
          x: layout.imageBounds.x,
          y: layout.imageBounds.y,
          width: layout.imageBounds.width,
          height: layout.imageBounds.height,
          rotation: 0,
          shape: 'rect',
          feather: 0,
          imageHistory: [
            ...(sourceImage.imageHistory || []),
            {
              action: 'cropped',
              timestamp: new Date().toISOString(),
              previousUrl: sourceImage.url,
              newUrl: resizedImageUrl!,
              details: 'Fitted to protected text-panel layout'
            }
          ],
          layoutRole: 'illustration',
          fit: 'cover',
          bleed: layout.canonicalTemplate === 'full-bleed-caption' ? 'full' : 'none'
        }] : [],
        panelElements: primaryComposition.panelElements || [],
        textElements: buildFlowTextElements(flow.pages[0]),
        aiTextBoxes: flow.pages[0].blocks
      };

      const continuationPages: Page[] = flow.pages.slice(1).map((flowPage: any) => ({
        id: flowPage.pageId,
        pageNumber: layoutSourcePage.pageNumber + flowPage.continuationIndex,
        backgroundColor: flowPage.layout.panelBackgroundColor,
        imageElements: [],
        panelElements: flowPage.productionComposition.panelElements || [],
        textElements: buildFlowTextElements(flowPage),
        isSystemPage: false,
        role: 'content',
        layout: {
          template: 'text-only',
          version: PAGE_LAYOUT_VERSION,
          sourcePageId: flowSourcePageId,
          continuationIndex: flowPage.continuationIndex,
          continuationCount: flowPage.continuationCount,
          allowSubjectOverlap: false,
          ...(storybook.seriesTemplateId ? { seriesTemplateId: storybook.seriesTemplateId } : {}),
          composition: flowPage.productionComposition
        },
        aiScenePrompt: layoutSourcePage.aiScenePrompt,
        aiCharacterNames: layoutSourcePage.aiCharacterNames,
        aiBackgroundName: layoutSourcePage.aiBackgroundName,
        aiTextBoxes: flowPage.blocks
      }));
      const replacementPages = [updatedPage, ...continuationPages];

      const saved = await safeSaveWithMerge((freshData: Storybook) => {
        const currentIndex = freshData.pages.findIndex(page => page.id === layoutSourcePage.id);
        if (currentIndex < 0) return freshData;
        const flowIndexes = freshData.pages
          .map((page, index) => ({ page, index }))
          .filter(({ page }) => (
            page.id === layoutSourcePage.id
            || page.layout?.sourcePageId === flowSourcePageId
          ))
          .map(({ index }) => index);
        const insertionIndex = Math.min(...flowIndexes);
        const flowIndexSet = new Set(flowIndexes);
        const pages = freshData.pages.filter((_, index) => !flowIndexSet.has(index));
        pages.splice(insertionIndex, 0, ...replacementPages);
        let contentPageNumber = 1;
        pages.forEach(page => {
          if (!page.isSystemPage) {
            page.pageNumber = contentPageNumber;
            contentPageNumber += 1;
          }
        });
        return { ...freshData, pages };
      }, `protected text layout for page ${layoutSourcePage.pageNumber}`);

      if (!saved) {
        throw new Error('The protected layout could not be saved.');
      }

      setLastSaved(new Date());
      console.log(`✅ Protected text layout applied as ${replacementPages.length} page(s) starting at page ${layoutSourcePage.pageNumber}`);
    } catch (error: any) {
      console.error('Failed to apply protected text layout:', error);
      alert(error.message || 'Failed to apply the protected text layout.');
    } finally {
      setIsApplyingSafeLayout(false);
    }
  };

  const handleFontSettingsSave = async () => {
    if (!storybook) return;

    const fontSettings = {
      fontSize: fontSettingsFontSize,
      fontFamily: fontSettingsFontFamily,
      fontWeight: fontSettingsFontWeight,
      fontStyle: fontSettingsFontStyle,
      textDecoration: fontSettingsTextDecoration,
      color: fontSettingsColor,
      textAlign: fontSettingsTextAlign,
      verticalAlign: fontSettingsVerticalAlign
    };

    const updatedStorybook = {
      ...storybook,
      defaultTextProperties: fontSettings
    };

    setStorybook(updatedStorybook);

    // Immediately save to backend
    try {
      await storybookAPI.update(id!, updatedStorybook);
      setLastSaved(new Date());
      setShowFontModal(false);
    } catch (error) {
      console.error('Failed to save font settings:', error);
    }
  };

  const handleTitleClick = () => {
    if (!storybook) return;
    setEditedTitle(storybook.title);
    setIsEditingTitle(true);
  };

  const handleTitleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setEditedTitle(e.target.value);
  };

  const handleTitleBlur = () => {
    if (!storybook) return;
    const trimmedTitle = editedTitle.trim();
    if (trimmedTitle && trimmedTitle !== storybook.title) {
      setStorybook({
        ...storybook,
        title: trimmedTitle
      });
    }
    setIsEditingTitle(false);
  };

  const handleTitleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      e.currentTarget.blur();
    } else if (e.key === 'Escape') {
      setIsEditingTitle(false);
    }
  };

  const handleCropImage = () => {
    if (!selectedElement) return;
    const imageElement = currentPage?.imageElements.find(el => el.id === selectedElement);
    if (imageElement) {
      setCropImageId(selectedElement);
      setShowCropModal(true);
    }
  };

  const handleCropComplete = (croppedDataUrl: string, metadata: { name: string; shape: string; feather: number }) => {
    if (!cropImageId) return;
    
    // Find the image element to determine its type
    const imageElement = currentPage?.imageElements.find(el => el.id === cropImageId);
    if (!imageElement) return;

    // Determine image type based on characterName or check in storybook collections
    type ImageType = 'asset' | 'character' | 'scene' | 'background';
    let imageType: ImageType = 'asset'; // default

    const character = imageElement.characterName
      ? storybook?.characters.find(c => c.name === imageElement.characterName)
      : undefined;

    // Try to detect scene/background by explicit name (if available) then by imageUrl fallback
    const sceneName = (imageElement as any).sceneName as string | undefined;
    const backgroundName = (imageElement as any).backgroundName as string | undefined;

    const scene = sceneName
      ? storybook?.scenes.find(s => s.name === sceneName)
      : storybook?.scenes.find(s => s.imageUrl === imageElement.url);
    const background = backgroundName
      ? storybook?.backgrounds.find(b => b.name === backgroundName)
      : storybook?.backgrounds.find(b => b.imageUrl === imageElement.url);

    if (character) {
      imageType = 'character';
    } else if (scene) {
      imageType = 'scene';
    } else if (background) {
      imageType = 'background';
    }

    const name = metadata.name;

    const finalizeWithDataUrl = () => {
      // Fallback: update element with data URL
      updateImageElement(cropImageId, { 
        url: croppedDataUrl,
        imageHistory: [
          ...(imageElement.imageHistory || []),
          {
            action: 'cropped' as const,
            timestamp: new Date().toISOString(),
            previousUrl: imageElement.url,
            newUrl: croppedDataUrl,
            details: `Cropped (${metadata.shape}${metadata.feather > 0 ? `, feather ${metadata.feather}` : ''})`
          }
        ]
      });
      setShowCropModal(false);
      setCropImageId(null);
    };

    if (!name) {
      // User cancelled naming — just use data URL inline
      finalizeWithDataUrl();
      return;
    }

    // Convert data URL to Blob
    const dataURLtoBlob = (dataurl: string) => {
      const arr = dataurl.split(',');
      const mimeMatch = arr[0].match(/:(.*?);/);
      const mime = mimeMatch ? mimeMatch[1] : 'image/png';
      const bstr = atob(arr[1]);
      let n = bstr.length;
      const u8arr = new Uint8Array(n);
      while (n--) {
        u8arr[n] = bstr.charCodeAt(n);
      }
      return new Blob([u8arr], { type: mime });
    };

    (async () => {
      try {
        const blob = dataURLtoBlob(croppedDataUrl);
        const filename = `${name.replace(/[^a-z0-9_-]/gi, '_')}.png`;
        const formData = new FormData();
        formData.append('image', new File([blob], filename, { type: blob.type }));
        formData.append('imageType', imageType);
        formData.append('imageName', name);
        if (imageElement.characterName) {
          formData.append('characterName', imageElement.characterName);
        }

        // Upload to server
        const uploadUrl = `${process.env.REACT_APP_API_URL}/storybooks/${id}/upload`;
        const resp = await fetch(uploadUrl, {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('token')}`
          },
          body: formData
        });

        if (!resp.ok) {
          console.error('Upload failed', resp.statusText);
          finalizeWithDataUrl();
          return;
        }

        const data = await resp.json();
        if (!data || !data.imageUrl) {
          console.error('Upload response missing imageUrl', data);
          finalizeWithDataUrl();
          return;
        }

        const newImageUrl: string = data.imageUrl;

        const prompt = `Cropped ${imageType} (${metadata.shape}${metadata.feather > 0 ? ` with feather ${metadata.feather}` : ''})`;

        // Load the final uploaded image to get its actual dimensions
        const img = new Image();
        img.crossOrigin = 'anonymous';
        
        await new Promise<void>((resolve, reject) => {
          img.onload = () => resolve();
          img.onerror = () => {
            console.warn('Failed to load final image, using data URL dimensions');
            img.src = croppedDataUrl;
            img.onload = () => resolve();
          };
          img.src = newImageUrl;
        });

        // Use the actual image dimensions without constraining them unnecessarily
        const actualWidth = img.naturalWidth || img.width;
        const actualHeight = img.naturalHeight || img.height;
        const aspectRatio = actualWidth / actualHeight;
        
        console.log(`🎨 Cropped image loaded: ${actualWidth}x${actualHeight}, aspect ratio: ${aspectRatio.toFixed(3)}`);
        
        // Keep a reasonable max display size but maintain aspect ratio
        // Scale down if too large, but preserve the exact aspect ratio
        const maxDisplaySize = 400;
        let displayWidth = actualWidth;
        let displayHeight = actualHeight;
        
        if (displayWidth > maxDisplaySize || displayHeight > maxDisplaySize) {
          if (aspectRatio > 1) {
            // Wider than tall
            displayWidth = maxDisplaySize;
            displayHeight = maxDisplaySize / aspectRatio;
          } else {
            // Taller than wide
            displayHeight = maxDisplaySize;
            displayWidth = maxDisplaySize * aspectRatio;
          }
        }
        
        console.log(`📏 Display dimensions: ${displayWidth.toFixed(1)}x${displayHeight.toFixed(1)}`);

        // Force canvas redraw by clearing cache
        const cacheBuster = `?t=${Date.now()}`;
        const finalUrl = newImageUrl.includes('?') ? newImageUrl : newImageUrl + cacheBuster;

        console.log(`✅ Updating canvas image ${cropImageId} to ${displayWidth.toFixed(1)}x${displayHeight.toFixed(1)}`);
        console.log(`🔄 Old URL: ${imageElement.url}`);
        console.log(`🔄 New URL: ${finalUrl}`);

        // Update both canvas and gallery in a single state update to avoid race conditions
        if (storybook && id) {
          // First update the canvas element in the current page
          const updatedPages = [...storybook.pages];
          
          // Log the original image element
          const originalEl = updatedPages[currentPageIndex].imageElements.find(el => el.id === cropImageId);
          console.log(`📍 Found image element to update:`, originalEl?.id, originalEl?.url);
          
          updatedPages[currentPageIndex] = {
            ...updatedPages[currentPageIndex],
            imageElements: updatedPages[currentPageIndex].imageElements.map(el =>
              el.id === cropImageId ? { 
                ...el, 
                url: finalUrl,
                width: displayWidth,
                height: displayHeight,
                prompt,
                characterName: imageType === 'character' ? name : el.characterName,
                imageHistory: [
                  ...(el.imageHistory || []),
                  {
                    action: 'cropped' as const,
                    timestamp: new Date().toISOString(),
                    previousUrl: el.url,
                    newUrl: finalUrl,
                    details: `Cropped (${metadata.shape}${metadata.feather > 0 ? `, feather ${metadata.feather}` : ''})`,
                    resolution: { width: Math.round(displayWidth), height: Math.round(displayHeight) }
                  }
                ]
              } : el
            )
          };

          // Then add to the appropriate gallery
          // Use the image URL as thumbnail (cropped images don't have separate thumbnails)
          let updatedGallery: any = {};
          if (imageType === 'character') {
            await storybookAPI.addCharacter(id, name, prompt, newImageUrl, newImageUrl);
            updatedGallery = {
              characters: [
                ...(storybook.characters || []),
                { name, prompt, imageUrl: newImageUrl, thumbnailUrl: newImageUrl }
              ]
            };
          } else if (imageType === 'scene') {
            await storybookAPI.addScene(id, name, prompt, newImageUrl, newImageUrl);
            const prevScene = storybook.scenes?.find(s => s.imageUrl === imageElement.url);
            updatedGallery = {
              scenes: [
                ...(storybook.scenes || []),
                {
                  name,
                  prompt,
                  imageUrl: newImageUrl,
                  thumbnailUrl: newImageUrl,
                  characterNames: prevScene?.characterNames || [],
                  backgroundName: prevScene?.backgroundName
                }
              ]
            };
          } else if (imageType === 'background') {
            await storybookAPI.addBackground(id, name, prompt, newImageUrl, newImageUrl);
            updatedGallery = {
              backgrounds: [
                ...(storybook.backgrounds || []),
                { name, prompt, imageUrl: newImageUrl, thumbnailUrl: newImageUrl }
              ]
            };
          } else {
            await storybookAPI.addAsset(id, name, prompt, newImageUrl, newImageUrl);
            updatedGallery = {
              assets: [
                ...(storybook.assets || []),
                { name, prompt, imageUrl: newImageUrl, thumbnailUrl: newImageUrl }
              ]
            };
          }

          // Create the updated storybook data
          const updatedStorybookData = {
            ...storybook,
            pages: updatedPages,
            ...updatedGallery
          };

          // Log what we're about to set
          console.log('📊 Updated pages[currentPageIndex].imageElements:', 
            updatedStorybookData.pages[currentPageIndex].imageElements.map((el: ImageElement) => ({id: el.id, url: el.url})));

          // Save to database FIRST to ensure persistence
          console.log('💾 Saving updated page with cropped image to database...');
          try {
            await storybookAPI.update(id, {
              pages: updatedPages
            });
            console.log('✅ Page update saved to database');
          } catch (saveErr) {
            console.error('Failed to save page update to database:', saveErr);
          }

          // Single state update with both canvas and gallery changes
          setStorybook(updatedStorybookData);

          console.log(`✅ Updated canvas image and added to ${imageType} gallery`);
          console.log('📊 New image URL in state:', finalUrl);
          
          // Verify the state was actually updated after the next render
          setTimeout(() => {
            setStorybook(prev => {
              if (prev) {
                const el = prev.pages[currentPageIndex]?.imageElements.find(e => e.id === cropImageId);
                console.log('🔍 After state update, image URL is:', el?.url);
              }
              return prev; // Don't change anything, just log
            });
          }, 200);

          // Force a re-render by triggering a state change after a brief delay
          // This ensures the DOM updates with the new image URL
          setTimeout(() => {
            setStorybook(prev => prev ? ({ ...prev }) : null);
          }, 100);

          // Verify the image was saved by polling the server
          console.log('🔍 Verifying image was saved to server...');
          let verified = false;
          let attempts = 0;
          const maxAttempts = 10;
          
          while (!verified && attempts < maxAttempts) {
            attempts++;
            await new Promise(resolve => setTimeout(resolve, 300));
            
            const refreshed = await storybookAPI.getById(id);
            const collection = imageType === 'character' ? refreshed.storybook.characters :
                             imageType === 'scene' ? refreshed.storybook.scenes :
                             imageType === 'background' ? refreshed.storybook.backgrounds :
                             refreshed.storybook.assets;
            
            verified = collection?.some((item: any) => item.name === name && item.imageUrl === newImageUrl) || false;
            
            if (verified) {
              console.log(`✅ Verified image "${name}" exists in ${imageType} collection after ${attempts} attempts`);
            }
          }
          
          if (!verified) {
            console.warn(`⚠️ Could not verify image "${name}" in ${imageType} collection after ${maxAttempts} attempts`);
          }
          
          console.log('✅ Gallery updated with new cropped image, canvas preserved');
          
          // Generate a new thumbnail for this page after the crop
          // Wait a moment for state to settle, then generate thumbnail
          setTimeout(async () => {
            console.log('🖼️ Generating thumbnail for cropped image...');
            try {
              const thumbnail = await generateThumbnail(currentPageIndex, true);
              if (thumbnail) {
                // Update only the thumbnail in state, preserving the pages array
                setStorybook(prev => {
                  if (!prev) return prev;
                  const pages = [...prev.pages];
                  pages[currentPageIndex] = {
                    ...pages[currentPageIndex],
                    thumbnail
                  };
                  return { ...prev, pages };
                });
                console.log('✅ Thumbnail generated for cropped image');
              }
            } catch (err) {
              console.warn('Failed to generate thumbnail after crop:', err);
            }
          }, 500);
        }
        
        setShowCropModal(false);
        setCropImageId(null);
      } catch (err) {
        console.error('Failed to upload cropped image', err);
        finalizeWithDataUrl();
      }
    })();
  };

  const closeRephraseOptionsModal = () => {
    setShowRephraseOptionsModal(false);
    setRephraseOptions([]);
    setRephraseOriginalText('');
    setRephraseTargetTextId(null);
    setRephraseOptionsError('');
  };

  const generateRephraseOptions = async (content: string) => {
    if (!storybook?.storyType || !storybook?.storyTone) {
      throw new Error('Please select Story Type and Story Tone first');
    }

    setRephraseOptionsError('');
    setRephraseOptions([]);

    const response = await aiAPI.rephraseTextOptions(
      content,
      storybook.storyType,
      storybook.storyTone,
      storybook.storyStylePrompt,
      5
    );

    if (!response.success) {
      throw new Error(response.error || 'Failed to generate rephrase options');
    }

    const options = Array.isArray(response.textOptions)
      ? response.textOptions.filter((item: any) => typeof item === 'string' && item.trim().length > 0)
      : (response.text ? [response.text] : []);

    if (options.length === 0) {
      throw new Error('No options returned from AI');
    }

    setRephraseOptions(options.slice(0, 5));
  };

  const handleRephraseText = async (textId: string, content: string) => {
    if (!storybook?.storyType || !storybook?.storyTone) {
      alert('Please select Story Type and Story Tone first');
      return;
    }

    setShowRephraseOptionsModal(true);
    setRephraseTargetTextId(textId);
    setRephraseOriginalText(content);
    setIsRephrasing(true);
    try {
      await generateRephraseOptions(content);
    } catch (error) {
      console.error('Failed to rephrase text:', error);
      setRephraseOptionsError('Failed to generate options. You can retry or cancel.');
    } finally {
      setIsRephrasing(false);
    }
  };

  const handleRegenerateRephraseOptions = async () => {
    if (!rephraseOriginalText) return;
    setIsRephrasing(true);
    try {
      await generateRephraseOptions(rephraseOriginalText);
    } catch (error) {
      console.error('Failed to regenerate rephrase options:', error);
      setRephraseOptionsError('Failed to regenerate options. Please try again.');
    } finally {
      setIsRephrasing(false);
    }
  };

  const handleApplyRephraseOption = (option: string) => {
    if (!rephraseTargetTextId) return;
    updateTextElement(rephraseTargetTextId, { content: option });
    closeRephraseOptionsModal();
  };

  const handleApplyTextStyle = (textId: string, styleId: string) => {
    if (!currentPage) return;
    
    const textElement = currentPage.textElements.find(el => el.id === textId);
    if (!textElement) return;

    const style = getTextBoxStyle(styleId);
    if (!style) return;

    const updatedText = applyStyleToText(textElement, style);
    updateTextElement(textId, updatedText);
  };

  // Update character height category (for scene composition size relationships)
  const handleUpdateCharacterHeight = (characterIndex: number, heightCategory: 'tiny' | 'small' | 'medium' | 'tall' | 'very-tall') => {
    if (!storybook || !id) return;
    const char = storybook.characters[characterIndex];
    if (!char) return;
    
    // Update local state — auto-save will persist to backend
    setStorybook(prev => {
      if (!prev) return prev;
      const updatedCharacters = [...prev.characters];
      updatedCharacters[characterIndex] = { ...updatedCharacters[characterIndex], heightCategory };
      return { ...prev, characters: updatedCharacters };
    });
    console.log(`📏 Updated "${char.name}" height to ${heightCategory}`);
  };

  const generateCharacter = async (name: string, prompt: string, referenceImages: string[] = [], model: 'chatgpt' | 'gemini' = 'chatgpt', backgroundOption: 'transparent' | 'full-color' = 'transparent', chromaKeyColor: 'white' | 'green' | 'blue' = 'white', heightCategory: 'tiny' | 'small' | 'medium' | 'tall' | 'very-tall' = 'medium') => {
    // Set flag to prevent auto-save during generation
    isGeneratingAIContent.current = true;

    try {
      // Use the storybook's metadata
      const storyType = storybook?.storyType;
      const drawingType = storybook?.drawingType;
      const drawingStyle = storybook?.drawingStyle;
      const storyTone = storybook?.storyTone;
      const visualStylePrompt = storybook?.visualStylePrompt;

      // Batch generation keeps this render closure alive while characters are
      // added. Read the latest saved cast so each later character can inherit
      // the book's evolving art style.
      let currentCharacters = storybook?.characters || [];
      if (id) {
        try {
          const latestStorybook = await storybookAPI.getById(id);
          currentCharacters = latestStorybook.storybook?.characters || currentCharacters;
        } catch (error) {
          console.warn('Could not refresh character style references; using current editor data', error);
        }
      }

      // Gather existing character images as style references for art consistency
      // Only include characters that have images (up to 2 for efficiency)
      const styleReferenceImages = currentCharacters
        .filter((c: any) => c.imageUrl && c.name !== name)
        .slice(0, 2)
        .map((c: any) => c.imageUrl);
      
      if (styleReferenceImages.length > 0) {
        console.log(`🎨 Passing ${styleReferenceImages.length} existing character(s) as style references for consistency`);
      }

      // The picker is explicitly style-only. Keep those images out of the
      // identity/content reference role so an existing character is not copied.
      const combinedStyleReferenceImages = Array.from(
        new Set([...referenceImages, ...styleReferenceImages])
      );
      if (model === 'chatgpt' && combinedStyleReferenceImages.length > MAX_OPENAI_REFERENCE_IMAGES) {
        throw new Error(
          `ChatGPT accepts up to ${MAX_OPENAI_REFERENCE_IMAGES} reference images. Deselect ${combinedStyleReferenceImages.length - MAX_OPENAI_REFERENCE_IMAGES} style reference(s).`
        );
      }
      const response = await aiAPI.generateCharacter(prompt, storyType, drawingType, drawingStyle, storyTone, [], visualStylePrompt, model, backgroundOption, chromaKeyColor, combinedStyleReferenceImages, manualAIIdempotencyKey('character', id));
      if (storybook && id) {
        console.log(`💾 Adding character "${name}" to storybook...`);
        await storybookAPI.addCharacter(id, name, prompt, response.imageUrl, response.thumbnailUrl, heightCategory);
        
        // Verify character was saved by checking backend with retries
        console.log(`🔍 Verifying ${name} was saved to database...`);
        let verified: any = null;
        let retries = 0;
        const maxRetries = 10;
        let delay = 500; // Start with 500ms, will increase with backoff
        
        while (retries < maxRetries) {
          await new Promise(resolve => setTimeout(resolve, delay));
          try {
            verified = await storybookAPI.getById(id);
            const charExists = verified.storybook.characters?.some((c: any) => c.name === name);
            
            if (charExists) {
              console.log(`✅ Verified character "${name}" saved to database`);
              // Merge verified characters into current storybook to preserve other data
              const newCharacters = verified.storybook.characters;
              console.log('🔍 Merging characters:', newCharacters?.map((c: any) => ({ name: c.name, hasImage: !!c.imageUrl })));
              setStorybook(prev => prev ? {
                ...prev,
                characters: newCharacters
              } : verified.storybook);
              console.log('✅ Character data updated in state from database');
              break;
            }
          } catch (fetchError) {
            console.warn(`⚠️ Error fetching storybook during verification:`, fetchError);
          }
          
          retries++;
          delay = Math.min(delay * 1.5, 3000); // Exponential backoff, max 3 seconds
          console.log(`⏳ Character "${name}" not found yet, retry ${retries}/${maxRetries} (next delay: ${delay}ms)...`);
          
          if (retries === maxRetries) {
            // Final attempt - don't throw, just warn and continue
            console.warn(`⚠️ Could not verify character save: ${name} after ${maxRetries} attempts. The character may still be saving.`);
            // Try one more reload to update state anyway
            try {
              const finalCheck = await storybookAPI.getById(id);
              setStorybook(prev => prev ? {
                ...prev,
                characters: finalCheck.storybook.characters || prev.characters
              } : finalCheck.storybook);
            } catch (e) {
              console.warn('Final reload also failed:', e);
            }
          }
        }
      }
      return response.imageUrl;
    } catch (error: any) {
      console.error('Failed to generate character:', error);
      
      // Check if it's a 402 Payment Required error
      if (error.response && error.response.status === 402) {
        const errorData = error.response.data;
        const message = errorData.message || errorData.error || 'Insufficient tokens. Please purchase more tokens to continue.';
        alert(message);
      } else {
        // Extract error message from response
        const errorMessage = error.response?.data?.error || error.message || 'Failed to generate character';
        const suggestion = error.response?.data?.suggestion;
        
        // Display user-friendly error with suggestion if available
        if (suggestion) {
          alert(`${errorMessage}\n\n💡 Suggestion: ${suggestion}`);
        } else {
          alert(errorMessage);
        }
      }
    } finally {
      // Clear flag to allow auto-save again
      isGeneratingAIContent.current = false;
    }
  };

  // Regenerate character - generates new image and replaces existing character
  const regenerateCharacter = async (name: string, prompt: string) => {
    isGeneratingAIContent.current = true;

    try {
      const storyType = storybook?.storyType;
      const drawingType = storybook?.drawingType;
      const drawingStyle = storybook?.drawingStyle;
      const storyTone = storybook?.storyTone;
      const visualStylePrompt = storybook?.visualStylePrompt;
      
      // Gather other character images as style references for consistency
      const styleReferenceImages = (storybook?.characters || [])
        .filter((c: any) => c.imageUrl && c.name !== name)
        .slice(0, 2)
        .map((c: any) => c.imageUrl);
      
      const response = await aiAPI.generateCharacter(prompt, storyType, drawingType, drawingStyle, storyTone, [], visualStylePrompt, 'gemini', 'transparent', 'white', styleReferenceImages, manualAIIdempotencyKey('character-regenerate', id));
      if (storybook && id) {
        console.log(`🔄 Regenerating character "${name}"...`);
        await storybookAPI.updateCharacter(id, name, prompt, response.imageUrl, response.thumbnailUrl);
        
        // Verify character was updated
        let verified: any = null;
        let retries = 0;
        const maxRetries = 10;
        let delay = 500;
        
        while (retries < maxRetries) {
          await new Promise(resolve => setTimeout(resolve, delay));
          try {
            verified = await storybookAPI.getById(id);
            const updatedChar = verified.storybook.characters?.find((c: any) => c.name === name);
            
            if (updatedChar && updatedChar.imageUrl === response.imageUrl) {
              console.log(`✅ Verified character "${name}" regenerated`);
              setStorybook(prev => prev ? {
                ...prev,
                characters: verified.storybook.characters
              } : verified.storybook);
              break;
            }
          } catch (fetchError) {
            console.warn(`⚠️ Error fetching storybook during verification:`, fetchError);
          }
          
          retries++;
          delay = Math.min(delay * 1.5, 3000);
          
          if (retries === maxRetries) {
            console.warn(`⚠️ Could not verify character regeneration: ${name}`);
            try {
              const finalCheck = await storybookAPI.getById(id);
              setStorybook(prev => prev ? {
                ...prev,
                characters: finalCheck.storybook.characters || prev.characters
              } : finalCheck.storybook);
            } catch (e) {
              console.warn('Final reload also failed:', e);
            }
          }
        }
      }
      return response.imageUrl;
    } catch (error: any) {
      console.error('Failed to regenerate character:', error);
      if (error.response && error.response.status === 402) {
        const errorData = error.response.data;
        const message = errorData.message || errorData.error || 'Insufficient tokens.';
        alert(message);
      } else {
        const errorMessage = error.response?.data?.error || error.message || 'Failed to regenerate character';
        alert(errorMessage);
      }
    } finally {
      isGeneratingAIContent.current = false;
    }
  };

  const generateAsset = async (name: string, prompt: string, referenceImages: string[] = [], model: 'chatgpt' | 'gemini' = 'chatgpt', backgroundOption: 'transparent' | 'full-color' = 'transparent', chromaKeyColor: 'white' | 'green' | 'blue' = 'white') => {
    // Set flag to prevent auto-save during generation
    isGeneratingAIContent.current = true;

    try {
      const drawingType = storybook?.drawingType || 'children\'s book illustration';
      const drawingStyle = storybook?.drawingStyle;
      const visualStylePrompt = storybook?.visualStylePrompt;
      if (model === 'chatgpt' && referenceImages.length > MAX_OPENAI_REFERENCE_IMAGES) {
        throw new Error(
          `ChatGPT accepts up to ${MAX_OPENAI_REFERENCE_IMAGES} reference images. Deselect ${referenceImages.length - MAX_OPENAI_REFERENCE_IMAGES} style reference(s).`
        );
      }
      const response = await aiAPI.generateAsset(prompt, drawingType, drawingStyle, referenceImages, visualStylePrompt, model, backgroundOption, chromaKeyColor, manualAIIdempotencyKey('asset', id));
      if (storybook && id) {
        // IMPORTANT: Fetch fresh data from DB to avoid overwriting other assets
        // React state might be stale if multiple pages are being generated
        const freshData = await storybookAPI.getById(id);
        const currentAssets = freshData.storybook?.assets || [];
        
        const updatedStorybook = {
          ...freshData.storybook,
          assets: [
            ...currentAssets,
            {
              name,
              prompt,
              imageUrl: response.imageUrl,
              thumbnailUrl: response.thumbnailUrl,
              assetId: response.assetId,
              assetVersionId: response.outputSha256 ? `asset_${response.outputSha256.slice(0, 32)}` : undefined,
              assetVersion: 1,
              approvalStatus: 'approved' as const,
              contentSha256: response.outputSha256,
              recurringRole: 'other' as const
            }
          ]
        };
        await storybookAPI.update(id, updatedStorybook);
        setStorybook(updatedStorybook);
      }
      return response.imageUrl;
    } catch (error: any) {
      console.error('Failed to generate asset:', error);
      
      // Check if it's a 402 Payment Required error
      if (error.response && error.response.status === 402) {
        const errorData = error.response.data;
        const message = errorData.message || errorData.error || 'Insufficient tokens. Please purchase more tokens to continue.';
        alert(message);
      } else {
        // Extract error message from response
        const errorMessage = error.response?.data?.error || error.message || 'Failed to generate asset';
        const suggestion = error.response?.data?.suggestion;
        
        // Display user-friendly error with suggestion if available
        if (suggestion) {
          alert(`${errorMessage}\n\n💡 Suggestion: ${suggestion}`);
        } else {
          alert(errorMessage);
        }
      }
    } finally {
      // Clear flag to allow auto-save again
      isGeneratingAIContent.current = false;
    }
  };

  const generateBackground = async (name: string, prompt: string, referenceImages: string[] = [], model: 'chatgpt' | 'gemini' = 'chatgpt', viewTransforms?: string[]) => {
    // Set flag to prevent auto-save during generation
    isGeneratingAIContent.current = true;

    try {
      const storyType = storybook?.storyType;
      const drawingType = storybook?.drawingType;
      const drawingStyle = storybook?.drawingStyle;
      const storyTone = storybook?.storyTone;
      const visualStylePrompt = storybook?.visualStylePrompt;
      const canvasSize = storybook?.canvasSize;
      
      // Gather existing character/background images as style references for art consistency
      const existingCharImages = (storybook?.characters || [])
        .filter((c: any) => c.imageUrl)
        .slice(0, 2)
        .map((c: any) => c.imageUrl);
      const styleReferenceImages = existingCharImages.length > 0 ? existingCharImages : [];
      
      if (styleReferenceImages.length > 0) {
        console.log(`🎨 Passing ${styleReferenceImages.length} existing image(s) as style references for background consistency`);
      }

      // A selected background becomes a content reference only when the user
      // asks for a camera/view transformation; otherwise the picker is style-only.
      const hasViewTransformation = (viewTransforms?.length || 0) > 0;
      const contentReferenceImages = hasViewTransformation ? referenceImages : [];
      const selectedStyleReferences = hasViewTransformation ? [] : referenceImages;
      const combinedStyleReferenceImages = Array.from(
        new Set([...selectedStyleReferences, ...styleReferenceImages])
      );
      const openAIReferenceCount = contentReferenceImages.length + combinedStyleReferenceImages.length;
      if (model === 'chatgpt' && openAIReferenceCount > MAX_OPENAI_REFERENCE_IMAGES) {
        throw new Error(
          `ChatGPT accepts up to ${MAX_OPENAI_REFERENCE_IMAGES} reference images. Deselect ${openAIReferenceCount - MAX_OPENAI_REFERENCE_IMAGES} reference(s).`
        );
      }
      const response = await aiAPI.generateBackground(prompt, storyType, drawingType, drawingStyle, storyTone, contentReferenceImages, visualStylePrompt, model, canvasSize, viewTransforms, combinedStyleReferenceImages, manualAIIdempotencyKey('background', id));
      if (storybook && id) {
        console.log(`💾 Adding background "${name}" to storybook...`);
        
        // IMPORTANT: Fetch fresh data from DB to avoid overwriting other backgrounds
        // React state might be stale if multiple pages are being generated
        const freshData = await storybookAPI.getById(id);
        const currentBackgrounds = freshData.storybook?.backgrounds || [];
        
        const updatedStorybook = {
          ...freshData.storybook,
          backgrounds: [
            ...currentBackgrounds,
            {
              name,
              prompt,
              imageUrl: response.imageUrl,
              thumbnailUrl: response.thumbnailUrl,
              assetId: response.assetId,
              assetVersionId: response.outputSha256 ? `background_${response.outputSha256.slice(0, 32)}` : undefined,
              assetVersion: 1,
              approvalStatus: 'approved' as const,
              contentSha256: response.outputSha256,
              recurringRole: 'environment' as const
            }
          ]
        };
        // Save to backend
        await storybookAPI.update(id, updatedStorybook);
        
        // Verify background was saved by checking backend with retries
        console.log(`🔍 Verifying ${name} was saved to database...`);
        let verified: any = null;
        let retries = 0;
        const maxRetries = 10;
        let delay = 500;
        
        while (retries < maxRetries) {
          await new Promise(resolve => setTimeout(resolve, delay));
          try {
            verified = await storybookAPI.getById(id);
            const bgExists = verified.storybook.backgrounds?.some((bg: any) => bg.name === name);
            
            if (bgExists) {
              console.log(`✅ Verified background "${name}" saved to database`);
              // Merge verified backgrounds into current storybook to preserve characters
              setStorybook(prev => prev ? {
                ...prev,
                backgrounds: verified.storybook.backgrounds
              } : verified.storybook);
              console.log('✅ Background data updated in state from database');
              break;
            }
          } catch (fetchError) {
            console.warn(`⚠️ Error fetching during verification:`, fetchError);
          }
          
          retries++;
          delay = Math.min(delay * 1.5, 3000);
          console.log(`⏳ Background "${name}" not found yet, retry ${retries}/${maxRetries} (next delay: ${delay}ms)...`);
          
          if (retries === maxRetries) {
            console.warn(`⚠️ Could not verify background save: ${name} after ${maxRetries} attempts. The background may still be saving.`);
            try {
              const finalCheck = await storybookAPI.getById(id);
              setStorybook(prev => prev ? {
                ...prev,
                backgrounds: finalCheck.storybook.backgrounds || prev.backgrounds
              } : finalCheck.storybook);
            } catch (e) {
              console.warn('Final reload also failed:', e);
            }
          }
        }
      }
      return response.imageUrl;
    } catch (error: any) {
      console.error('Failed to generate background:', error);
      
      // Check if it's a 402 Payment Required error
      if (error.response && error.response.status === 402) {
        const errorData = error.response.data;
        const message = errorData.message || errorData.error || 'Insufficient tokens. Please purchase more tokens to continue.';
        alert(message);
      } else {
        // Extract error message from response
        const errorMessage = error.response?.data?.error || error.message || 'Failed to generate background';
        const suggestion = error.response?.data?.suggestion;
        
        // Display user-friendly error with suggestion if available
        if (suggestion) {
          alert(`${errorMessage}\n\n💡 Suggestion: ${suggestion}`);
        } else {
          alert(errorMessage);
        }
      }
    } finally {
      // Clear flag to allow auto-save again
      isGeneratingAIContent.current = false;
    }
  };

  // Regenerate background - generates new image and replaces existing background
  const regenerateBackground = async (name: string, prompt: string) => {
    isGeneratingAIContent.current = true;

    try {
      const storyType = storybook?.storyType;
      const drawingType = storybook?.drawingType;
      const drawingStyle = storybook?.drawingStyle;
      const storyTone = storybook?.storyTone;
      const visualStylePrompt = storybook?.visualStylePrompt;
      const canvasSize = storybook?.canvasSize;
      
      // Gather existing character images as style references for consistency
      const styleReferenceImages = (storybook?.characters || [])
        .map(character => approvedCharacterAnchor(character))
        .filter((imageUrl): imageUrl is string => Boolean(imageUrl))
        .slice(0, 2);
      
      const response = await aiAPI.generateBackground(prompt, storyType, drawingType, drawingStyle, storyTone, [], visualStylePrompt, 'gemini', canvasSize, undefined, styleReferenceImages, manualAIIdempotencyKey('background-regenerate', id));
      if (storybook && id) {
        console.log(`🔄 Regenerating background "${name}"...`);
        const freshData = await storybookAPI.getById(id);
        await storybookAPI.update(id, {
          ...freshData.storybook,
          backgrounds: (freshData.storybook.backgrounds || []).map((background: any) => (
            background.name === name
              ? {
                  ...background,
                  prompt,
                  imageUrl: response.imageUrl,
                  thumbnailUrl: response.thumbnailUrl,
                  assetId: response.assetId,
                  assetVersionId: response.outputSha256 ? `background_${response.outputSha256.slice(0, 32)}` : undefined,
                  assetVersion: Math.max(0, Number(background.assetVersion) || 0) + 1,
                  approvalStatus: 'approved' as const,
                  contentSha256: response.outputSha256,
                  recurringRole: 'environment' as const
                }
              : background
          ))
        });
        
        // Verify background was updated
        let verified: any = null;
        let retries = 0;
        const maxRetries = 10;
        let delay = 500;
        
        while (retries < maxRetries) {
          await new Promise(resolve => setTimeout(resolve, delay));
          try {
            verified = await storybookAPI.getById(id);
            const updatedBg = verified.storybook.backgrounds?.find((b: any) => b.name === name);
            
            if (updatedBg && updatedBg.imageUrl === response.imageUrl) {
              console.log(`✅ Verified background "${name}" regenerated`);
              setStorybook(prev => prev ? {
                ...prev,
                backgrounds: verified.storybook.backgrounds
              } : verified.storybook);
              break;
            }
          } catch (fetchError) {
            console.warn(`⚠️ Error fetching storybook during verification:`, fetchError);
          }
          
          retries++;
          delay = Math.min(delay * 1.5, 3000);
          
          if (retries === maxRetries) {
            console.warn(`⚠️ Could not verify background regeneration: ${name}`);
            try {
              const finalCheck = await storybookAPI.getById(id);
              setStorybook(prev => prev ? {
                ...prev,
                backgrounds: finalCheck.storybook.backgrounds || prev.backgrounds
              } : finalCheck.storybook);
            } catch (e) {
              console.warn('Final reload also failed:', e);
            }
          }
        }
      }
      return response.imageUrl;
    } catch (error: any) {
      console.error('Failed to regenerate background:', error);
      if (error.response && error.response.status === 402) {
        const errorData = error.response.data;
        const message = errorData.message || errorData.error || 'Insufficient tokens.';
        alert(message);
      } else {
        const errorMessage = error.response?.data?.error || error.message || 'Failed to regenerate background';
        alert(errorMessage);
      }
    } finally {
      isGeneratingAIContent.current = false;
    }
  };

  const generateScene = async (sceneName: string, scenePrompt: string, characterNames: string[], assetNames: string[], backgroundName?: string, cameraView?: string, backgroundOption?: 'transparent' | 'full-color', chromaKeyColor: 'white' | 'green' | 'blue' = 'green', model: 'chatgpt' | 'gemini' = 'gemini') => {
    // Set flag to prevent auto-save during generation
    isGeneratingAIContent.current = true;

    try {
      const readiness = identityReadinessForBook(storybook);
      if (!readiness.ready) {
        throw new Error(`Complete Cast & Style identity approval before composing scenes. ${readiness.issues[0]?.message || ''}`.trim());
      }
      const storyType = storybook?.storyType;
      const drawingType = storybook?.drawingType;
      const storyTone = storybook?.storyTone;
      const canvasSize = storybook?.canvasSize || '5.5x8.5';
      
      // Get selected characters' images
      const missingCharacterNames = characterNames.filter(name => {
        const character = storybook!.characters.find(char => char.name === name);
        return !approvedCharacterAnchor(character);
      });
      if (missingCharacterNames.length > 0) {
        throw new Error(
          `Add an approved image for ${missingCharacterNames.join(', ')} before composing this scene.`
        );
      }

      // Resolve characters in the exact selection order so names, heights, and
      // reference image slots always describe the same identity.
      const selectedChars = characterNames
        .map(name => storybook!.characters.find(char => char.name === name))
        .filter((char): char is NonNullable<typeof char> => Boolean(approvedCharacterAnchor(char)));
      const resolvedCharacterNames = selectedChars.map(char => char.name);
      const characterImages = selectedChars.map(char => approvedCharacterAnchor(char)!);
      const characterHeights = selectedChars.map(char => char.heightCategory || 'medium');
      
      // Get selected assets' images
      const missingAssetNames = assetNames.filter(name => {
        const asset = (storybook!.assets || []).find(candidate => candidate.name === name);
        return !asset?.imageUrl
          || asset.approvalStatus !== 'approved'
          || !asset.assetId
          || !/^[a-f0-9]{64}$/.test(asset.contentSha256 || '');
      });
      if (missingAssetNames.length > 0) {
        throw new Error(
          `Add an approved image for ${missingAssetNames.join(', ')} before composing this scene.`
        );
      }
      const selectedAssets = assetNames
        .map(name => (storybook!.assets || []).find(asset => asset.name === name))
        .filter((asset): asset is NonNullable<typeof asset> => !!asset?.imageUrl);
      const resolvedAssetNames = selectedAssets.map(asset => asset.name);
      const assetImages = selectedAssets.map(asset => asset.imageUrl!);
      
      // Get selected background image
      let backgroundImage: string | undefined;
      if (backgroundName) {
        const selectedBg = (storybook!.backgrounds || []).find(bg => bg.name === backgroundName);
        if (selectedBg?.imageUrl && (
          selectedBg.approvalStatus !== 'approved'
          || !selectedBg.assetId
          || !/^[a-f0-9]{64}$/.test(selectedBg.contentSha256 || '')
        )) {
          throw new Error(`Regenerate or approve ${backgroundName} as a versioned background reference before composing this scene.`);
        }
        backgroundImage = selectedBg?.imageUrl;
      }

      const openAIReferenceCount = characterImages.length + assetImages.length + (backgroundImage ? 1 : 0);
      if (model === 'chatgpt' && openAIReferenceCount > MAX_OPENAI_REFERENCE_IMAGES) {
        throw new Error(
          `ChatGPT scenes accept up to ${MAX_OPENAI_REFERENCE_IMAGES} total references. This scene has ${openAIReferenceCount}; remove a character, prop, or background.`
        );
      }
      
      console.log('🎨 Composing scene with:');
      console.log(`  Characters: ${characterImages.length}`);
      console.log(`  Assets: ${assetImages.length}`);
      console.log(`  Background: ${backgroundImage ? 'Yes' : 'No'}`);
      console.log(`  Canvas Size: ${canvasSize}`);

      // Enhance prompt with camera view and background option
      let enhancedPrompt = scenePrompt;
      if (backgroundOption === 'transparent') {
        // Add color instructions for chroma key
        if (chromaKeyColor === 'green') {
          enhancedPrompt = `${scenePrompt}, solid bright green chroma key background (RGB: 0, 255, 0)`;
        } else if (chromaKeyColor === 'blue') {
          enhancedPrompt = `${scenePrompt}, solid bright blue chroma key background (RGB: 0, 0, 255)`;
        } else if (chromaKeyColor === 'white') {
          enhancedPrompt = `${scenePrompt}, solid pure white background (RGB: 255, 255, 255)`;
        }
      } else if (backgroundOption === 'full-color') {
        // For full-color backgrounds, request canvas color matching the current page
        const currentPageBgColor = storybook?.pages[currentPageIndex]?.backgroundColor || '#FFFFFF';
        enhancedPrompt = `${scenePrompt}, with background color matching ${currentPageBgColor}`;
      }
      if (cameraView) {
        enhancedPrompt = `${enhancedPrompt}. Camera angle: ${cameraView}`;
      }

      // Use the new composeScene API with canvas size
      const drawingStyle = storybook?.drawingStyle;
      const visualStylePrompt = storybook?.visualStylePrompt;
      const response = await aiAPI.composeScene(
        enhancedPrompt, 
        characterImages,
        backgroundImage, 
        storyType,
        drawingType,
        drawingStyle,
        storyTone,
        canvasSize,
        visualStylePrompt,
        backgroundOption,
        chromaKeyColor,
        model,
        resolvedCharacterNames,
        characterHeights,
        assetImages,
        resolvedAssetNames,
        undefined,
        manualAIIdempotencyKey('scene', id)
      );
      
      if (response.imageUrl && storybook && id) {
        // IMPORTANT: Fetch fresh data from DB to avoid overwriting other scenes
        // React state might be stale if multiple pages are being generated
        const freshData = await storybookAPI.getById(id);
        const currentScenes = freshData.storybook?.scenes || [];
        
        // Save the scene to the storybook
        const updatedStorybook = {
          ...freshData.storybook,
          scenes: [
            ...currentScenes,
            {
              name: sceneName,
              prompt: scenePrompt,
              imageUrl: response.imageUrl,
              thumbnailUrl: response.thumbnailUrl,
              characterNames: resolvedCharacterNames,
              backgroundName: backgroundName
            }
          ]
        };
        
        await storybookAPI.update(id, updatedStorybook);
        
        // Verify scene was saved by checking backend with retries
        console.log(`🔍 Verifying scene "${sceneName}" was saved to database...`);
        let verified: any = null;
        let retries = 0;
        const maxRetries = 10;
        let delay = 500;
        
        while (retries < maxRetries) {
          await new Promise(resolve => setTimeout(resolve, delay));
          try {
            verified = await storybookAPI.getById(id);
            const sceneExists = verified.storybook.scenes?.some((s: any) => s.name === sceneName);
            
            if (sceneExists) {
              console.log(`✅ Verified scene "${sceneName}" saved to database`);
              // Merge verified scenes into current storybook to preserve other data
              setStorybook(prev => prev ? {
                ...prev,
                scenes: verified.storybook.scenes
              } : verified.storybook);
              console.log('✅ Scene data updated in state from database');
              break;
            }
          } catch (fetchError) {
            console.warn(`⚠️ Error fetching during verification:`, fetchError);
          }
          
          retries++;
          delay = Math.min(delay * 1.5, 3000);
          console.log(`⏳ Scene "${sceneName}" not found yet, retry ${retries}/${maxRetries} (next delay: ${delay}ms)...`);
          
          if (retries === maxRetries) {
            console.warn(`⚠️ Could not verify scene save: ${sceneName} after ${maxRetries} attempts. The scene may still be saving.`);
            try {
              const finalCheck = await storybookAPI.getById(id);
              setStorybook(prev => prev ? {
                ...prev,
                scenes: finalCheck.storybook.scenes || prev.scenes
              } : finalCheck.storybook);
            } catch (e) {
              console.warn('Final reload also failed:', e);
            }
          }
        }
        
        return response.imageUrl;
      }
    } catch (error: any) {
      console.error('Failed to compose scene:', error);
      
      // Check if it's a 402 Payment Required error
      if (error.response && error.response.status === 402) {
        const errorData = error.response.data;
        const message = errorData.message || errorData.error || 'Insufficient tokens. Please purchase more tokens to continue.';
        alert(message);
      } else {
        // Extract error message from response
        const errorMessage = error.response?.data?.error || error.message || 'Failed to compose scene';
        const suggestion = error.response?.data?.suggestion;
        
        // Display user-friendly error with suggestion if available
        if (suggestion) {
          alert(`${errorMessage}\n\n💡 Suggestion: ${suggestion}`);
        } else {
          alert(errorMessage);
        }
      }
    } finally {
      // Clear flag to allow auto-save again
      isGeneratingAIContent.current = false;
    }
  };

  const deleteAsset = (imageUrl: string) => {
    if (!storybook || !id) return;
    
    const updatedStorybook = {
      ...storybook,
      assets: (storybook.assets || []).filter(asset => asset.imageUrl !== imageUrl)
    };
    storybookAPI.update(id, updatedStorybook);
    setStorybook(updatedStorybook);
  };

  const editImage = async (imageUrl: string, prompt: string, name: string, type: 'asset' | 'character' | 'background' | 'scene', backgroundOption?: 'transparent' | 'full-color', chromaKeyColor?: 'white' | 'green' | 'blue', fromCanvas?: boolean, canvasImageId?: string, maskImageUrl?: string, lassoData?: { points: {x: number, y: number}[], mode: string, canvasWidth: number, canvasHeight: number }, referenceImages?: string[], aspectRatio?: string, cameraAngle?: string): Promise<string | undefined> => {
    // Set flag to prevent auto-save during generation
    isGeneratingAIContent.current = true;

    try {
      const storyType = storybook?.storyType;
      const drawingType = storybook?.drawingType;
      const storyTone = storybook?.storyTone;
      
      console.log('✏️ Editing image:', { imageUrl, prompt, name, type, backgroundOption, chromaKeyColor, fromCanvas, canvasImageId, hasMask: !!maskImageUrl, hasLasso: !!lassoData, hasReferenceImages: !!referenceImages, aspectRatio, cameraAngle });
      
      const response = await aiAPI.editImage(imageUrl, prompt, storyType, drawingType, storyTone, backgroundOption, chromaKeyColor, maskImageUrl, lassoData, referenceImages, aspectRatio, cameraAngle);
      
      if (response.imageUrl && storybook && id) {
        let updatedStorybook = { ...storybook };
        
        // If editing from canvas, replace the image element with proper dimensions
        if (fromCanvas && canvasImageId && currentPage) {
          // Load the new image to get its dimensions
          const img = new Image();
          img.crossOrigin = 'anonymous';
          
          await new Promise<void>((resolve, reject) => {
            img.onload = () => {
              const updatedPages = updatedStorybook.pages.map(page => {
                if (page.id === currentPage.id) {
                  return {
                    ...page,
                    imageElements: page.imageElements.map(imgElement => {
                      if (imgElement.id === canvasImageId) {
                        // Use the new image's actual dimensions with proper scaling
                        const newAspectRatio = img.naturalWidth / img.naturalHeight;
                        const oldWidth = imgElement.width;
                        
                        // Keep the same width but adjust height to match new aspect ratio
                        let newWidth = oldWidth;
                        let newHeight = oldWidth / newAspectRatio;
                        
                        console.log(`📐 Updating image dimensions: ${oldWidth}x${imgElement.height} → ${newWidth}x${newHeight}`);
                        console.log(`   Natural size: ${img.naturalWidth}x${img.naturalHeight}, aspect ratio: ${newAspectRatio.toFixed(3)}`);
                        
                        return { 
                          ...imgElement, 
                          url: response.imageUrl, 
                          characterName: name, 
                          prompt: `Edited: ${prompt}`,
                          width: newWidth,
                          height: newHeight,
                          imageHistory: [
                            ...(imgElement.imageHistory || []),
                            {
                              action: 'edited' as const,
                              timestamp: new Date().toISOString(),
                              previousUrl: imgElement.url,
                              newUrl: response.imageUrl,
                              details: `AI Edit: ${prompt}`,
                              resolution: { width: img.naturalWidth, height: img.naturalHeight }
                            }
                          ]
                        };
                      }
                      return imgElement;
                    })
                  };
                }
                return page;
              });
              
              updatedStorybook = { ...updatedStorybook, pages: updatedPages };
              console.log('✅ Canvas image updated with new aspect ratio');
              resolve();
            };
            
            img.onerror = () => {
              console.error('Failed to load new image, updating URL only');
              // Fallback: just update URL without changing dimensions
              const updatedPages = updatedStorybook.pages.map(page => {
                if (page.id === currentPage.id) {
                  return {
                    ...page,
                    imageElements: page.imageElements.map(imgElement => 
                      imgElement.id === canvasImageId 
                        ? { 
                            ...imgElement, 
                            url: response.imageUrl, 
                            characterName: name, 
                            prompt: `Edited: ${prompt}`,
                            imageHistory: [
                              ...(imgElement.imageHistory || []),
                              {
                                action: 'edited' as const,
                                timestamp: new Date().toISOString(),
                                previousUrl: imgElement.url,
                                newUrl: response.imageUrl,
                                details: `AI Edit: ${prompt}`
                              }
                            ]
                          }
                        : imgElement
                    )
                  };
                }
                return page;
              });
              updatedStorybook = { ...updatedStorybook, pages: updatedPages };
              resolve();
            };
            
            img.src = response.imageUrl;
          });
        }
        
        // Add the edited image as a new entry in the appropriate gallery
        switch (type) {
          case 'asset':
            // Always add as new asset
            updatedStorybook = {
              ...updatedStorybook,
              assets: [
                ...(updatedStorybook.assets || []),
                {
                  name: `${name}-edited`,
                  prompt: `Edited: ${prompt}`,
                  imageUrl: response.imageUrl,
                  thumbnailUrl: response.thumbnailUrl
                }
              ]
            };
            console.log('➕ Added edited image as new asset');
            break;
          case 'character':
            // Always add as new character
            await storybookAPI.update(id, updatedStorybook); // Save canvas changes first
            await storybookAPI.addCharacter(id, `${name}-edited`, `Edited: ${prompt}`, response.imageUrl, response.thumbnailUrl);
            // Update local state instead of full reload
            const refreshed = await storybookAPI.getById(id);
            setStorybook(prev => prev ? {
              ...prev,
              characters: refreshed.storybook.characters
            } : prev);
            console.log('➕ Added edited image as new character');
            return response.imageUrl;
          case 'background':
            // Always add as new background
            updatedStorybook = {
              ...updatedStorybook,
              backgrounds: [
                ...(updatedStorybook.backgrounds || []),
                {
                  name: `${name}-edited`,
                  prompt: `Edited: ${prompt}`,
                  imageUrl: response.imageUrl,
                  thumbnailUrl: response.thumbnailUrl
                }
              ]
            };
            console.log('➕ Added edited image as new background');
            break;
          case 'scene':
            // Always add as new scene
            updatedStorybook = {
              ...updatedStorybook,
              scenes: [
                ...(updatedStorybook.scenes || []),
                {
                  name: `${name}-edited`,
                  prompt: `Edited: ${prompt}`,
                  imageUrl: response.imageUrl,
                  thumbnailUrl: response.thumbnailUrl,
                  characterNames: [],
                  backgroundName: undefined
                }
              ]
            };
            console.log('➕ Added edited image as new scene');
            break;
        }
        
        console.log('📝 Updating storybook with new', type, ':', name);
        await storybookAPI.update(id, updatedStorybook);
        
        // Wait for server to fully process and save
        await new Promise(resolve => setTimeout(resolve, 500));
        // Refresh galleries and pages to show new thumbnails and update canvas
        console.log('🔄 Refreshing galleries and pages after image edit...');
        if (id) {
          const refreshed = await storybookAPI.getById(id);
          setStorybook(prev => prev ? {
            ...prev,
            pages: refreshed.storybook.pages,
            characters: refreshed.storybook.characters,
            assets: refreshed.storybook.assets,
            backgrounds: refreshed.storybook.backgrounds,
            scenes: refreshed.storybook.scenes
          } : prev);
        }
        
        console.log('✅ Image edited successfully and added as new', type);
        return response.imageUrl;
      }
    } catch (error: any) {
      console.error('Failed to edit image:', error);
      
      // Check if it's a 402 Payment Required error
      if (error.response && error.response.status === 402) {
        const errorData = error.response.data;
        const message = errorData.message || errorData.error || 'Insufficient tokens. Please purchase more tokens to continue.';
        alert(message);
      } else if (error.response && error.response.data) {
        // Handle other API errors
        const message = error.response.data.message || error.response.data.error || 'Failed to edit image. Please try again.';
        alert(message);
      } else {
        alert('Failed to edit image. Please try again.');
      }
      throw error;
    } finally {
      // Clear flag to allow auto-save again
      isGeneratingAIContent.current = false;
    }
    return undefined;
  };

  const deleteCharacter = (imageUrl: string) => {
    if (!storybook || !id) return;
    
    const updatedStorybook = {
      ...storybook,
      characters: storybook.characters.filter(char => char.imageUrl !== imageUrl)
    };
    storybookAPI.update(id, updatedStorybook);
    setStorybook(updatedStorybook);
  };

  const deleteBackground = (imageUrl: string) => {
    if (!storybook || !id) return;
    
    const updatedStorybook = {
      ...storybook,
      backgrounds: (storybook.backgrounds || []).filter(bg => bg.imageUrl !== imageUrl)
    };
    storybookAPI.update(id, updatedStorybook);
    setStorybook(updatedStorybook);
  };

  const deleteScene = (imageUrl: string) => {
    if (!storybook || !id) return;
    
    const updatedStorybook = {
      ...storybook,
      scenes: (storybook.scenes || []).filter(scene => scene.imageUrl !== imageUrl)
    };
    storybookAPI.update(id, updatedStorybook);
    setStorybook(updatedStorybook);
  };

  const uploadAsset = async (file: File, name: string) => {
    if (!storybook || !id) return;
    
    try {
      const formData = new FormData();
      formData.append('image', file);
      formData.append('type', 'asset');
      formData.append('name', name);
      
      const response = await fetch(`${process.env.REACT_APP_API_URL}/storybooks/${id}/upload`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        },
        body: formData
      });
      
      if (!response.ok) throw new Error('Upload failed');
      
      const data = await response.json();
      
      const updatedStorybook = {
        ...storybook,
        assets: [
          ...(storybook.assets || []),
          {
            name,
            prompt: 'Uploaded image',
            imageUrl: data.imageUrl,
            thumbnailUrl: data.thumbnailUrl
          }
        ]
      };
      await storybookAPI.update(id, updatedStorybook);
      setStorybook(updatedStorybook);
    } catch (error) {
      console.error('Failed to upload asset:', error);
      throw error;
    }
  };

  const uploadCharacter = async (file: File, name: string) => {
    if (!storybook || !id) return;
    
    try {
      const formData = new FormData();
      formData.append('image', file);
      formData.append('type', 'character');
      formData.append('name', name);
      
      const response = await fetch(`${process.env.REACT_APP_API_URL}/storybooks/${id}/upload`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        },
        body: formData
      });
      
      if (!response.ok) throw new Error('Upload failed');
      
      const data = await response.json();
      
      const updatedStorybook = {
        ...storybook,
        characters: [
          ...storybook.characters,
          {
            name,
            prompt: 'Uploaded image',
            imageUrl: data.imageUrl,
            thumbnailUrl: data.thumbnailUrl
          }
        ]
      };
      await storybookAPI.update(id, updatedStorybook);
      setStorybook(updatedStorybook);
    } catch (error) {
      console.error('Failed to upload character:', error);
      throw error;
    }
  };

  const uploadBackground = async (file: File, name: string) => {
    if (!storybook || !id) return;
    
    try {
      const formData = new FormData();
      formData.append('image', file);
      formData.append('type', 'background');
      formData.append('name', name);
      
      const response = await fetch(`${process.env.REACT_APP_API_URL}/storybooks/${id}/upload`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        },
        body: formData
      });
      
      if (!response.ok) throw new Error('Upload failed');
      
      const data = await response.json();
      
      const updatedStorybook = {
        ...storybook,
        backgrounds: [
          ...(storybook.backgrounds || []),
          {
            name,
            prompt: 'Uploaded image',
            imageUrl: data.imageUrl,
            thumbnailUrl: data.thumbnailUrl
          }
        ]
      };
      await storybookAPI.update(id, updatedStorybook);
      setStorybook(updatedStorybook);
    } catch (error) {
      console.error('Failed to upload background:', error);
      throw error;
    }
  };

  const uploadScene = async (file: File, name: string) => {
    if (!storybook || !id) return;
    
    try {
      const formData = new FormData();
      formData.append('image', file);
      formData.append('type', 'scene');
      formData.append('name', name);
      
      const response = await fetch(`${process.env.REACT_APP_API_URL}/storybooks/${id}/upload`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        },
        body: formData
      });
      
      if (!response.ok) throw new Error('Upload failed');
      
      const data = await response.json();
      
      const updatedStorybook = {
        ...storybook,
        scenes: [
          ...(storybook.scenes || []),
          {
            name,
            prompt: 'Uploaded image',
            imageUrl: data.imageUrl,
            thumbnailUrl: data.thumbnailUrl,
            characterNames: []
          }
        ]
      };
      await storybookAPI.update(id, updatedStorybook);
      setStorybook(updatedStorybook);
    } catch (error) {
      console.error('Failed to upload scene:', error);
      throw error;
    }
  };

  const handleSaveImageToScene = async (imageUrl: string) => {
    if (!storybook || !id) return;
    
    let sceneName = window.prompt('Enter a name for this scene:');
    if (!sceneName) return;
    
    sceneName = sceneName.trim();
    const existingNames = (storybook.scenes || []).map(s => s.name);
    
    // Check for duplicate names
    while (existingNames.includes(sceneName)) {
      alert(`A scene named "${sceneName}" already exists. Please choose a different name.`);
      sceneName = window.prompt('Enter a different name for this scene:');
      if (!sceneName) return;
      sceneName = sceneName.trim();
    }
    
    try {
      const updatedStorybook = {
        ...storybook,
        scenes: [
          ...(storybook.scenes || []),
          {
            name: sceneName,
            prompt: 'Saved from canvas',
            imageUrl: imageUrl,
            characterNames: []
          }
        ]
      };
      await storybookAPI.update(id, updatedStorybook);
      setStorybook(updatedStorybook);
    } catch (error) {
      console.error('Failed to save scene:', error);
      throw error;
    }
  };

  const handleUpscaleImage = async (imageUrl: string, upscaleFactor: 'x2' | 'x3' | 'x4' = 'x2'): Promise<string> => {
    try {
      const result = await aiAPI.upscaleImage(imageUrl, upscaleFactor, id);
      return result.imageUrl;
    } catch (error) {
      console.error('Failed to upscale image:', error);
      throw error;
    }
  };

  // Resolution checker: update image element on a specific page
  const handleResolutionUpdateImageElement = (pageIndex: number, imageId: string, updates: Partial<ImageElement>) => {
    setStorybook(prev => {
      if (!prev) return prev;
      const updatedPages = [...prev.pages];
      const page = { ...updatedPages[pageIndex] };
      page.imageElements = page.imageElements.map(el =>
        el.id === imageId ? { ...el, ...updates } : el
      );
      updatedPages[pageIndex] = page;
      return { ...prev, pages: updatedPages };
    });
  };

  // Show image history for an element
  const handleShowImageHistory = (element: ImageElement) => {
    setImageHistoryElement(element);
    setShowImageHistory(true);
  };

  // Update gallery image (character/asset/background/scene) when URL changes (e.g., after upscale)
  const handleUpdateGalleryImage = (oldUrl: string, newUrl: string) => {
    if (!storybook) return;
    const updated: Partial<Storybook> = {};

    // Check characters
    const charIdx = storybook.characters.findIndex(c => c.imageUrl === oldUrl);
    if (charIdx !== -1) {
      const chars = [...storybook.characters];
      chars[charIdx] = { ...chars[charIdx], imageUrl: newUrl, thumbnailUrl: undefined };
      updated.characters = chars;
    }

    // Check assets
    const assetIdx = (storybook.assets || []).findIndex(a => a.imageUrl === oldUrl);
    if (assetIdx !== -1) {
      const assets = [...(storybook.assets || [])];
      assets[assetIdx] = { ...assets[assetIdx], imageUrl: newUrl, thumbnailUrl: undefined };
      updated.assets = assets;
    }

    // Check backgrounds
    const bgIdx = (storybook.backgrounds || []).findIndex(b => b.imageUrl === oldUrl);
    if (bgIdx !== -1) {
      const bgs = [...(storybook.backgrounds || [])];
      bgs[bgIdx] = { ...bgs[bgIdx], imageUrl: newUrl, thumbnailUrl: undefined };
      updated.backgrounds = bgs;
    }

    // Check scenes
    const sceneIdx = (storybook.scenes || []).findIndex(s => s.imageUrl === oldUrl);
    if (sceneIdx !== -1) {
      const scenes = [...(storybook.scenes || [])];
      scenes[sceneIdx] = { ...scenes[sceneIdx], imageUrl: newUrl, thumbnailUrl: undefined };
      updated.scenes = scenes;
    }

    if (Object.keys(updated).length > 0) {
      setStorybook(prev => prev ? { ...prev, ...updated } : prev);
      console.log('✅ Gallery image updated from', oldUrl, 'to', newUrl);
    }
  };

  // Restore a version from image history
  const handleRestoreImageVersion = (elementId: string, url: string) => {
    if (!storybook || !currentPage) return;
    const element = currentPage.imageElements.find(el => el.id === elementId);
    if (!element) return;

    const oldUrl = element.url;
    updateImageElement(elementId, {
      url,
      naturalWidth: undefined,
      naturalHeight: undefined,
      imageHistory: [
        ...(element.imageHistory || []),
        {
          action: 'replaced',
          timestamp: new Date().toISOString(),
          previousUrl: oldUrl,
          newUrl: url,
          details: 'Restored from history'
        }
      ]
    });
    // Also update gallery entry
    handleUpdateGalleryImage(oldUrl, url);
    // Update the history modal element so it re-renders
    setImageHistoryElement(prev => prev ? {
      ...prev,
      url,
      naturalWidth: undefined,
      naturalHeight: undefined,
      imageHistory: [
        ...(prev.imageHistory || []),
        {
          action: 'replaced',
          timestamp: new Date().toISOString(),
          previousUrl: oldUrl,
          newUrl: url,
          details: 'Restored from history'
        }
      ]
    } : prev);
  };

  const reorderPages = (fromIndices: number[], toIndex: number) => {
    if (!storybook || fromIndices.length === 0) return;
    const reordered = reorderBookPages(storybook, fromIndices, toIndex, currentPageIndex);
    setStorybook(reordered.storybook);
    setCurrentPageIndex(reordered.selectedIndex);
  };

  const handlePanMouseDown = (e: React.MouseEvent) => {
    // Only pan with middle mouse button or space + left click
    if (e.button === 1 || (e.button === 0 && e.shiftKey)) {
      e.preventDefault();
      setIsPanning(true);
      setPanStart({ x: e.clientX - pan.x, y: e.clientY - pan.y });
    }
  };

  const handlePanMouseMove = (e: React.MouseEvent) => {
    if (isPanning) {
      setPan({
        x: e.clientX - panStart.x,
        y: e.clientY - panStart.y
      });
    }
  };

  const handlePanMouseUp = () => {
    setIsPanning(false);
  };

  if (loading) {
    return <div className="spinner"></div>;
  }

  if (!storybook) {
    return <div>Storybook not found</div>;
  }

  const selectedFlowPages = currentPage
    ? orderedFlowPagesFor(storybook, currentPage)
    : [];
  const selectedFlowRoot = selectedFlowPages[0];
  const selectedFlowTextBoxes = editableTextBoxesForFlow(selectedFlowPages)
    .map(({ originalTextElementId: _originalTextElementId, ...textBox }) => textBox);

  return (
    <div
      ref={workspaceRef}
      className={`editor-container${isDistractionFree ? ' is-distraction-free' : ''}`}
      data-testid="editor-workspace"
    >
      {isDistractionFree && (
        <button
          ref={previewExitRef}
          type="button"
          className="preview-exit"
          data-testid="preview-exit"
          onClick={exitDistractionFree}
          aria-label="Exit distraction-free preview"
        >
          Exit Preview <span aria-hidden="true">(Esc)</span>
        </button>
      )}
      <header className="editor-header">
        <button className="btn btn-secondary" onClick={async () => {
          await saveStorybook();
          navigate('/dashboard');
        }}>
          ← Back
        </button>
        <div className="editor-banner" onClick={async () => {
          await saveStorybook();
          navigate('/');
        }} style={{ cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '8px' }}>
          <img src="/banner-icon.png" alt="Tiny Little Fox" style={{ width: '24px', height: '24px' }} />
          <span style={{ fontSize: '14px', fontWeight: 600 }}>Tiny Little Fox</span>
        </div>
        {isEditingTitle ? (
          <input
            type="text"
            className="title-input"
            value={editedTitle}
            onChange={handleTitleChange}
            onBlur={handleTitleBlur}
            onKeyDown={handleTitleKeyDown}
            autoFocus
            style={{
              fontSize: '24px',
              fontWeight: 'normal',
              border: '2px solid #007bff',
              borderRadius: '4px',
              padding: '4px 8px',
              outline: 'none',
              flex: 1,
              maxWidth: '400px'
            }}
          />
        ) : (
          <h1 
            onClick={handleTitleClick}
            style={{ 
              cursor: 'pointer',
              padding: '4px 8px',
              borderRadius: '4px',
              transition: 'background-color 0.2s'
            }}
            onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#f3f4f6'}
            onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
            title="Click to edit title"
          >
            {storybook.title}
          </h1>
        )}
        <div className="header-actions">
          <span className="save-status">
            {saveConflict
              ? '⚠ Save conflict'
              : saving ? 'Saving…' : lastSaved ? `✓ Saved ${formatTimeSince(lastSaved)}` : '✓ Saved'}
          </span>
          {saveConflict && (
            <span role="alert" data-testid="editor-save-conflict" className="save-conflict-notice">
              {saveConflict.message}
              <button
                type="button"
                className="btn btn-secondary"
                onClick={() => {
                  clearSaveConflict();
                  void loadStorybook();
                }}
              >
                Reload latest
              </button>
            </span>
          )}
          <button className="btn btn-primary" onClick={saveStorybook} disabled={saving}>
            Save Now
          </button>
          {storybook.quickCreate && (
            <button
              className="btn btn-secondary"
              data-testid="open-quick-create"
              onClick={() => {
                setQuickCreateError(null);
                setShowQuickCreate(true);
              }}
              style={{ marginLeft: '8px' }}
            >
              ✦ Quick Create
            </button>
          )}
          <button
            className="btn btn-secondary"
            data-testid="open-book-review"
            onClick={() => void openBookReview()}
            disabled={!storybook || bookReviewLoading}
            style={{ marginLeft: '8px' }}
          >
            {bookReviewLoading ? 'Reviewing…' : 'Review Book'}
          </button>
          <button
            className="btn btn-secondary"
            data-testid="layout-check"
            onClick={() => void handleRunLayoutCheck()}
            disabled={!storybook || isRunningPreflight}
            style={{ marginLeft: '8px' }}
          >
            {isRunningPreflight ? 'Checking…' : '✓ Layout Check'}
          </button>
          <button
            className="btn btn-secondary"
            data-testid="export-pdf"
            onClick={() => void handleOpenPdfExport()}
            disabled={!storybook || isRunningPreflight || currentPreflightSummary.hasErrors}
            title={currentPreflightSummary.hasErrors
              ? 'Fix the blocking Layout Check issues before exporting.'
              : 'Export this storybook as PDF'}
            style={{ marginLeft: '8px' }}
          >
            Export PDF
          </button>
          <button 
            className="btn btn-hint"
            onClick={() => setShowTutorial(true)}
            title="Show Tutorial"
            style={{
              background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
              color: 'white',
              border: 'none',
              padding: '8px 16px',
              borderRadius: '8px',
              cursor: 'pointer',
              fontSize: '14px',
              fontWeight: 600,
              display: 'flex',
              alignItems: 'center',
              gap: '6px',
              transition: 'all 0.2s ease',
              boxShadow: '0 2px 8px rgba(102, 126, 234, 0.3)',
              marginLeft: '8px'
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.transform = 'translateY(-2px)';
              e.currentTarget.style.boxShadow = '0 4px 12px rgba(102, 126, 234, 0.4)';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.transform = 'translateY(0)';
              e.currentTarget.style.boxShadow = '0 2px 8px rgba(102, 126, 234, 0.3)';
            }}
          >
            Tutorial
          </button>
        </div>
      </header>

      <div
        className="editor-main"
        style={!isNarrowViewport && !isDistractionFree ? {
          gridTemplateColumns: `${pageStripExpanded ? pageStripWidth : 0}px ${pageStripExpanded ? 6 : 0}px minmax(0, 1fr) ${inspectorExpanded ? 6 : 0}px ${inspectorExpanded ? inspectorWidth : 0}px`
        } : undefined}
      >
        {isNarrowViewport && !isDistractionFree && (pageStripExpanded || inspectorExpanded) && (
          <button
            type="button"
            className="workspace-drawer-backdrop"
            data-testid="workspace-drawer-backdrop"
            aria-label={`Close ${pageStripExpanded ? 'pages' : 'inspector'} drawer`}
            onClick={() => closeResponsivePanel(pageStripExpanded ? 'pages' : 'inspector')}
          />
        )}
        <aside
          id="editor-page-strip"
          className={`workspace-page-strip${pageStripExpanded ? ' is-expanded' : ' is-collapsed'}`}
          data-testid="page-strip"
          aria-hidden={!pageStripExpanded}
        >
          <div className="workspace-drawer-header">
            <strong>Pages</strong>
            <button
              ref={pageDrawerCloseRef}
              type="button"
              className="workspace-drawer-close"
              data-testid="page-drawer-close"
              aria-label="Close pages drawer"
              onClick={() => closeResponsivePanel('pages')}
            >
              <span aria-hidden="true">×</span>
            </button>
          </div>
          <PagesList
          pages={storybook.pages}
          currentPageIndex={currentPageIndex}
          preflightIssues={currentPreflightIssues}
          preflightCheckedPageIds={currentPreflightCheckedPageIds}
          onOpenPageIssues={handleOpenPagePreflight}
          pageAspectRatio={(() => {
            const size = CANVAS_SIZES.find(entry => entry.value === (storybook.canvasSize || '5.5x8.5'))
              || CANVAS_SIZES[3];
            return size.width / size.height;
          })()}
          onPageSelect={handlePageSelect}
          onPageReorder={reorderPages}
          onAddPage={addPage}
          onDeletePage={deletePage}
          onOpenPlots={async () => {
            // Refresh user data to get latest token counts before opening plot modal
            await refreshUser();
            setPlotModalSinglePageIndex(null); // Ensure full modal mode
            setShowPlotModal(true);
          }}
          />
        </aside>

        <div
          className="workspace-resizer workspace-resizer-pages"
          data-testid="page-strip-resizer"
          role="separator"
          aria-label="Resize page strip"
          aria-orientation="vertical"
          aria-valuemin={144}
          aria-valuemax={320}
          aria-valuenow={Math.round(pageStripWidth)}
          tabIndex={0}
          hidden={!pageStripExpanded || isNarrowViewport || isDistractionFree}
          onMouseDown={(event) => beginPanelResize('pages', event)}
          onKeyDown={(event) => handlePanelResizeKeyDown('pages', event)}
        />

        <div className="editor-center">
          <StoryInfo
            storyType={storybook.storyType || 'Fairy Tale'}
            drawingType={storybook.drawingType || 'Digital Art'}
            drawingStyle={storybook.drawingStyle || 'Children\'s Book Illustration'}
            storyTone={storybook.storyTone || 'Playful'}
            canvasSize={storybook.canvasSize || '5.5x8.5'}
            backgroundColor={currentPage?.backgroundColor || '#ffffff'}
            customDrawingStyles={customDrawingStyles}
            onUpdate={updateStoryInfo}
            onOpenStyleModal={() => setShowStyleModal(true)}
            onOpenFontModal={() => setShowFontModal(true)}
            onOpenDesignModal={() => setShowDesignModal(true)}
            showGrid={showGrid}
            onToggleGrid={() => setShowGrid(!showGrid)}
            showPreviousPageOverlay={showPreviousPageOverlay}
            onTogglePreviousPageOverlay={() => setShowPreviousPageOverlay(!showPreviousPageOverlay)}
            previousPageOpacity={previousPageOpacity}
            onPreviousPageOpacityChange={setPreviousPageOpacity}
            hasPreviousPage={currentPageIndex > 0}
          />
          
          <Toolbar
            onAddText={addTextElement}
            onAddTextBox={addTextBoxElement}
            onAddPage={addPage}
            onDeletePage={() => deletePage(currentPageIndex)}
            canDeletePage={storybook.pages.length > 3 && !currentPage?.isSystemPage}
            onRegeneratePage={() => {
              // Open plot modal in single page mode for current page
              // Calculate content page index (0-indexed among non-system pages only)
              const contentPages = storybook.pages.filter(p => !p.isSystemPage);
              const contentPageIndex = contentPages.findIndex(p => p.id === currentPage?.id);
              setPlotModalSinglePageIndex(contentPageIndex >= 0 ? contentPageIndex : null);
              setShowPlotModal(true);
            }}
            canRegeneratePage={(() => {
              // Can regenerate/add prompt for any non-system page if user has API access
              if (currentPage?.isSystemPage) return false;
              return true; // Allow opening PlotModal to add or edit page prompts
            })()}
            onApplySafeLayout={handleApplySafeLayoutToCurrentPage}
            onApplyPageTemplate={handleApplySafeLayoutToCurrentPage}
            canApplySafeLayout={Boolean(
              currentPage
              && !currentPage.isSystemPage
              && currentPage.textElements.length > 0
              && currentPage.imageElements.length <= 1
              && (currentPage.imageElements.length === 0 || isFullSceneIllustration(
                  currentPage.imageElements[0],
                  CANVAS_SIZES.find(size => size.value === (storybook.canvasSize || '5.5x8.5'))
                    || CANVAS_SIZES[3]
                ) || Boolean(
                  currentPage.layout?.version === PAGE_LAYOUT_VERSION
                  && currentPage.layout.template !== 'custom'
                  && currentPage.imageElements[0].layoutRole === 'illustration'
                  && currentPage.imageElements[0].fit === 'cover'
                ))
              && currentPage.textElements.every(text => (
                !text.styleId && (text.borderWidth || 0) === 0
              ))
            )}
            isApplyingSafeLayout={isApplyingSafeLayout}
            selectedElement={selectedElement}
            currentPage={currentPage}
            onDeleteElement={() => selectedElement && deleteElement(selectedElement)}
            onUpdateTextElement={updateTextElement}
            onUpdateImageElement={updateImageElement}
            onCropImage={handleCropImage}
            onRephraseText={handleRephraseText}
            isRephrasing={isRephrasing}
            onApplyTextStyle={handleApplyTextStyle}
            onSaveImageToScene={handleSaveImageToScene}
            onUpscaleImage={handleUpscaleImage}
            onCheckResolution={() => setShowResolutionChecker(true)}
            existingSceneNames={(storybook.scenes || []).map(s => s.name)}
          />

          <div className="zoom-controls">
            <button
              ref={pageStripToggleRef}
              type="button"
              className="workspace-panel-toggle"
              data-testid="page-strip-toggle"
              aria-label={pageStripExpanded ? 'Collapse page strip' : 'Expand page strip'}
              aria-controls="editor-page-strip"
              aria-expanded={pageStripExpanded}
              onClick={togglePageStrip}
            >
              <span className="workspace-control-label">Pages</span>
            </button>
            <button
              ref={inspectorToggleRef}
              type="button"
              className="workspace-panel-toggle"
              data-testid="inspector-toggle"
              aria-label={inspectorExpanded ? 'Collapse inspector' : 'Expand inspector'}
              aria-controls="editor-inspector"
              aria-expanded={inspectorExpanded}
              onClick={toggleInspector}
            >
              <span className="workspace-control-label">Inspector</span>
            </button>
            <span className="workspace-control-separator" aria-hidden="true" />
            <button 
              onClick={() => {
                setZoomMode('manual');
                setZoom(Math.max(0.1, effectiveZoom - 0.1));
              }}
              disabled={effectiveZoom <= 0.1}
              title="Zoom Out"
              aria-label="Zoom out"
            >
              −
            </button>
            <span className="zoom-value" aria-live="polite">{Math.round(effectiveZoom * 100)}%</span>
            <button 
              onClick={() => {
                setZoomMode('manual');
                setZoom(Math.min(2, effectiveZoom + 0.1));
              }}
              disabled={effectiveZoom >= 2}
              title="Zoom In"
              aria-label="Zoom in"
            >
              +
            </button>
            <button
              type="button"
              className="fit-page-control"
              data-testid="fit-page"
              onClick={fitPage}
              aria-label="Fit complete page in workspace"
              title="Fit the complete page in the available workspace"
            >
              Fit Page
            </button>
            <button
              ref={previewTriggerRef}
              type="button"
              className="preview-control"
              data-testid="distraction-free-preview"
              onClick={enterDistractionFree}
              aria-label="Open distraction-free preview"
              title="Preview the page without editor panels"
            >
              Preview
            </button>
            <span className="pan-hint">Hold Shift + Drag to pan</span>
          </div>

          <div 
            ref={canvasStageRef}
            className="editor-content canvas-stage"
            data-testid="canvas-stage"
            style={{ cursor: isPanning ? 'grabbing' : (zoomMode === 'manual' ? 'grab' : 'default') }}
            onMouseDown={handlePanMouseDown}
            onMouseMove={handlePanMouseMove}
            onMouseUp={handlePanMouseUp}
            onMouseLeave={handlePanMouseUp}
            onClick={(e) => {
              if (!(e.target as HTMLElement).closest('[data-testid="canonical-page-editor"]')) {
                setSelectedElement(null);
              }
            }}
          >
            <div
              className="canvas-stage-scroll-area"
              style={{
                width: workspaceCanvasSize.width * effectiveZoom + 48,
                height: workspaceCanvasSize.height * effectiveZoom + 48
              }}
            >
              <div
                className="canvas-fit-frame"
                style={{
                  width: workspaceCanvasSize.width * effectiveZoom,
                  height: workspaceCanvasSize.height * effectiveZoom
                }}
              >
                <div
                  ref={canvasContainerRef}
                  className="canvas-transform"
                  style={{
                    width: workspaceCanvasSize.width,
                    height: workspaceCanvasSize.height,
                    transform: `translate(${pan.x}px, ${pan.y}px) scale(${effectiveZoom})`,
                    transformOrigin: 'top left',
                    '--canvas-editor-inverse-zoom': String(1 / Math.max(0.1, effectiveZoom))
                  } as React.CSSProperties}
                >
                  <Canvas
                    page={currentPage!}
                    canvasSize={storybook.canvasSize || '5.5x8.5'}
                    selectedElement={selectedElement}
                    onSelectElement={setSelectedElement}
                    onUpdateTextElement={updateTextElement}
                    onUpdateImageElement={updateImageElement}
                    onUpdatePage={(updates) => updateCurrentPage({ ...currentPage!, ...updates })}
                    onAddImage={addImageElement}
                    onCopyElement={handleCopyElement}
                    onPasteElement={handlePasteElement}
                    onBringToFront={bringToFront}
                    onSendToBack={sendToBack}
                    showGrid={showGrid}
                    showPreviousPageOverlay={showPreviousPageOverlay}
                    previousPage={currentPageIndex > 0 ? storybook.pages[currentPageIndex - 1] : undefined}
                    previousPageOpacity={previousPageOpacity}
                    safeMarginInches={storybook.printSettings?.safeMarginInches ?? 0.25}
                    isPreviewMode={isDistractionFree}
                  />
                </div>
              </div>
            </div>
          </div>
        </div>

        <div
          className="workspace-resizer workspace-resizer-inspector"
          data-testid="inspector-resizer"
          role="separator"
          aria-label="Resize inspector"
          aria-orientation="vertical"
          aria-valuemin={280}
          aria-valuemax={520}
          aria-valuenow={Math.round(inspectorWidth)}
          tabIndex={0}
          hidden={!inspectorExpanded || isNarrowViewport || isDistractionFree}
          onMouseDown={(event) => beginPanelResize('inspector', event)}
          onKeyDown={(event) => handlePanelResizeKeyDown('inspector', event)}
        />

        <aside
          id="editor-inspector"
          className={`workspace-inspector${inspectorExpanded ? ' is-expanded' : ' is-collapsed'}`}
          data-testid="editor-inspector"
          aria-hidden={!inspectorExpanded}
          style={isNarrowViewport ? { width: Math.min(inspectorWidth, 420) } : undefined}
        >
          <div className="workspace-drawer-header">
            <strong>Inspector</strong>
            <button
              ref={inspectorDrawerCloseRef}
              type="button"
              className="workspace-drawer-close"
              data-testid="inspector-drawer-close"
              aria-label="Close inspector drawer"
              onClick={() => closeResponsivePanel('inspector')}
            >
              <span aria-hidden="true">×</span>
            </button>
          </div>
          <Sidebar
            storybook={storybook}
            onAddImage={addImageElement}
            onGenerateCharacter={generateCharacter}
            onGenerateBackground={generateBackground}
            onGenerateScene={generateScene}
            onGenerateAsset={generateAsset}
            onDeleteAsset={deleteAsset}
            onDeleteCharacter={deleteCharacter}
            onDeleteBackground={deleteBackground}
            onDeleteScene={deleteScene}
            onUploadAsset={uploadAsset}
            onUploadCharacter={uploadCharacter}
            onUploadBackground={uploadBackground}
            onUploadScene={uploadScene}
            onRefreshStorybook={loadStorybook}
            hasApiAccess={user?.hasApiAccess || false}
            selectedElement={selectedElement}
            currentPage={currentPage}
            onUpdateTextElement={updateTextElement}
            onUpdateImageElement={updateImageElement}
            onDeleteElement={() => selectedElement && deleteElement(selectedElement)}
            onCropImage={handleCropImage}
            onRephraseText={handleRephraseText}
            isRephrasing={isRephrasing}
            onSaveImageToScene={handleSaveImageToScene}
            onUpscaleImage={handleUpscaleImage}
            onShowImageHistory={handleShowImageHistory}
            onUpdateGalleryImage={handleUpdateGalleryImage}
            onUpdateCharacterHeight={handleUpdateCharacterHeight}
            onEditImage={editImage}
            onBringToFront={bringToFront}
            onSendToBack={sendToBack}
          />
        </aside>
      </div>

      {showQuickCreate && storybook.quickCreate && (
        <QuickCreateWorkflow
          storybook={storybook}
          state={storybook.quickCreate}
          availableTokens={user?.totalTokens ?? (
            user?.monthlyCredits !== undefined || user?.paidTokens !== undefined
              ? (user?.monthlyCredits || 0) + (user?.paidTokens || 0)
              : undefined
          )}
          preflight={{
            checked: storybook.pages.length > 0
              && currentPreflightCheckedPageIds.size === storybook.pages.length
              && quickCreateExportPreflight?.bookRevision === storybook.revision,
            errors: currentQuickCreatePreflightCounts.errors,
            warnings: currentQuickCreatePreflightCounts.warnings
          }}
          identityReadiness={identityReadinessForBook(storybook)}
          busyAction={quickCreateBusyAction}
          error={quickCreateError}
          onSelectStage={handleQuickSelectStage}
          onApproveBrief={handleQuickApproveBrief}
          onGenerateOutline={handleQuickGenerateOutline}
          onApproveOutline={handleQuickApproveOutline}
          onApproveCastStyle={handleQuickApproveCastStyle}
          onPrepareIdentity={handleQuickPrepareIdentity}
          onApproveIdentity={handleQuickApproveIdentity}
          onUpdateComposition={handleQuickUpdateComposition}
          resolveImageUrl={getImageUrl}
          onAnalyzeReferenceBook={handleQuickAnalyzeReferenceBook}
          onUseSeriesTemplate={handleQuickUseSeriesTemplate}
          onClearReferenceBook={handleQuickClearReferenceBook}
          onGenerateDraft={handleQuickGenerateDraft}
          onRunReview={handleQuickRunReview}
          onApproveReview={handleQuickApproveReview}
          onExport={() => {
            closeQuickCreate();
            void handleOpenPdfExport();
          }}
          onAdvancedEditor={closeQuickCreate}
          onClose={closeQuickCreate}
          draftJobPanel={generationJob ? (
            <GenerationJobPanel
              job={generationJob}
              connectionState={generationConnection}
              pollIntervalSeconds={4}
              actionInProgress={generationJobAction}
              onRetryFailed={pageIds => runGenerationJobAction('retry', pageIds)}
              onPause={() => runGenerationJobAction('pause')}
              onResume={() => runGenerationJobAction('resume')}
              onCancel={() => runGenerationJobAction('cancel')}
              onOpenPage={openGeneratedPage}
            />
          ) : undefined}
        />
      )}

      {showBookReview && (
        <div className="book-review-editor-overlay" data-testid="book-review-overlay">
          {bookReview ? (
            <BookReviewDashboard
              review={bookReview}
              canvasSize={storybook.canvasSize || bookReview.canvasSize || '5.5x8.5'}
              characterValidationAvailable={true}
              resolveImageUrl={getImageUrl}
              onOpenPage={pageId => {
                const pageIndex = storybook.pages.findIndex(page => page.id === pageId);
                if (pageIndex >= 0) setCurrentPageIndex(pageIndex);
                setShowBookReview(false);
              }}
              onRegenerateLayer={handleReviewRegenerate}
              onSetLayerLock={handleReviewLayerLock}
              onFixAllSafeIssues={handleFixAllSafeIssues}
              onLoadHistory={handleLoadReviewHistory}
              onRestoreRevision={handleRestoreReviewRevision}
              onRefresh={async () => { await refreshBookReview(); }}
              onClose={() => setShowBookReview(false)}
            />
          ) : (
            <div className="book-review-editor-state" role={bookReviewError ? 'alert' : 'status'}>
              <div>
                <strong>{bookReviewError ? 'Book review is unavailable' : 'Checking every page…'}</strong>
                <p>{bookReviewError || 'Preparing page, character, layout, text, DPI, and job checks.'}</p>
                {bookReviewError && (
                  <button type="button" className="btn btn-primary" onClick={() => void refreshBookReview()}>
                    Try again
                  </button>
                )}
              </div>
            </div>
          )}
        </div>
      )}

      {showCropModal && cropImageId && (() => {
        const imageElement = currentPage?.imageElements.find(el => el.id === cropImageId);
        if (!imageElement) return null;
        
        // Determine image type and original name
        let imageType = 'asset';
        let originalName = 'image';
        
        if (imageElement.characterName) {
          const character = storybook?.characters.find(c => c.name === imageElement.characterName);
          const scene = storybook?.scenes.find(s => s.imageUrl === imageElement.url);
          const background = storybook?.backgrounds.find(b => b.imageUrl === imageElement.url);
          
          if (character) {
            imageType = 'character';
            originalName = character.name;
          } else if (scene) {
            imageType = 'scene';
            originalName = scene.name;
          } else if (background) {
            imageType = 'background';
            originalName = background.name;
          }
        }
        
        return (
          <CropModal
            imageUrl={getImageUrl(imageElement.url)}
            onCrop={handleCropComplete}
            onClose={() => {
              setShowCropModal(false);
              setCropImageId(null);
            }}
            imageType={imageType}
            originalName={originalName}
          />
        );
      })()}

      {showPlotModal && (
        <PlotModal
          onClose={() => {
            setShowPlotModal(false);
            setPlotModalSinglePageIndex(null);
          }}
          onApply={handleApplyPlots}
          singlePageIndex={plotModalSinglePageIndex}
          // Pass content page number for display (1-indexed among content pages only, not including title cover)
          singlePageNumber={plotModalSinglePageIndex !== null ? plotModalSinglePageIndex + 1 : undefined}
          // Pass current page's AI data for regeneration (persists through reordering)
          currentPageAiData={selectedFlowRoot ? {
            scenePrompt: selectedFlowRoot.aiScenePrompt,
            characterNames: selectedFlowRoot.aiCharacterNames,
            backgroundName: selectedFlowRoot.aiBackgroundName,
            textBoxes: selectedFlowTextBoxes,
          } : undefined}
          onApplyStructuredPlot={handleApplyStructuredPlot}
          onGeneratePage={(pageData, generationOptions) => {
            const rootContentPages = storybook.pages.filter(page => (
              !page.isSystemPage && (page.layout?.continuationIndex ?? 0) === 0
            ));
            const stableTargetId = plotModalSinglePageIndex !== null
              ? selectedFlowRoot?.id
              : rootContentPages[(pageData.pageNumber || 1) - 1]?.id;
            return handleGeneratePage(pageData, {
              ...generationOptions,
              targetPageId: stableTargetId
            });
          }}
          onGenerateAllPages={handleGenerateAllPages}
          onGenerateCharacter={async (name, prompt, options) => {
            if (options?.replaceExisting) {
              // Regenerate - generate new image and update existing character
              return await regenerateCharacter(name, prompt);
            } else {
              return await generateCharacter(name, prompt, [], 'gemini', 'transparent', 'white');
            }
          }}
          onGenerateBackground={async (name, prompt, options) => {
            if (options?.replaceExisting) {
              // Regenerate - generate new image and update existing background
              return await regenerateBackground(name, prompt);
            } else {
              return await generateBackground(name, prompt, [], 'gemini');
            }
          }}
          hasApiAccess={user?.hasApiAccess || false}
          storyType={storybook.storyType}
          storyTone={storybook.storyTone}
          storyStylePrompt={storybook.storyStylePrompt}
          visualStylePrompt={storybook.visualStylePrompt}
          dialogueStyle={storybook.dialogueStyle}
          drawingType={storybook.drawingType}
          drawingStyle={storybook.drawingStyle}
          description={storybook.description}
          lastStoryPrompt={storybook.lastStoryPrompt}
          onSaveStoryPrompt={(prompt) => {
            setStorybook(prev => prev ? { ...prev, lastStoryPrompt: prompt } : prev);
          }}
          existingPages={storybook.pages.filter(p => !p.isSystemPage).length}
          savedPlots={savedPlots}
          structuredPlotData={structuredPlotData}
          currentPageIndex={currentPageIndex}
          onAddToPage={handleAddPlotToPage}
          canvasWidth={CANVAS_SIZES.find(s => s.value === storybook.canvasSize)?.width || 800}
          canvasHeight={CANVAS_SIZES.find(s => s.value === storybook.canvasSize)?.height || 500}
          defaultFontStyle={fontSettingsFontStyle}
          onFontStyleChange={(style) => setFontSettingsFontStyle(style)}
          onOpenFontSettings={() => setShowFontModal(true)}
          onOpenStyleSettings={() => setShowStyleModal(true)}
          onOpenDesignSettings={() => setShowDesignModal(true)}
          backgrounds={storybook.backgrounds || []}
          onUpdateStructuredPlot={(updatedData) => setStructuredPlotData(updatedData)}
          user={user}
          existingCharacters={storybook.characters || []}
          existingBackgrounds={storybook.backgrounds || []}
          onSavePageAiData={(aiData) => {
            // Save AI data to the current page
            if (!storybook || currentPageIndex < 0) return;
            const updatedPages = [...storybook.pages];
            updatedPages[currentPageIndex] = {
              ...updatedPages[currentPageIndex],
              aiScenePrompt: aiData.scenePrompt,
              aiCharacterNames: aiData.characterNames,
              aiBackgroundName: aiData.backgroundName,
              aiTextBoxes: aiData.textBoxes
            };
            setStorybook({ ...storybook, pages: updatedPages });
          }}
        />
      )}

      <PageGenerationProgress 
        steps={progressSteps} 
        isOpen={showProgressModal}
        onClose={generationDomain.resetProgress}
        onCancel={handleCancelGeneration}
        isCancelling={isCancellingGeneration}
        batchProgress={batchProgress}
        batchSummary={batchSummary}
        currentPageNumber={generatingPageNumber}
      />

      {showStyleModal && (() => {
        console.log('=== STORY SETTINGS MODAL PROPS ===');
        console.log('storyStylePrompt:', storybook.storyStylePrompt);
        console.log('visualStylePrompt:', storybook.visualStylePrompt);
        console.log('storyType:', storybook.storyType);
        console.log('storyTone:', storybook.storyTone);
        console.log('drawingType:', storybook.drawingType);
        console.log('drawingStyle:', storybook.drawingStyle);
        console.log('useReferenceBook:', storybook.useReferenceBook);
        console.log('referenceBook:', storybook.referenceBook);
        console.log('dialogueStyle:', storybook.dialogueStyle);
        return (
          <StylePromptsModal
            isOpen={showStyleModal}
            onClose={() => setShowStyleModal(false)}
            onSave={handleStylePromptsSave}
            currentStoryStylePrompt={storybook.storyStylePrompt}
            currentVisualStylePrompt={storybook.visualStylePrompt}
            currentStoryType={storybook.storyType}
            currentStoryTone={storybook.storyTone}
            currentDrawingMedium={storybook.drawingType}
            currentArtStyle={storybook.drawingStyle}
            currentUseReferenceBook={storybook.useReferenceBook}
            currentReferenceBook={storybook.referenceBook}
            currentDialogueStyle={storybook.dialogueStyle}
          />
        );
      })()}

      {showDesignModal && (
        <DesignSettingsModal
          isOpen={showDesignModal}
          onClose={() => setShowDesignModal(false)}
          onSave={handleDesignSettingsSave}
          currentAutoCropSettings={storybook.autoCropSettings}
          currentDesignStyle={storybook.designStyle}
          currentDialogueBoxStyle={storybook.dialogueBoxStyle}
        />
      )}

      {showRephraseOptionsModal && (
        <div className="modal-overlay" onClick={closeRephraseOptionsModal}>
          <div
            className="modal-content"
            onClick={(e) => e.stopPropagation()}
            style={{ maxWidth: '760px', maxHeight: '85vh', overflow: 'auto' }}
          >
            <h2 style={{ marginBottom: '8px' }}>AI Improve Text</h2>
            <p style={{ fontSize: '0.9em', color: '#666', marginBottom: '12px' }}>
              Pick one option, regenerate another set of 5, or cancel.
            </p>
            <div style={{ background: '#f7f7f7', borderRadius: '8px', padding: '10px 12px', marginBottom: '12px' }}>
              <div style={{ fontSize: '12px', color: '#777', marginBottom: '4px' }}>Original</div>
              <div style={{ fontSize: '14px', lineHeight: 1.45 }}>{rephraseOriginalText}</div>
            </div>

            <div style={{ display: 'grid', gap: '10px' }}>
              {isRephrasing && (
                <div style={{ border: '1px solid #e5e5e5', borderRadius: '8px', padding: '12px', color: '#666' }}>
                  Generating 5 options...
                </div>
              )}

              {!isRephrasing && rephraseOptionsError && (
                <div style={{ border: '1px solid #f3c2c2', background: '#fff5f5', borderRadius: '8px', padding: '12px', color: '#b42318' }}>
                  {rephraseOptionsError}
                </div>
              )}

              {!isRephrasing && !rephraseOptionsError && rephraseOptions.map((option, idx) => (
                <div key={`${idx}-${option.slice(0, 24)}`} style={{ border: '1px solid #e5e5e5', borderRadius: '8px', padding: '10px' }}>
                  <div style={{ fontSize: '12px', color: '#777', marginBottom: '6px' }}>Option {idx + 1}</div>
                  <div style={{ fontSize: '14px', lineHeight: 1.45, marginBottom: '8px' }}>{option}</div>
                  <button
                    className="btn btn-primary btn-small"
                    onClick={() => handleApplyRephraseOption(option)}
                  >
                    Use This Option
                  </button>
                </div>
              ))}
            </div>

            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '8px', marginTop: '14px' }}>
              <button className="btn btn-secondary" onClick={closeRephraseOptionsModal}>
                Cancel
              </button>
              <button className="btn btn-primary" onClick={handleRegenerateRephraseOptions} disabled={isRephrasing}>
                {isRephrasing ? 'Generating...' : 'Regenerate Next 5'}
              </button>
            </div>
          </div>
        </div>
      )}

      {showFontModal && (
        <div className="modal-overlay" onClick={() => setShowFontModal(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()} style={{ maxWidth: '520px', maxHeight: '90vh', overflow: 'auto' }}>
            <h2 style={{ marginBottom: '8px' }}>Font Settings</h2>
            <p style={{ fontSize: '0.85em', color: '#666', marginBottom: '16px' }}>
              Default properties for new text elements
            </p>
            <form onSubmit={(e) => { e.preventDefault(); handleFontSettingsSave(); }}>
              {/* Row 1: Font Size, Font Family */}
              <div style={{ display: 'grid', gridTemplateColumns: '100px 1fr', gap: '12px', marginBottom: '12px' }}>
                <div className="form-group" style={{ margin: 0 }}>
                  <label style={{ fontSize: '12px', marginBottom: '4px' }}>Size</label>
                  <input
                    type="number"
                    value={fontSettingsFontSize}
                    onChange={(e) => setFontSettingsFontSize(parseInt(e.target.value))}
                    min="8"
                    max="120"
                    required
                    style={{ padding: '6px 8px' }}
                  />
                </div>
                <div className="form-group" style={{ margin: 0 }}>
                  <label style={{ fontSize: '12px', marginBottom: '4px' }}>Font Family</label>
                  <select
                    value={fontSettingsFontFamily}
                    onChange={(e) => setFontSettingsFontFamily(e.target.value)}
                    style={{ fontFamily: fontSettingsFontFamily, padding: '6px 8px' }}
                  >
                    <optgroup label="Sans Serif">
                      <option value="Roboto" style={{ fontFamily: 'Roboto' }}>Roboto</option>
                      <option value="Open Sans" style={{ fontFamily: 'Open Sans' }}>Open Sans</option>
                      <option value="Lato" style={{ fontFamily: 'Lato' }}>Lato</option>
                      <option value="Montserrat" style={{ fontFamily: 'Montserrat' }}>Montserrat</option>
                      <option value="Raleway" style={{ fontFamily: 'Raleway' }}>Raleway</option>
                      <option value="Ubuntu" style={{ fontFamily: 'Ubuntu' }}>Ubuntu</option>
                      <option value="Oswald" style={{ fontFamily: 'Oswald' }}>Oswald</option>
                    </optgroup>
                    <optgroup label="Serif">
                      <option value="Merriweather" style={{ fontFamily: 'Merriweather' }}>Merriweather</option>
                      <option value="Playfair Display" style={{ fontFamily: 'Playfair Display' }}>Playfair Display</option>
                    </optgroup>
                    <optgroup label="Handwriting & Fun">
                      <option value="Patrick Hand" style={{ fontFamily: 'Patrick Hand' }}>Patrick Hand</option>
                      <option value="Indie Flower" style={{ fontFamily: 'Indie Flower' }}>Indie Flower</option>
                      <option value="Shadows Into Light" style={{ fontFamily: 'Shadows Into Light' }}>Shadows Into Light</option>
                      <option value="Caveat" style={{ fontFamily: 'Caveat' }}>Caveat</option>
                      <option value="Architects Daughter" style={{ fontFamily: 'Architects Daughter' }}>Architects Daughter</option>
                      <option value="Kalam" style={{ fontFamily: 'Kalam' }}>Kalam</option>
                      <option value="Permanent Marker" style={{ fontFamily: 'Permanent Marker' }}>Permanent Marker</option>
                    </optgroup>
                    <optgroup label="Elegant & Decorative">
                      <option value="Dancing Script" style={{ fontFamily: 'Dancing Script' }}>Dancing Script</option>
                      <option value="Pacifico" style={{ fontFamily: 'Pacifico' }}>Pacifico</option>
                    </optgroup>
                    <optgroup label="Bold & Strong">
                      <option value="Bebas Neue" style={{ fontFamily: 'Bebas Neue' }}>Bebas Neue</option>
                      <option value="Bangers" style={{ fontFamily: 'Bangers' }}>Bangers</option>
                      <option value="Fredoka One" style={{ fontFamily: 'Fredoka One' }}>Fredoka One</option>
                      <option value="Righteous" style={{ fontFamily: 'Righteous' }}>Righteous</option>
                      <option value="Audiowide" style={{ fontFamily: 'Audiowide' }}>Audiowide</option>
                      <option value="Press Start 2P" style={{ fontFamily: 'Press Start 2P' }}>Press Start 2P</option>
                    </optgroup>
                  </select>
                </div>
              </div>

              {/* Row 2: Font Weight, Text Color */}
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px', marginBottom: '12px' }}>
                <div className="form-group" style={{ margin: 0 }}>
                  <label style={{ fontSize: '12px', marginBottom: '4px' }}>Weight</label>
                  <select
                    value={fontSettingsFontWeight}
                    onChange={(e) => setFontSettingsFontWeight(e.target.value)}
                    style={{ padding: '6px 8px' }}
                  >
                    <option value="300">Light</option>
                    <option value="normal">Normal</option>
                    <option value="500">Medium</option>
                    <option value="600">Semi Bold</option>
                    <option value="bold">Bold</option>
                    <option value="800">Extra Bold</option>
                  </select>
                </div>
                <div className="form-group" style={{ margin: 0 }}>
                  <label style={{ fontSize: '12px', marginBottom: '4px' }}>Color</label>
                  <input
                    type="color"
                    value={fontSettingsColor}
                    onChange={(e) => setFontSettingsColor(e.target.value)}
                    style={{ height: '34px', padding: '2px' }}
                  />
                </div>
              </div>

              {/* Row 3: Font Style, Text Decoration */}
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px', marginBottom: '12px' }}>
                <div className="form-group" style={{ margin: 0 }}>
                  <label style={{ fontSize: '12px', marginBottom: '4px' }}>Style</label>
                  <select
                    value={fontSettingsFontStyle}
                    onChange={(e) => setFontSettingsFontStyle(e.target.value as 'normal' | 'italic')}
                    style={{ padding: '6px 8px' }}
                  >
                    <option value="normal">Normal</option>
                    <option value="italic">Italic</option>
                  </select>
                </div>
                <div className="form-group" style={{ margin: 0 }}>
                  <label style={{ fontSize: '12px', marginBottom: '4px' }}>Decoration</label>
                  <select
                    value={fontSettingsTextDecoration}
                    onChange={(e) => setFontSettingsTextDecoration(e.target.value as 'none' | 'underline' | 'line-through' | 'underline line-through')}
                    style={{ padding: '6px 8px' }}
                  >
                    <option value="none">None</option>
                    <option value="underline">Underline</option>
                    <option value="line-through">Strikethrough</option>
                  </select>
                </div>
              </div>

              {/* Row 4: Text Align, Vertical Align */}
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px', marginBottom: '16px' }}>
                <div className="form-group" style={{ margin: 0 }}>
                  <label style={{ fontSize: '12px', marginBottom: '4px' }}>Text Align</label>
                  <select
                    value={fontSettingsTextAlign}
                    onChange={(e) => setFontSettingsTextAlign(e.target.value as 'left' | 'center' | 'right')}
                    style={{ padding: '6px 8px' }}
                  >
                    <option value="left">Left</option>
                    <option value="center">Center</option>
                    <option value="right">Right</option>
                  </select>
                </div>
                <div className="form-group" style={{ margin: 0 }}>
                  <label style={{ fontSize: '12px', marginBottom: '4px' }}>Vertical Align</label>
                  <select
                    value={fontSettingsVerticalAlign}
                    onChange={(e) => setFontSettingsVerticalAlign(e.target.value as 'top' | 'center' | 'bottom')}
                    style={{ padding: '6px 8px' }}
                  >
                    <option value="top">Top</option>
                    <option value="center">Center</option>
                    <option value="bottom">Bottom</option>
                  </select>
                </div>
              </div>

              {/* Bulk Operations Section */}
              <div style={{ 
                padding: '12px', 
                background: '#F0F9FF', 
                borderRadius: '8px',
                border: '1px solid #BAE6FD',
                marginBottom: '16px'
              }}>
                <h3 style={{ margin: '0 0 10px 0', fontSize: '13px', color: '#0369A1' }}>
                  📝 Apply to All Existing Text (All Pages)
                </h3>
                
                {/* Font Size Adjustment */}
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '10px' }}>
                  <span style={{ fontSize: '12px', color: '#666', width: '80px' }}>Font Size</span>
                  <button
                    type="button"
                    onClick={() => adjustAllTextFontSizeAllPages(-1)}
                    style={{ padding: '4px 12px', fontSize: '14px', fontWeight: 'bold', border: '1px solid #ddd', borderRadius: '4px', background: 'white', cursor: 'pointer' }}
                  >
                    A-
                  </button>
                  <button
                    type="button"
                    onClick={() => adjustAllTextFontSizeAllPages(1)}
                    style={{ padding: '4px 12px', fontSize: '14px', fontWeight: 'bold', border: '1px solid #ddd', borderRadius: '4px', background: 'white', cursor: 'pointer' }}
                  >
                    A+
                  </button>
                  <span style={{ fontSize: '11px', color: '#888' }}>±1</span>
                </div>
                
                {/* Font Family for All */}
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '10px' }}>
                  <span style={{ fontSize: '12px', color: '#666', width: '80px' }}>Font</span>
                  <select
                    onChange={(e) => {
                      if (e.target.value) {
                        changeAllTextFontFamilyAllPages(e.target.value);
                        e.target.value = '';
                      }
                    }}
                    defaultValue=""
                    style={{ flex: 1, padding: '5px 8px', borderRadius: '4px', border: '1px solid #ddd', fontSize: '12px' }}
                  >
                    <option value="">Select font...</option>
                    <optgroup label="Sans Serif">
                      <option value="Roboto" style={{ fontFamily: 'Roboto' }}>Roboto</option>
                      <option value="Open Sans" style={{ fontFamily: 'Open Sans' }}>Open Sans</option>
                      <option value="Lato" style={{ fontFamily: 'Lato' }}>Lato</option>
                      <option value="Montserrat" style={{ fontFamily: 'Montserrat' }}>Montserrat</option>
                      <option value="Raleway" style={{ fontFamily: 'Raleway' }}>Raleway</option>
                      <option value="Ubuntu" style={{ fontFamily: 'Ubuntu' }}>Ubuntu</option>
                      <option value="Oswald" style={{ fontFamily: 'Oswald' }}>Oswald</option>
                    </optgroup>
                    <optgroup label="Serif">
                      <option value="Merriweather" style={{ fontFamily: 'Merriweather' }}>Merriweather</option>
                      <option value="Playfair Display" style={{ fontFamily: 'Playfair Display' }}>Playfair Display</option>
                    </optgroup>
                    <optgroup label="Handwriting & Fun">
                      <option value="Patrick Hand" style={{ fontFamily: 'Patrick Hand' }}>Patrick Hand</option>
                      <option value="Indie Flower" style={{ fontFamily: 'Indie Flower' }}>Indie Flower</option>
                      <option value="Shadows Into Light" style={{ fontFamily: 'Shadows Into Light' }}>Shadows Into Light</option>
                      <option value="Caveat" style={{ fontFamily: 'Caveat' }}>Caveat</option>
                      <option value="Architects Daughter" style={{ fontFamily: 'Architects Daughter' }}>Architects Daughter</option>
                      <option value="Kalam" style={{ fontFamily: 'Kalam' }}>Kalam</option>
                      <option value="Permanent Marker" style={{ fontFamily: 'Permanent Marker' }}>Permanent Marker</option>
                    </optgroup>
                    <optgroup label="Elegant & Decorative">
                      <option value="Dancing Script" style={{ fontFamily: 'Dancing Script' }}>Dancing Script</option>
                      <option value="Pacifico" style={{ fontFamily: 'Pacifico' }}>Pacifico</option>
                    </optgroup>
                    <optgroup label="Bold & Strong">
                      <option value="Bebas Neue" style={{ fontFamily: 'Bebas Neue' }}>Bebas Neue</option>
                      <option value="Bangers" style={{ fontFamily: 'Bangers' }}>Bangers</option>
                      <option value="Fredoka One" style={{ fontFamily: 'Fredoka One' }}>Fredoka One</option>
                      <option value="Righteous" style={{ fontFamily: 'Righteous' }}>Righteous</option>
                      <option value="Audiowide" style={{ fontFamily: 'Audiowide' }}>Audiowide</option>
                      <option value="Press Start 2P" style={{ fontFamily: 'Press Start 2P' }}>Press Start 2P</option>
                    </optgroup>
                  </select>
                </div>

                {/* Font Weight for All */}
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '10px' }}>
                  <span style={{ fontSize: '12px', color: '#666', width: '80px' }}>Weight</span>
                  <select
                    onChange={(e) => {
                      if (e.target.value) {
                        changeAllTextFontWeightAllPages(e.target.value);
                        e.target.value = '';
                      }
                    }}
                    defaultValue=""
                    style={{ flex: 1, padding: '5px 8px', borderRadius: '4px', border: '1px solid #ddd', fontSize: '12px' }}
                  >
                    <option value="">Select weight...</option>
                    <option value="300">Light</option>
                    <option value="normal">Normal</option>
                    <option value="500">Medium</option>
                    <option value="600">Semi Bold</option>
                    <option value="bold">Bold</option>
                    <option value="800">Extra Bold</option>
                  </select>
                </div>

                {/* Text Align for All */}
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '10px' }}>
                  <span style={{ fontSize: '12px', color: '#666', width: '80px' }}>Text Align</span>
                  <select
                    onChange={(e) => {
                      if (e.target.value) {
                        changeAllTextAlignAllPages(e.target.value as 'left' | 'center' | 'right');
                        e.target.value = '';
                      }
                    }}
                    defaultValue=""
                    style={{ flex: 1, padding: '5px 8px', borderRadius: '4px', border: '1px solid #ddd', fontSize: '12px' }}
                  >
                    <option value="">Select alignment...</option>
                    <option value="left">Left</option>
                    <option value="center">Center</option>
                    <option value="right">Right</option>
                  </select>
                </div>

                {/* Vertical Align for All */}
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <span style={{ fontSize: '12px', color: '#666', width: '80px' }}>V. Align</span>
                  <select
                    onChange={(e) => {
                      if (e.target.value) {
                        changeAllVerticalAlignAllPages(e.target.value as 'top' | 'center' | 'bottom');
                        e.target.value = '';
                      }
                    }}
                    defaultValue=""
                    style={{ flex: 1, padding: '5px 8px', borderRadius: '4px', border: '1px solid #ddd', fontSize: '12px' }}
                  >
                    <option value="">Select vertical align...</option>
                    <option value="top">Top</option>
                    <option value="center">Center</option>
                    <option value="bottom">Bottom</option>
                  </select>
                </div>
              </div>

              <div className="modal-actions">
                <button type="button" onClick={() => setShowFontModal(false)}>
                  Cancel
                </button>
                <button type="submit" className="primary">
                  Save Settings
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {showPreflightModal && (
        <PreflightPanel
          pages={storybook.pages}
          issues={currentPreflightIssues}
          focusedPageId={focusedPreflightPageId}
          fixingIssueKey={fixingPreflightIssueKey}
          onClose={() => setShowPreflightModal(false)}
          onGoToIssue={handleGoToPreflightIssue}
          onFixIssue={handleApplyPreflightFix}
          onContinue={handleContinueAfterPreflight}
          onContinueToExportReview={handleContinueAfterPreflight}
        />
      )}

      {/* PDF Export Modal */}
      {showPdfExportModal && (
        <div className="modal-overlay" onClick={() => setShowPdfExportModal(false)}>
          <div
            className="modal-content"
            role="dialog"
            aria-modal="true"
            aria-labelledby="pdf-export-title"
            onClick={(e) => e.stopPropagation()}
            style={{ maxWidth: '600px' }}
          >
            <h2 id="pdf-export-title">Export PDF</h2>
            <p style={{ marginBottom: '20px', color: '#666' }}>
              Choose which PDF files you want to download for your storybook.
            </p>

            <fieldset style={{ border: 0, padding: 0, margin: '0 0 24px' }}>
              <legend style={{ fontWeight: 700, marginBottom: '10px' }}>Pages to include</legend>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
              {/* Full PDF Option */}
              <label
                style={{ 
                  padding: '16px', 
                  border: pdfExportType === 'full' ? '2px solid #007bff' : '2px solid #ddd', 
                  borderRadius: '8px', 
                  cursor: 'pointer',
                  backgroundColor: pdfExportType === 'full' ? '#f0f8ff' : 'white'
                }}
              >
                <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                  <input 
                    type="radio" 
                    name="pdf-export-type"
                    value="full"
                    checked={pdfExportType === 'full'} 
                    onChange={() => setPdfExportType('full')}
                    style={{ cursor: 'pointer' }}
                  />
                  <div style={{ flex: 1 }}>
                    <strong style={{ fontSize: '16px' }}>Complete Book PDF</strong>
                    <p style={{ margin: '4px 0 0 0', fontSize: '14px', color: '#666' }}>
                      Full storybook with all pages including front cover, interior pages, and back cover
                    </p>
                  </div>
                </div>
              </label>

              {/* Manuscript Option */}
              <label
                style={{ 
                  padding: '16px', 
                  border: pdfExportType === 'manuscript' ? '2px solid #007bff' : '2px solid #ddd', 
                  borderRadius: '8px', 
                  cursor: 'pointer',
                  backgroundColor: pdfExportType === 'manuscript' ? '#f0f8ff' : 'white'
                }}
              >
                <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                  <input 
                    type="radio" 
                    name="pdf-export-type"
                    value="manuscript"
                    checked={pdfExportType === 'manuscript'} 
                    onChange={() => setPdfExportType('manuscript')}
                    style={{ cursor: 'pointer' }}
                  />
                  <div style={{ flex: 1 }}>
                    <strong style={{ fontSize: '16px' }}>Manuscript (Interior Pages Only)</strong>
                    <p style={{ margin: '4px 0 0 0', fontSize: '14px', color: '#666' }}>
                      Interior content pages only, without covers. Ideal for uploading to print services (KDP, IngramSpark, etc.)
                    </p>
                  </div>
                </div>
              </label>

              {/* Front Cover Option */}
              <label
                style={{ 
                  padding: '16px', 
                  border: pdfExportType === 'front-cover' ? '2px solid #007bff' : '2px solid #ddd', 
                  borderRadius: '8px', 
                  cursor: 'pointer',
                  backgroundColor: pdfExportType === 'front-cover' ? '#f0f8ff' : 'white'
                }}
              >
                <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                  <input 
                    type="radio" 
                    name="pdf-export-type"
                    value="front-cover"
                    checked={pdfExportType === 'front-cover'} 
                    onChange={() => setPdfExportType('front-cover')}
                    style={{ cursor: 'pointer' }}
                  />
                  <div style={{ flex: 1 }}>
                    <strong style={{ fontSize: '16px' }}>Front Cover Only</strong>
                    <p style={{ margin: '4px 0 0 0', fontSize: '14px', color: '#666' }}>
                      Just the front cover page for separate cover file submission
                    </p>
                  </div>
                </div>
              </label>

              {/* Back Cover Option */}
              <label
                style={{ 
                  padding: '16px', 
                  border: pdfExportType === 'back-cover' ? '2px solid #007bff' : '2px solid #ddd', 
                  borderRadius: '8px', 
                  cursor: 'pointer',
                  backgroundColor: pdfExportType === 'back-cover' ? '#f0f8ff' : 'white'
                }}
              >
                <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                  <input 
                    type="radio" 
                    name="pdf-export-type"
                    value="back-cover"
                    checked={pdfExportType === 'back-cover'} 
                    onChange={() => setPdfExportType('back-cover')}
                    style={{ cursor: 'pointer' }}
                  />
                  <div style={{ flex: 1 }}>
                    <strong style={{ fontSize: '16px' }}>🔙 Back Cover Only</strong>
                    <p style={{ margin: '4px 0 0 0', fontSize: '14px', color: '#666' }}>
                      Just the back cover page for separate cover file submission
                    </p>
                  </div>
                </div>
              </label>
              </div>
            </fieldset>

            {/* Bleed Toggle */}
            <label style={{
              display: 'flex',
              alignItems: 'center',
              gap: '12px',
              cursor: 'pointer',
              padding: '14px 16px', 
              border: '2px solid #ddd', 
              borderRadius: '8px', 
              marginBottom: '24px',
              backgroundColor: pdfBleedEnabled ? '#f0fff4' : 'white'
            }}>
              <input
                type="checkbox"
                role="switch"
                checked={pdfBleedEnabled}
                aria-labelledby="pdf-bleed-label"
                aria-describedby="pdf-bleed-description"
                onChange={(e) => {
                  const enabled = e.target.checked;
                  setPdfBleedEnabled(enabled);
                  setStorybook(current => current ? {
                    ...current,
                    printSettings: {
                      schemaVersion: 1,
                      safeMarginInches: current.printSettings?.safeMarginInches ?? 0.25,
                      bleedInches: enabled ? 0.125 : 0
                    }
                  } : current);
                }}
                style={{ width: '22px', height: '22px', cursor: 'pointer', accentColor: '#28a745' }}
              />
              <div style={{ flex: 1 }}>
                <strong id="pdf-bleed-label" style={{ fontSize: '15px' }}>Include KDP Bleed (0.125")</strong>
                <p id="pdf-bleed-description" style={{ margin: '4px 0 0 0', fontSize: '13px', color: '#666' }}>
                  Adds 0.125" bleed margin on all sides for print-ready PDFs.
                  Required by KDP/IngramSpark when images or backgrounds extend to the page edge.
                  {pdfBleedEnabled && <span style={{ color: '#28a745', fontWeight: 'bold' }}> Recommended for printing.</span>}
                </p>
              </div>
            </label>

            <div style={{ display: 'flex', gap: '12px', justifyContent: 'flex-end' }}>
              <button 
                type="button" 
                className="btn btn-secondary"
                onClick={() => setShowPdfExportModal(false)}
                disabled={isGeneratingServerPDF}
              >
                Cancel
              </button>
              <button 
                type="button" 
                className="btn btn-primary"
                onClick={async () => {
                  if (!storybook || isGeneratingServerPDF) return;
                  const selectedBook = {
                    ...storybook,
                    pages: pagesForPdfExport(storybook, pdfExportType)
                  };
                  const issues = await runLayoutPreflight(selectedBook, false);
                  const summary = summarizePreflight(issues);
                  const warningSignature = preflightWarningSignature(issues);
                  const warningsNeedAcknowledgement = summary.hasWarnings
                    && acknowledgedPreflightWarningsRef.current !== warningSignature;
                  if (!summary.canExport || warningsNeedAcknowledgement) {
                    setShowPdfExportModal(false);
                    setFocusedPreflightPageId(null);
                    setPreflightContinueTarget('pdf-progress');
                    setShowPreflightModal(true);
                    return;
                  }
                  setShowPdfExportModal(false);
                  await runAuthoritativeExportPreflight();
                }}
                disabled={isGeneratingServerPDF}
              >
                Continue
              </button>
            </div>
          </div>
        </div>
      )}

      {showExportProofModal && (
        <ExportProofModal
          phase={exportProofPhase}
          report={exportPreflightReport}
          artifact={exportProofArtifact}
          error={exportProofError}
          layoutOverrideApplied={Boolean(
            exportLayoutOverrideReceipt
              && exportPreflightReport
              && exportLayoutOverrideReceipt.reportId === exportPreflightReport.reportId
          )}
          onClose={handleCloseExportProof}
          onRetryPreflight={runAuthoritativeExportPreflight}
          onGenerateProof={generateAuthoritativeExportProof}
          onGenerateAnyway={(report) => generateAuthoritativeExportProof(
            report,
            undefined,
            {
              schemaVersion: 1,
              acknowledged: true,
              reportId: report.reportId
            }
          )}
          onGoToIssue={handleGoToExportIssue}
          onDownloadPdf={(artifact, resolvedSource) => {
            const downloadSource = resolvedSource.startsWith('blob:')
              ? resolvedSource
              : (() => {
                const url = new URL(resolvedSource, window.location.href);
                url.searchParams.set('download', '1');
                return url.toString();
              })();
            const anchor = document.createElement('a');
            anchor.href = downloadSource;
            anchor.download = artifact.filename || 'storybook-proof.pdf';
            anchor.rel = 'noopener noreferrer';
            document.body.appendChild(anchor);
            anchor.click();
            anchor.remove();
            void markQuickCreatePdfComplete();
          }}
          onDownloadDiagnostics={exportDiagnosticsUrl ? () => {
            const anchor = document.createElement('a');
            anchor.href = exportDiagnosticsUrl;
            anchor.download = `${exportPreflightReport?.title || 'storybook'}-export-diagnostics.json`;
            anchor.rel = 'noopener noreferrer';
            document.body.appendChild(anchor);
            anchor.click();
            anchor.remove();
          } : undefined}
        />
      )}

      {/* PDF Progress Modal */}
      {showPdfProgressModal && storybook && (
        <PdfProgressModal
          storybookId={storybook._id || id || ''}
          exportType={pdfExportType}
          bleed={pdfBleedEnabled}
          onClose={() => {
            setShowPdfProgressModal(false);
            setIsGeneratingServerPDF(false);
          }}
          onGenerate={async () => {
            // This is called when user clicks "Generate PDF" from the modal
            if (!storybook || isGeneratingServerPDF) return;
            
            setIsGeneratingServerPDF(true);
            
            try {
              // Auto-save before generating PDF
              await saveStorybook();
              const exportIssues = await runLayoutPreflight({
                ...storybook,
                pages: pagesForPdfExport(storybook, pdfExportType)
              }, false);
              if (!summarizePreflight(exportIssues).canExport) {
                setShowPdfProgressModal(false);
                setFocusedPreflightPageId(null);
                setPreflightContinueTarget('pdf-progress');
                setShowPreflightModal(true);
                throw new Error('PDF export blocked by layout preflight. Fix the listed page issues and try again.');
              }
              
              // Determine filename based on export type
              const baseFilename = (storybook.title || 'storybook').replace(/[^a-z0-9]/gi, '_').toLowerCase();
              let filename;
              switch (pdfExportType) {
                case 'manuscript':
                  filename = `${baseFilename}_manuscript.pdf`;
                  break;
                case 'front-cover':
                  filename = `${baseFilename}_front_cover.pdf`;
                  break;
                case 'back-cover':
                  filename = `${baseFilename}_back_cover.pdf`;
                  break;
                default:
                  filename = `${baseFilename}.pdf`;
              }
              
              // Start PDF generation
              console.log('[PDF] Starting generation...', { bleed: pdfBleedEnabled });
              const data = await storybookAPI.generatePdfServer(
                storybook._id || id || '', 
                pdfExportType,
                pdfBleedEnabled
              );
              
              console.log('[PDF] Generation complete, received data:', {
                type: typeof data,
                isArrayBuffer: data instanceof ArrayBuffer,
                isBlob: data instanceof Blob,
                hasDownloadUrl: data && typeof data === 'object' && 'downloadUrl' in data,
                dataKeys: data && typeof data === 'object' ? Object.keys(data) : 'not an object',
                byteLength: data?.byteLength || 0
              });
              
              // Check if response is a download URL (Cloud Storage)
              if (data && typeof data === 'object' && 'downloadUrl' in data) {
                // PDF is on Cloud Storage - modal will fetch and show download button
                console.log('[PDF] PDF ready on Cloud Storage, URL will be fetched by modal');
                // Don't download here - let the modal handle it via completion state
              } else {
                // Handle direct blob download (fallback)
                console.log('[PDF] Creating blob from ArrayBuffer, size:', data?.byteLength || 0);
                
                if (!data || data.byteLength === 0) {
                  throw new Error('Received empty PDF data');
                }
                
                const blob = new Blob([data], { type: 'application/pdf' });
                console.log('[PDF] Blob created, size:', blob.size, 'bytes');
                const url = window.URL.createObjectURL(blob);
                const a = document.createElement('a');
                a.href = url;
                a.download = filename;
                a.style.display = 'none';
                document.body.appendChild(a);
                console.log('[PDF] Triggering download for:', filename);
                a.click();
                
                // Clean up after a short delay
                setTimeout(() => {
                  document.body.removeChild(a);
                  window.URL.revokeObjectURL(url);
                  console.log('[PDF] Download cleanup complete');
                }, 100);
                
                console.log('[PDF] Download initiated (direct blob)');
                alert(`✅ PDF downloaded! Check your downloads folder for: ${filename}`);
              }

              // Quick Create is complete only after the server returned a real,
              // non-empty PDF payload or a ready Cloud Storage download URL.
              await markQuickCreatePdfComplete();
              
            } catch (err: any) {
              console.error('PDF generation failed', err);
              // Check if this is an "already in progress" error
              if (err?.inProgress) {
                alert(`⏳ ${err.message || 'PDF generation is already in progress. Please wait for it to complete.'}`);
              } else {
                alert('PDF generation failed. See console for details.');
              }
            } finally {
              setIsGeneratingServerPDF(false);
            }
          }}
          onError={(error: string) => {
            console.error('PDF generation error:', error);
            setShowPdfProgressModal(false);
            setIsGeneratingServerPDF(false);
            alert(`PDF generation failed: ${error}`);
          }}
        />
      )}

      {/* AI Tutorial Modal - Shows first */}
      <AIPlotTutorialModal
        isOpen={showAITutorial}
        onClose={() => {
          setShowAITutorial(false);
          // Show manual tools tutorial after AI tutorial
          setShowTutorial(true);
        }}
      />

      {/* Tutorial Modal */}
      <TutorialModal 
        isOpen={showTutorial}
        onClose={() => setShowTutorial(false)}
      />

      {/* Resolution Checker Modal */}
      {storybook && (
        <ResolutionCheckerModal
          isOpen={showResolutionChecker}
          onClose={() => setShowResolutionChecker(false)}
          pages={storybook.pages}
          currentPageIndex={currentPageIndex}
          canvasSize={storybook.canvasSize || '8.5x8.5'}
          onUpscaleImage={handleUpscaleImage}
          onUpdateImageElement={handleResolutionUpdateImageElement}
        />
      )}

      {/* Image History Modal */}
      <ImageHistoryModal
        isOpen={showImageHistory}
        onClose={() => { setShowImageHistory(false); setImageHistoryElement(null); }}
        element={imageHistoryElement}
        onRestoreVersion={handleRestoreImageVersion}
      />
    </div>
  );
};

export default StorybookEditor;
